'use client';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useRect, useFlyoutPortalElement, FlyoutProvider, ButtonIcon, Modal, ModalHeader, ModalBody, Typography, MenuItem, MenuItemText, ButtonGroup, Button, Tabs, ExperimentalFormGroup, Checkbox, ModalFooter, ButtonWithMenu, TextField, ButtonWithFlyout, VideoLoader, ConfirmationDialog, Toast, Icon, Truncate, Loader, Tooltip } from '@statsbomb/kitbag-components';
import { atom, useAtomValue, useAtom, useSetAtom, Provider } from 'jotai';
import { styled, useTheme } from 'styled-components';
import * as React2 from 'react';
import { useState, useEffect, useCallback, memo, useRef, useLayoutEffect, forwardRef, useMemo } from 'react';
import { ChromelessPlayer } from 'theoplayer';
import { atomWithStorage, useAtomCallback, useHydrateAtoms } from 'jotai/utils';
import { arrayMove, useSortable, sortableKeyboardCoordinates, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useKitbagAuth } from '@statsbomb/kitbag-auth';
import useSWR, { useSWRConfig as useSWRConfig$1 } from 'swr';
import { ThemeProvider, videoTheme } from '@statsbomb/kitbag-themes';
import { usePlayerTracksData, HighlightPlayerPositions as HighlightPlayerPositions$2 } from '@statsbomb/kitbag-tracking';
import ReactDOM, { createPortal } from 'react-dom';
import { DragOverlay as DragOverlay$1, useSensors, useSensor, PointerSensor, KeyboardSensor, DndContext, closestCenter } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import { useDebounce } from 'use-debounce';
import Mousetrap from 'mousetrap';

const myCutupsAtom = atom(null);
myCutupsAtom.debugLabel = 'video.client.myCutups';
const selectedCutupAtom = atom(null);
selectedCutupAtom.debugLabel = 'video.client.selectedCutup';
const displayDeleteCutupConfirmationAtom = atom(false);
displayDeleteCutupConfirmationAtom.debugLabel =
    'video.client.displayDeleteCutupConfirmation';
const cutupContextMenuOpenAtom = atom(null);
cutupContextMenuOpenAtom.debugLabel = 'video.client.cutupContextMenuOpen';

/* eslint sort-keys: 0 */
// @todo TEMP to be removed in another PR
// eslint-disable-next-line import/prefer-default-export
const BREAKPOINTS = {
    xs: 300,
    sm: 575,
    md: 768,
    lg: 992,
    xl: 1200,
};

const PLAYER_AND_PLAYLIST_WRAPPER_BREAKPOINT = BREAKPOINTS.lg;
const MY_PLAYLISTS_CONTAINER_BREAKPOINT = PLAYER_AND_PLAYLIST_WRAPPER_BREAKPOINT;

const PlayerAndMyPlaylistContainer = styled.div ``;
const MyPlaylistsWrapper = styled.aside `
  background-color: ${({ theme }) => theme.colours.graphics.primary.ink};
`;
const MyPlaylistsHeader = styled.div `
  align-items: center;
  display: flex;
  min-height: 2rem;
  padding: 1rem;
`;
const MyPlaylistsOrderedList = styled.ol `
  padding: 0.5rem;
  width: 100%;
`;

const TimelineContainer = styled.div `
  align-items: center;
  display: flex;
  flex-grow: 1;
  justify-content: space-between;
`;
const StyledSlider = styled.input.attrs(({ max = 0, min = 0, value = 0 }) => {
    const currentOffsetByMin = +value - +min;
    const maxOffsetByMin = max - +min;
    const backgroundPercentage = (currentOffsetByMin / maxOffsetByMin) * 100;
    return {
        style: {
            backgroundSize: `${backgroundPercentage}% 100%`,
        },
    };
}) `
  appearance: none;
  background: ${({ theme }) => theme.applyOpacity(theme.colours.graphics.primary.main, 'overlay')};
  /*
  Using linear-gradient on a background-image here to create the progress bar
  Need a solid colour so just using using the same colour twice in the gradient
  The background-size below sets the width of the progress bar
  */
  background-image: linear-gradient(
    ${({ theme }) => `${theme.colours.interface.strong},${theme.colours.interface.strong}`}
  );
  background-repeat: no-repeat;
  cursor: pointer;
  flex-grow: 1;
  height: 0.375rem;

  &::-webkit-slider-thumb {
    appearance: none;
    background: #fff;
    border-radius: 100%;
    height: 0.75rem;
    width: 0.75rem;
  }

  &::-webkit-slider-thumb:hover {
    scale: 1.25;
  }

  &::-webkit-slider-runnable-track {
    appearance: none;
    box-shadow: none;
    border: none;
    background: transparent;
  }

  &::-moz-range-thumb {
    background: #fff;
    border: none;
    border-radius: 100%;
    height: 0.75rem;
    width: 0.75rem;
  }

  &::-moz-range-thumb:hover {
    scale: 1.25;
  }

  &::-moz-range-track {
    height: 0.75rem;
  }

  &:focus-visible {
    outline: none;

    &::-webkit-slider-thumb {
      outline: auto 1px -webkit-focus-ring-color;
    }

    ::-moz-range-thumb {
      outline: auto 2px Highlight;
    }
  }
`;

const SliderContainer = styled.div `
  align-items: center;
  background: ${({ theme }) => theme.colours.canvas.secondary.main};
  border-radius: 1.5rem;
  box-sizing: border-box;
  display: flex;
  height: 1.5rem;
  padding: 0 0.5rem;
  margin-left: 0.25rem;
`;
const VolumeWrapper = styled.div `
  bottom: 0;
  left: 0;
  position: absolute;
  rotate: 270deg;
  transform-origin: top left;
`;
const StyledVolumeSlider = styled(StyledSlider) `
  width: 3.75rem;
`;

const VIDEO_CONTROL_HEIGHT = {
    DESKTOP_CONTROLS_BELOW: '5.5rem',
    DESKTOP_CONTROLS_BELOW_WITH_ADDITIONAL_SPACE_FOR_TIMELINE: '6.5rem',
    DESKTOP_CONTROLS_INLINE: '8.5rem',
    DESKTOP_CONTROLS_INLINE_WITH_ADDITIONAL_SPACE_FOR_TIMELINE: '11rem',
    MOBILE_CONTROLS: '3.5rem',
    MOBILE_CONTROLS_WITH_ADDITIONAL_SPACE_FOR_TIMELINE: '5rem',
};
const getBackground = ({ $showMobileControls, $showControlsBelowVideo, $isFullScreen, theme, }) => {
    const gradient = `linear-gradient(0deg, ${theme.colours.canvas.tertiary.main} 0%, rgba(0, 0, 0, 0) 100%)`;
    const controlsBackground = $showControlsBelowVideo && !$isFullScreen
        ? theme.colours.canvas.secondary.main
        : gradient;
    return !$showControlsBelowVideo && $showMobileControls && !$isFullScreen
        ? theme.applyOpacity(theme.colours.canvas.tertiary.main, 'overlay')
        : controlsBackground;
};
const getClickableOverlayBackground = ({ $showMobileControls, theme, }) => $showMobileControls
    ? theme.applyOpacity(
    // we always want to show the overlay to show as a dark colour,
    // so for light mode we use the inverse
    theme.isDark
        ? theme.colours.canvas.tertiary.main
        : theme.colours.canvas.tertiary.ink, 'overlay')
    : 'transparent';
const getControlsHeightOffset = ({ $showControlsBelowVideo, }) => ($showControlsBelowVideo ? '0rem' : '9rem');
const getControlsHeight = ({ $allowAdditionalSpaceForTimelineButtons, $showMobileControls, $showControlsBelowVideo, $isFullScreen, }) => {
    if ($isFullScreen || (!$showMobileControls && !$showControlsBelowVideo)) {
        return $allowAdditionalSpaceForTimelineButtons
            ? VIDEO_CONTROL_HEIGHT.DESKTOP_CONTROLS_INLINE_WITH_ADDITIONAL_SPACE_FOR_TIMELINE
            : VIDEO_CONTROL_HEIGHT.DESKTOP_CONTROLS_INLINE;
    }
    if (!($showControlsBelowVideo && !$showMobileControls))
        return $allowAdditionalSpaceForTimelineButtons
            ? VIDEO_CONTROL_HEIGHT.MOBILE_CONTROLS_WITH_ADDITIONAL_SPACE_FOR_TIMELINE
            : VIDEO_CONTROL_HEIGHT.MOBILE_CONTROLS;
    return $allowAdditionalSpaceForTimelineButtons
        ? VIDEO_CONTROL_HEIGHT.DESKTOP_CONTROLS_BELOW_WITH_ADDITIONAL_SPACE_FOR_TIMELINE
        : VIDEO_CONTROL_HEIGHT.DESKTOP_CONTROLS_BELOW;
};
const getDesktopStylesForControlsBelowVideo = (props) => {
    // for show video below on desktop, we want to fix the position and background.
    if (props.$showControlsBelowVideo)
        return `position: relative;
    top: ${getControlsHeight(props)};
    opacity: 1;
  `;
    return '';
};
const getClickableWrapperStyles = (props) => {
    const { $showControlsBelowVideo, $showMobileControls } = props;
    // for show video below, always 100% height
    if ($showControlsBelowVideo)
        return `height: 100%; `;
    // for inline mobile controls
    if ($showMobileControls)
        return `height: calc(100% - ${getControlsHeight(props)});`;
    // for desktop view with controls inline
    return `height: calc(100% - ${getControlsHeightOffset(props)})`;
};
const DesktopWrapper = styled.div `
  background: ${getBackground};
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  height: ${getControlsHeight};
  justify-content: flex-end;
  padding: 0.5rem;
  width: 100%;

  ${getDesktopStylesForControlsBelowVideo}
`;
const ClickableWrapper = styled.div `
  background: ${getClickableOverlayBackground};
  opacity: ${(props) => (props.$displayControls ? 1 : 0)};
  /* only set opacity when showControlsBelowVideo is true - this is to prevent the controls from flickering 
  when the video is paused in mobile view. */
  transition: ${(props) => props.$showControlsBelowVideo && 'opacity 500ms'};

  position: absolute;
  align-self: flex-start;
  width: 100%;

  ${getClickableWrapperStyles}
`;
const ControlsWrapper = styled.div `
  align-items: center;
  display: flex;
  gap: 0.5rem;
`;
const PlayerWrapper = styled.div `
  align-items: flex-end;
  cursor: ${(props) => (props.$displayControls ? 'default' : 'none')};
  bottom: 0;
  display: flex;
  height: 100%;
  left: 0;
  opacity: ${(props) => props.$displayControls || props.$showControlsBelowVideo ? 1 : 0};
  position: absolute;
  width: 100%;
  &:has(${ControlsWrapper}:hover) {
    opacity: 1;
  }
`;
const ButtonGroupWrapper = styled.div `
  align-items: center;
  display: flex;
  gap: 0.5rem;
`;
const VolumeControlsWrapper = styled.span `
  position: relative;

  ${VolumeWrapper} {
    opacity: 0;
    z-index: -1;

    &:has(${StyledVolumeSlider}:focus-visible) {
      opacity: 1;
      z-index: 1;
    }
  }

  &:hover ${VolumeWrapper}, button:focus-visible + ${VolumeWrapper} {
    opacity: 1;
    z-index: 1;
  }
`;

var Root = styled.div `
  aspect-ratio: 16/9;
  position: relative;
  width: 100%;
  &:focus {
    outline: none;
  }
  margin-bottom: ${getControlsHeight};
`;

const PlaylistWrapper = styled.aside `
  background-color: ${({ theme }) => theme.colours.canvas.tertiary.main};
  position: relative;
  z-index: 0;
`;
const SharedPlaylistStyle = styled.div `
  align-items: center;
  background-color: ${({ theme }) => theme.colours.canvas.tertiary.main};
  display: flex;
  justify-content: space-between;
  min-height: 2rem;
  padding: 0.5rem;
  position: sticky;
  top: 0;
  z-index: 1;
`;
const StyledPlaylistHeader = styled(SharedPlaylistStyle) ``;
const StyledEmptyPlayList = styled(SharedPlaylistStyle) ``;
const PlaylistTitle = styled.div `
  flex-grow: 1;
  min-width: 0;
`;
const PlaylistOrderedList = styled.ol `
  width: 100%;
`;
const PlaylistBackButtonWrapper = styled.div `
  padding-right: 0.5rem;
`;
const PlaylistBackButtonAndTitleWrapper = styled.div `
  display: flex;
  align-items: start;
  min-width: 0;
  width: 100%;
  justify-content: space-between;
`;

/*
Once we have a supporting CSS framework this
could be re-written to use @container queries.
This would also negate the need for the useMeasure hook
to determine the hasColumnLayout prop value.
*/
const PlayerAndPlaylist = styled.section `
  display: flex;
  gap: 0.5rem;
  width: 100%;
  ${(props) => !props.$hasColumnLayout && `flex-direction: column; max-height: inherit;`}

  ${Root} {
    width: ${(props) => (props.$hasColumnLayout ? '66%' : '100%')};
    ${(props) => !props.$hasColumnLayout &&
    `max-height: inherit; flex-shrink: 0; order: -1;`};
  }

  ${PlaylistWrapper}, ${MyPlaylistsWrapper} {
    background-color: ${({ theme }) => theme.colours.canvas.tertiary.main};
    max-height: inherit;
    overflow: auto;
    position: relative;
    width: ${(props) => (props.$hasColumnLayout ? '34%' : '100%')};

    > ${PlaylistOrderedList}, ${MyPlaylistsOrderedList} {
      position: ${(props) => props.$hasColumnLayout ? 'absolute' : 'relative'};
    }
  }
`;

function PlayerAndPlaylistWrapper({ children }) {
    const [elementRef, { width }] = useRect();
    return (jsx(PlayerAndPlaylist, { "$hasColumnLayout": width >= PLAYER_AND_PLAYLIST_WRAPPER_BREAKPOINT, ref: elementRef, children: children }));
}

const DEFAULT_THEO_PLAYER_LIBRARY_LOCATION = 'https://video-static.statsbomb.com/vendor/theoplayer/8.5.0';
const DEFAULT_THEO_PLAYER_LICENSE = undefined;
const baseUrlVideoServiceAtom = atom('https://video-services.statsbomb.com');
baseUrlVideoServiceAtom.debugLabel = 'video.client.baseUrlVideoService';
const streamsOnlyAtom = atom(false);
streamsOnlyAtom.debugLabel = 'video.client.streamsOnly';
const theoPlayerLicenseAtom = atom(DEFAULT_THEO_PLAYER_LICENSE);
theoPlayerLicenseAtom.debugLabel = 'video.client.theoPlayerLicense';
const theoPlayerLibraryLocationAtom = atom(DEFAULT_THEO_PLAYER_LIBRARY_LOCATION);
theoPlayerLibraryLocationAtom.debugLabel =
    'video.client.theoPlayerLibraryLocation';

const MICROSECOND = 1000000;
function padTo2Digits(num) {
    return num.toString().padStart(2, '0');
}
const secondsToMicroseconds = (seconds) => seconds * MICROSECOND;
const microsecondsToSeconds = (microseconds) => microseconds / MICROSECOND;
function convertMircosecondsToDisplayTimestamp(microseconds) {
    const minutes = Math.floor(microseconds / 60000000);
    const seconds = Math.floor((microseconds % 60000000) / MICROSECOND);
    return seconds === 60
        ? `${padTo2Digits(minutes + 1)}:00`
        : `${padTo2Digits(minutes)}:${padTo2Digits(seconds)}`;
}
const FRAMES_PER_SECOND = 30;
const calculatePlaybackTimeOfDesiredStep = (currentPlaybackTime, stepsToSkip) => {
    const stepSize = secondsToMicroseconds(1) / FRAMES_PER_SECOND;
    return currentPlaybackTime + Math.round(stepSize * stepsToSkip);
};

var HighlightPlayers;
(function (HighlightPlayers) {
    HighlightPlayers[HighlightPlayers["HIDDEN"] = 0] = "HIDDEN";
    HighlightPlayers[HighlightPlayers["VISIBLE"] = 1] = "VISIBLE";
})(HighlightPlayers || (HighlightPlayers = {}));

const TIMEOUT_DURATION_MILLISECONDS = 2000;
const timeoutIdAtom = atom(null);
const DEFAULT_SCRUB_DURATION_MICROSECONDS = secondsToMicroseconds(5);
const DEFAULT_SCRUB_FINE_DURATION_MICROSECONDS = secondsToMicroseconds(1);
const scrubDurationAtom = atom(DEFAULT_SCRUB_DURATION_MICROSECONDS);
scrubDurationAtom.debugLabel = 'video.client.scrubDuration';
const scrubFineDurationAtom = atom(DEFAULT_SCRUB_FINE_DURATION_MICROSECONDS);
scrubFineDurationAtom.debugLabel = 'video.client.scrubFineDuration';
const showControlsAtom = atom(true);
showControlsAtom.debugLabel = 'video.client.showControls';
const showMobileControlsAtom = atom(false);
showMobileControlsAtom.debugLabel = 'video.client.showMobileControls';
const renderControlsBelowVideoAtom = atom(true);
renderControlsBelowVideoAtom.debugLabel =
    'video.client.renderControlsBelowVideo';
const showControlsWithTimeoutAtom = atom((get) => get(showControlsAtom), (get, set, newValue) => {
    const existingTimeoutId = get(timeoutIdAtom);
    if (existingTimeoutId) {
        clearTimeout(existingTimeoutId);
    }
    const timeoutId = setTimeout(() => {
        set(showControlsAtom, newValue);
    }, TIMEOUT_DURATION_MILLISECONDS);
    set(timeoutIdAtom, timeoutId);
});
showControlsWithTimeoutAtom.debugLabel = 'video.client.showControlsWithTimeout';
const highlightPlayerPositionsAtom = atomWithStorage('highlightPlayerPositions', HighlightPlayers.VISIBLE);
highlightPlayerPositionsAtom.debugLabel =
    'video.client.highlightPlayerPositions';
const showMoreOptionsAtom = atom(false);
showMoreOptionsAtom.debugLabel = 'video.client.showMoreOptions';
const showHighlightPlayersFormAtom = atom(false);
showHighlightPlayersFormAtom.debugLabel =
    'video.client.showHighlightPlayersForm';

function simpleFocusAtom(originalAtom, propertyName) {
    return atom((get) => get(originalAtom)[propertyName], (get, set, newValue) => set(originalAtom, {
        ...get(originalAtom),
        [propertyName]: newValue,
    }));
}

const desiredPlayerStateAtom = atom({
    muted: true,
    playbackRate: 1,
    playbackTime: 0,
    playing: undefined,
    reset: false,
    volume: 1,
});
desiredPlayerStateAtom.debugLabel = 'video.client.desiredPlayerState';
const mutedRequestedAtom = simpleFocusAtom(desiredPlayerStateAtom, 'muted');
mutedRequestedAtom.debugLabel = 'video.client.mutedRequested';
const playRequestedAtom = simpleFocusAtom(desiredPlayerStateAtom, 'playing');
playRequestedAtom.debugLabel = 'video.client.playRequested';
const playbackRateRequestedAtom = simpleFocusAtom(desiredPlayerStateAtom, 'playbackRate');
playbackRateRequestedAtom.debugLabel = 'video.client.playbackRate';
const playbackTimeRequestedAtom = simpleFocusAtom(desiredPlayerStateAtom, 'playbackTime');
playbackTimeRequestedAtom.debugLabel = 'video.client.playbackTimeRequested';
const resetRequestedAtom = simpleFocusAtom(desiredPlayerStateAtom, 'reset');
resetRequestedAtom.debugLabel = 'video.client.resetRequested';
const volumeRequestedAtom = simpleFocusAtom(desiredPlayerStateAtom, 'volume');
volumeRequestedAtom.debugLabel = 'video.client.volume';
const autoSkipEnabledAtom = atom(true);
autoSkipEnabledAtom.debugLabel = 'video.client.autoSkipEnabled';
const pauseOnClipEndAtom = atom(false);
pauseOnClipEndAtom.debugLabel = 'video.client.pauseOnClipEnd';
const fullscreenRequestedAtom = atom(false);
fullscreenRequestedAtom.debugLabel = 'video.client.fullscreenRequested';

var VideoSport;
(function (VideoSport) {
    VideoSport["AMF"] = "AMF";
    VideoSport["Basketball"] = "BASKETBALL";
    VideoSport["Soccer"] = "FOOTBALL";
})(VideoSport || (VideoSport = {}));
var VideoTrack;
(function (VideoTrack) {
    VideoTrack["Broadcast"] = "BROADCAST";
    VideoTrack["Other"] = "OTHER";
})(VideoTrack || (VideoTrack = {}));

var EventType;
(function (EventType) {
    EventType["PASS"] = "PASS";
    EventType["SHOT"] = "SHOT";
})(EventType || (EventType = {}));
var SequenceType;
(function (SequenceType) {
    SequenceType["PLAY"] = "PLAY";
})(SequenceType || (SequenceType = {}));

const BUFFERS = {
    [`EVENT_${EventType.PASS.valueOf()}`]: {
        after: secondsToMicroseconds(5),
        before: secondsToMicroseconds(5),
    },
    [`EVENT_${EventType.SHOT.valueOf()}`]: {
        after: secondsToMicroseconds(5),
        before: secondsToMicroseconds(5),
    },
    [`SEQUENCE_${SequenceType.PLAY.valueOf()}`]: {
        after: secondsToMicroseconds(0),
        before: secondsToMicroseconds(0),
    },
    DEFAULT: {
        after: secondsToMicroseconds(5),
        before: secondsToMicroseconds(5),
    },
};
const getEventType = (playlistEntry) => playlistEntry && 'eventType' in playlistEntry && playlistEntry.eventType;
const getSequenceType = (playlistEntry) => (playlistEntry &&
    'sequenceType' in playlistEntry &&
    playlistEntry.sequenceType) ||
    '';
const getBuffer = (playlistEntry) => {
    const eventType = getEventType(playlistEntry);
    const isEvent = !!eventType;
    const key = isEvent
        ? `EVENT_${eventType === null || eventType === void 0 ? void 0 : eventType.toUpperCase()}`
        : `SEQUENCE_${getSequenceType(playlistEntry).toUpperCase()}`;
    return BUFFERS[key] || BUFFERS.DEFAULT;
};
const getVideoClipReference = (playlistItem) => {
    if (!(playlistItem && 'videoClipReference' in playlistItem)) {
        return undefined;
    }
    return playlistItem.videoClipReference;
};
const applyBuffer = (playlistEntry) => {
    const videoClipReference = getVideoClipReference(playlistEntry);
    const hasTimestamp = (videoClipReference === null || videoClipReference === void 0 ? void 0 : videoClipReference.startTimestamp) || (videoClipReference === null || videoClipReference === void 0 ? void 0 : videoClipReference.endTimestamp);
    const hasEventOrSequenceType = getEventType(playlistEntry) || getSequenceType(playlistEntry);
    if (!videoClipReference || !hasTimestamp || !hasEventOrSequenceType) {
        return playlistEntry;
    }
    const { after, before } = getBuffer(playlistEntry);
    let startTimestamp = (videoClipReference.startTimestamp || 0) - before;
    if (startTimestamp < 0) {
        startTimestamp = 0;
    }
    const endTimestamp = (videoClipReference.endTimestamp || 0) + after;
    return {
        ...playlistEntry,
        videoClipReference: {
            ...videoClipReference,
            endTimestamp,
            startTimestamp,
        },
    };
};

const ALLOWED_CLIENT_OVERRIDES = [
    'description',
    'descriptionLineTwo',
    'descriptionLineThree',
    'selectedEntry',
];
const ALLOWED_VIDEO_CLIP_REFERENCE_OVERRIDES = [
    'endTimestamp',
    'overlays',
    'startTimestamp',
];
/**
 * Gets all the client data which we allow to override the server response.
 *
 * @param {PlaylistEntryClient} playlistEntryClient
 * @returns {PlaylistEntryAllowedClientOverrides}
 */
const applyClientOverrides = (playlistEntryClient) => ALLOWED_CLIENT_OVERRIDES.reduce((overrides, key) => ({
    ...overrides,
    ...(key in playlistEntryClient
        ? { [key]: playlistEntryClient[key] }
        : {}),
}), {});
/**
 * Gets all the data which we allow to override in the app.
 *
 * @returns {PlaylistEntryAllowedInAppVideoClipReferenceOverrides}
 * @param playlistEntryClientVideoClipReference
 */
const applyInAppVideoClipReferenceOverrides = (playlistEntryClientVideoClipReference) => ALLOWED_VIDEO_CLIP_REFERENCE_OVERRIDES.reduce((overrides, key) => ({
    ...overrides,
    ...(key in playlistEntryClientVideoClipReference
        ? { [key]: playlistEntryClientVideoClipReference[key] }
        : {}),
}), {});
/**
 * Merges the client and server data together in the following order, client, server and finally applying client
 * overrides
 *
 * @param {PlaylistEntryClient} client
 * @param {PlaylistEntryServerResponse} [server] optional because, at least initially, there will be no server response
 * @returns {PlaylistEntry}
 */
function mergeClientServerEntryData(client, server) {
    const mergedEntry = {
        // TODO: VIDEO-1203 prevent clients form providing initial server state
        ...client,
        ...(server || {
            id: client.id,
        }),
        ...applyClientOverrides(client),
    };
    const clientVideoClipReference = 'videoClipReference' in client && client.videoClipReference;
    if (clientVideoClipReference &&
        'videoClipReference' in mergedEntry &&
        mergedEntry.videoClipReference) {
        mergedEntry.videoClipReference = {
            ...mergedEntry.videoClipReference,
            ...applyInAppVideoClipReferenceOverrides(clientVideoClipReference),
        };
    }
    return mergedEntry;
}

/**
 * TEMP: This will filter out any entries without a presigned url
 * Will need to be removed when Lazy Loading story is tackled
 *
 * @param {PlaylistEntryClient} playlistEntry
 * @returns {Boolean}
 */
const byNotErroneous = (playlistEntry) => playlistEntry && !('error' in playlistEntry);
/**
 * Find or filter playlist entries by if they're selected or not.
 *
 * @param {PlaylistEntryClient} playlistEntry
 * @returns {Boolean}
 */
const bySelected = ({ selectedEntry }) => !!selectedEntry;
/**
 * Deselect a playlist entry.
 *
 * @param {PlaylistEntry} playlistEntry
 * @returns {PlaylistEntry}
 */
const deselect = (playlistEntry) => ({
    ...playlistEntry,
    selectedEntry: false,
});
/**
 * To be used within an array.find to find an entry by an id
 *
 * @param {PlaylistEntryId} idToFind
 */
const byId = (idToFind) => ({ id }) => id === idToFind;
/**
 * Private atom that holds the playlist entries that have been requested by the client
 */
const playlistEntriesClientAtom = atom([]);
playlistEntriesClientAtom.debugLabel = 'video.client.playlist-entries.client';
/**
 * Private atom that holds the playlist entries that have had a server response.
 */
const playlistEntriesServerAtom = atom([]);
playlistEntriesServerAtom.debugLabel = 'video.client.playlist-entries.server';
/**
 * Derived atom that holds the playlist entries in the current playlist.
 *
 * The Getter returns all the entries that the client has requested.
 * By default this will be just be the client state (ie id etc), until the server has responded with either a presigned
 * url or an error.
 *
 * The setter will add the playlist entries requested by the client and if the client has not specifically requested an
 * entry to be selected, will select the first one automatically.
 */
const playlistEntriesAtom = atom((get) => {
    const clientEntries = get(playlistEntriesClientAtom);
    const serverEntries = get(playlistEntriesServerAtom);
    return clientEntries.map((client) => {
        const serverEntry = serverEntries.find((server) => server.id === client.id);
        return mergeClientServerEntryData(client, serverEntry);
    });
}, (_, set, playlistEntries) => {
    const entries = [...playlistEntries];
    const selected = playlistEntries.find(bySelected);
    if (!selected && entries.length) {
        entries[0] = { ...entries[0], selectedEntry: true };
    }
    set(playlistEntriesClientAtom, entries);
});
playlistEntriesAtom.debugLabel = 'video.client.playlist-entries';
const updatePlaylistEntryAtom = atom(null, (get, set, newValues) => {
    const clientEntries = get(playlistEntriesClientAtom);
    const index = clientEntries.findIndex(byId(newValues.id));
    clientEntries[index] = newValues;
    set(playlistEntriesAtom, clientEntries);
});
updatePlaylistEntryAtom.debugLabel = 'video.client.playlist-entry.update';
/**
 * Read only atom for playlist entries without errors.
 */
const validPlaylistEntriesAtom = atom((get) => get(playlistEntriesAtom).filter(byNotErroneous));
validPlaylistEntriesAtom.debugLabel = 'video.client.valid-playlist-entries';
/**
 * Determines if the playlist has entries or not.
 */
const hasPlaylistEntriesAtom = atom((get) => !!get(playlistEntriesAtom).length);
hasPlaylistEntriesAtom.debugLabel = 'video.client.has-playlist-entries';
/**
 * Write only atom used to map the server response to a `PlaylistEntry`.
 * Merges the contents of the current server response with the new entries, allowing us to paginate if required.
 */
const addPlaylistEntriesServerResponseAtom = atom(null, (get, set, serverResponse) => {
    const serverResponseWithBuffer = serverResponse.map(applyBuffer);
    const currentPlaylistEntriesWithServerResponse = get(playlistEntriesServerAtom);
    // Using a map here so we don't get dupes.
    const entryMap = new Map(currentPlaylistEntriesWithServerResponse.map((entries) => [
        entries.id,
        entries,
    ]));
    serverResponseWithBuffer.forEach((entry) => {
        entryMap.set(entry.id, { ...entryMap.get(entry.id), ...entry });
    });
    // Convert back to array.
    const mergedEntries = Array.from(entryMap.values());
    set(playlistEntriesServerAtom, mergedEntries);
});
addPlaylistEntriesServerResponseAtom.debugLabel =
    'video.client.playlist-entries.add-server-response';
/**
 * Write only atom used to remove the server response for a `PlaylistEntry`.
 */
const removePlaylistEntriesServerResponseAtom = atom(null, (get, set, idToRemove) => {
    set(playlistEntriesServerAtom, get(playlistEntriesServerAtom).filter((entry) => entry.id !== idToRemove));
});
removePlaylistEntriesServerResponseAtom.debugLabel =
    'video.client.playlist-entries.remove-server-response';
/**
 * Derived atom that manages the currently selected entry in the playlist.
 */
const selectedPlaylistEntryAtom = atom((get) => get(playlistEntriesAtom).find(bySelected), (get, set, id) => {
    set(autoSkipEnabledAtom, false);
    const playlistEntries = get(playlistEntriesAtom).map(deselect);
    const entryToSelect = playlistEntries.find(byId(id));
    if (entryToSelect) {
        entryToSelect.selectedEntry = true;
        set(playlistEntriesAtom, playlistEntries);
    }
});
selectedPlaylistEntryAtom.debugLabel = 'video.client.playlist-entries.selected';
/**
 * Read only atom that stores the index of the currently selected playlist entry.
 */
const selectedPlaylistEntryIndexAtom = atom((get) => get(validPlaylistEntriesAtom).findIndex(bySelected));
selectedPlaylistEntryIndexAtom.debugLabel =
    'video.client.playlist-entries.selectedIndex';
/**
 * Write only atom that deletes all server responses for any clip that contains the same presigned url.
 * Returns a list of all the ids that have been removed.
 *
 * Contains a side effect where the SWR cache is mutated. @todo need a nicer way of doing this.
 */
const expirePresignedUrlsAtom = atom(null, async (get, set, { id: idToExpire, mutate, }) => {
    const expireIdsFromCache = (ids = []) => Promise.all(ids.map((id) => mutate((key) => typeof key === 'string' && key.includes(id), undefined)));
    const playlistEntry = get(playlistEntriesServerAtom).find(byId(idToExpire)) || {};
    if (!('presignedUrl' in playlistEntry && playlistEntry.presignedUrl))
        // No presigned url on this atom for some reason. Bail here.
        return [];
    const { presignedUrl } = playlistEntry;
    const byPlaylistUrl = (entry) => 'presignedUrl' in entry && entry.presignedUrl === presignedUrl;
    const idsToRemove = get(playlistEntriesServerAtom)
        .filter(byPlaylistUrl)
        .map(({ id }) => id);
    await expireIdsFromCache(idsToRemove);
    set(playlistEntriesServerAtom, get(playlistEntriesServerAtom).filter((entry) => !idsToRemove.includes(entry.id)));
    return idsToRemove;
});
expirePresignedUrlsAtom.debugLabel =
    'video-client.playlist-entries.expire-presigned-urls';
const reorderPlaylistAtom = atom(undefined, (get, set, { activeId, overId }) => {
    set(playlistEntriesClientAtom, () => {
        const currentValue = get(playlistEntriesClientAtom);
        const oldIndex = currentValue.findIndex((item) => item.id === activeId);
        const newIndex = currentValue.findIndex((item) => item.id === overId);
        return arrayMove(currentValue, oldIndex, newIndex);
    });
});
reorderPlaylistAtom.debugLabel =
    'video-client.playlist-entries.reorder-playlist';
const currentlyDraggingPlaylistEntryIdAtom = atom(undefined);
currentlyDraggingPlaylistEntryIdAtom.debugLabel =
    'video.client.playlist-entries.currently-dragging-playlist-entry-id';
const removeFromPlaylistAtom = atom(undefined, (get, set, idsToRemove) => {
    set(playlistEntriesClientAtom, get(playlistEntriesClientAtom).filter((entry) => !idsToRemove.includes(entry.id)));
});
removeFromPlaylistAtom.debugLabel =
    'video-client.playlist-entries.remove-from-playlist';

const getVideoClipReferenceAtom = atom((get) => {
    const currentlySelected = get(selectedPlaylistEntryAtom);
    return currentlySelected && 'videoClipReference' in currentlySelected
        ? currentlySelected.videoClipReference
        : undefined;
});
getVideoClipReferenceAtom.debugLabel = 'video.client.videoClipReference';
const getVideoClipStartTimestampAtom = atom((get) => { var _a; return ((_a = get(getVideoClipReferenceAtom)) === null || _a === void 0 ? void 0 : _a.startTimestamp) || 0; });
getVideoClipStartTimestampAtom.debugLabel =
    'video.client.videoClipReference.startTimestamp';
const getVideoClipEndTimestampAtom = atom((get) => { var _a; return ((_a = get(getVideoClipReferenceAtom)) === null || _a === void 0 ? void 0 : _a.endTimestamp) || 0; });
getVideoClipEndTimestampAtom.debugLabel =
    'video.client.videoClipReference.endTimestamp';
const getVideoClipSportAtom = atom((get) => {
    const videoClipReference = get(getVideoClipReferenceAtom);
    return videoClipReference && 'videoReference' in videoClipReference
        ? videoClipReference.videoReference.sport
        : null;
});
getVideoClipSportAtom.debugLabel =
    'video.client.videoClipReference.videoClipSport';
const getVideoClipDurationAtom = atom((get) => get(getVideoClipEndTimestampAtom) - get(getVideoClipStartTimestampAtom));
getVideoClipDurationAtom.debugLabel =
    'video.client.videoClipReference.videoClipDuration';
const getIsSoccerClipAtom = atom((get) => !!get(getVideoClipEndTimestampAtom) &&
    get(getVideoClipSportAtom) === VideoSport.Soccer);
getIsSoccerClipAtom.debugLabel =
    'video.client.videoClipReference.getIsSoccerClip';

const currentPlaybackTimeAtom = atom(0);
currentPlaybackTimeAtom.debugLabel = 'video.client.currentPlaybackTime';
const currentPlaybackItemDurationAtom = atom(0);
currentPlaybackItemDurationAtom.debugLabel =
    'video.client.currentPlaybackItemDuration';
const videoCanPlayAtom = atom(false);
videoCanPlayAtom.debugLabel = 'video.client.videoCanPlay';
const videoSeekingAtom = atom(false);
videoSeekingAtom.debugLabel = 'video.client.videoSeeking';
const videoPlaybackFullDurationReachedAtom = atom(false);
videoPlaybackFullDurationReachedAtom.debugLabel =
    'video.client.videoPlaybackFullDurationReached';
const videoClipEndedAtom = atom((get) => {
    const isAClip = get(getVideoClipEndTimestampAtom);
    const clipEnded = get(currentPlaybackTimeAtom) >= get(getVideoClipEndTimestampAtom);
    const fullVideoEnded = get(videoPlaybackFullDurationReachedAtom);
    return isAClip ? clipEnded : fullVideoEnded;
});
videoClipEndedAtom.debugLabel = 'video.client.videoClipEnded';
const videoIsPlayingAtom = atom(false);
videoIsPlayingAtom.debugLabel = 'video.client.videoIsPlaying';
const videoResolutionAtom = atom([1920, 1080]);
videoResolutionAtom.debugLabel = 'video.client.videoResolution';
const TWO_SECONDS_AS_MICROSECONDS = secondsToMicroseconds(2);
const getVideoClipRecentlyStartedAtom = atom((get) => get(currentPlaybackTimeAtom) <
    get(getVideoClipStartTimestampAtom) + TWO_SECONDS_AS_MICROSECONDS);
getVideoClipRecentlyStartedAtom.debugLabel =
    'video.client.videoClipReference.clipRecentlyStarted';
const getVideoClipNearlyEndingAtom = atom((get) => get(currentPlaybackTimeAtom) >
    get(getVideoClipEndTimestampAtom) - TWO_SECONDS_AS_MICROSECONDS);
getVideoClipNearlyEndingAtom.debugLabel =
    'video.client.videoClipReference.getVideoClipNearlyEnding';

const VIDEO_EVENTS = [
    'canplay',
    'canplaythrough',
    'destroy',
    'durationchange',
    'emptied',
    'ended',
    'error',
    'loadeddata',
    'loadedmetadata',
    'loadstart',
    'manifestupdate',
    'nosupportedrepresentationfound',
    'pause',
    'play',
    'playing',
    'progress',
    'ratechange',
    'readystatechange',
    'seeked',
    'seeking',
    'segmentnotfound',
    'sourcechange',
    'timeupdate',
    'volumechange',
    'waiting',
];

var Endpoint;
(function (Endpoint) {
    Endpoint["CUTUPS"] = "cutups";
    Endpoint["EVENTS"] = "events";
    Endpoint["GSL_CREDENTIALS"] = "gsl-credentials";
    Endpoint["LICENSE"] = "player-license";
    Endpoint["MY_PLAYLISTS"] = "playlist";
    Endpoint["PLAYLIST_ENTRY"] = "playlist/entry";
    Endpoint["PLAYS"] = "plays/AMF";
    Endpoint["SEQUENCES"] = "sequences";
})(Endpoint || (Endpoint = {}));

var Case;
(function (Case) {
    Case["Camel"] = "camel";
    Case["Snake"] = "snake";
})(Case || (Case = {}));
const toCamelCase = (key) => {
    const keyWithSpecialCharsRemoved = key.replace(/([-_][a-z])/g, (group) => group.toUpperCase().replace('-', '').replace('_', ''));
    return (keyWithSpecialCharsRemoved.charAt(0).toLowerCase() +
        keyWithSpecialCharsRemoved.slice(1));
};
const toSnakeCase = (key) => key.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase();
const CASE_FORMATTER = {
    [Case.Camel]: toCamelCase,
    [Case.Snake]: toSnakeCase,
};
/**
 * Recursively converts all object keys from snake_case to camelCase and vice versa.
 * This function handles nested objects and arrays as well.
 *
 * @param {*} valueToConvert - The object whose keys are to be converted. Can be an object, array, or any other type.
 * @param {Case} type
 * @returns {*} The new object with its keys in camelCase. If the input is an array or a non-object,
 * it returns the input as is.
 *
 * @example
 * convertKeys({ 'example-key': 'value', nested_object: { 'another-key': 'value' } }, Case.Camel);
 * // Returns: { exampleKey: 'value', nestedObject: { anotherKey: 'value' } }
 */
const convertKeys = (valueToConvert, type = Case.Camel) => {
    const convertKey = CASE_FORMATTER[type];
    if (Array.isArray(valueToConvert)) {
        return valueToConvert.map((value) => convertKeys(value, type));
    }
    if (valueToConvert !== null && (valueToConvert === null || valueToConvert === void 0 ? void 0 : valueToConvert.constructor) === Object) {
        return Object.keys(valueToConvert).reduce((result, key) => ({
            ...result,
            [convertKey(key)]: convertKeys(valueToConvert[key], type),
        }), {});
    }
    return valueToConvert;
};

/**
 * Creates a custom fetcher that gets the auth0 auth token and attaches it to our headers before performing the fetch.
 * An optional abort signal can be passed in to cancel the request.
 */
const fetcher = ({ getAccessTokenSilently, signal }) => async (endpoint, options = {}) => {
    const authToken = await getAccessTokenSilently();
    const response = await fetch(endpoint, {
        ...options,
        headers: {
            ...((options === null || options === void 0 ? void 0 : options.headers) || {}),
            Authorization: `Bearer ${authToken}`,
        },
        signal,
    });
    if (!response.ok) {
        const error = new Error('Api response not ok');
        error.status = response.status;
        error.message = ((await response.json()) || {}).message;
        throw error;
    }
    return convertKeys(await response.json());
};
/**
 * Wraps the useSWR with a custom fetcher that handles auth for use
 *
 * What is SWR and how to use it: https://swr.vercel.app/
 *
 * @param {URL} [endpoint]
 * @param {SWRConfiguration} [config]
 * @returns {SWRResponse}
 */
function useSWRWithAuth(endpoint, config) {
    const { search, signal, ...swrConfig } = config || {};
    const baseVideoServiceUrl = useAtomValue(baseUrlVideoServiceAtom);
    const { getAccessTokenSilently } = useKitbagAuth();
    const url = new URL(`${baseVideoServiceUrl}/${endpoint}`);
    if (search) {
        url.search = new URLSearchParams(search).toString();
    }
    const fullUrl = endpoint ? url.toString() : undefined;
    return useSWR(fullUrl, fetcher({ getAccessTokenSilently, signal }), swrConfig);
}

const HUDL_CONFIGURE_COOKIE_URL = 'https://www.hudl.com/app/users/configure_video_cookie';
const HUDL_COOKIE_DURATION = 36000;
const config = { revalidateOnFocus: false };
const usePlaybackAuth = () => {
    const [gslAuthComplete, setGSLAuthComplete] = useState(false);
    const [theoPlayerLicense, setTheoPlayerLicense] = useState(undefined);
    const { data: licenseData } = useSWRWithAuth(Endpoint.LICENSE, config);
    const { data: credentialsData } = useSWRWithAuth(Endpoint.GSL_CREDENTIALS, config);
    const configureVideoCookie = async ({ policy, signature, keyPairId, }) => {
        const params = new URLSearchParams();
        params.append('cookieDurationSeconds', HUDL_COOKIE_DURATION.toString());
        params.append('policy', policy);
        params.append('keyPairId', keyPairId);
        params.append('signature', signature);
        await fetch(HUDL_CONFIGURE_COOKIE_URL, {
            body: params.toString(),
            credentials: 'include',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            method: 'POST',
            mode: 'no-cors',
        });
        setGSLAuthComplete(true);
    };
    useEffect(() => {
        // useEffect Required to prevent setState in render errors
        if (licenseData === null || licenseData === void 0 ? void 0 : licenseData.license) {
            setTheoPlayerLicense(licenseData.license);
        }
    }, [licenseData]);
    useEffect(() => {
        // useEffect Required to prevent setState in render errors
        async function handleNewCredentialData() {
            if (credentialsData) {
                await configureVideoCookie(credentialsData);
            }
        }
        handleNewCredentialData();
    }, [credentialsData]);
    return { gslAuthComplete, theoPlayerLicense };
};

/**
 * Returns a function that can be used within a callback to get the current state of an atom without requiring a
 * rerender
 *
 * @param {Atom} atom - can be of `any` type.
 */
const useCallbackSafeAtomValue = (atom) => useAtomCallback(useCallback((get) => get(atom), []));

function useVideoCanPlay() {
    return useAtomValue(videoCanPlayAtom);
}
function useVideoCanPlayInCallback() {
    return useCallbackSafeAtomValue(videoCanPlayAtom);
}
function useVideoCurrentTime() {
    return useAtomValue(currentPlaybackTimeAtom);
}
function useVideoCurrentTimeInCallback() {
    return useCallbackSafeAtomValue(currentPlaybackTimeAtom);
}
function useVideoDuration() {
    return useAtomValue(currentPlaybackItemDurationAtom);
}
function useVideoDurationInCallback() {
    return useCallbackSafeAtomValue(currentPlaybackItemDurationAtom);
}
function useVideoIsPlaying() {
    return useAtomValue(videoIsPlayingAtom);
}
function useVideoIsPlayingInCallback() {
    return useCallbackSafeAtomValue(videoIsPlayingAtom);
}
function useVideoResolution() {
    return useAtomValue(videoResolutionAtom);
}
function useVideoResolutionInCallback() {
    return useCallbackSafeAtomValue(videoResolutionAtom);
}

var SVG = styled.svg `
  bottom: 0;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
`;

function VideoOverlay({ children }) {
    const [height, width] = useVideoResolution();
    return (jsx(SVG, { width: "100%", height: "100%", viewBox: `0 0 ${height} ${width}`, children: children }));
}

const StyledTheoPlayer = styled.div `
  height: 100%;
  width: 100%;
`;

function TheoPlayer({ onMount, testId }) {
    return (jsx(StyledTheoPlayer, { className: "theoplayer-container THEOplayer", "data-testid": testId, ref: onMount, tabIndex: -1 }));
}

var useFullScreenHandler = (videoContainer) => {
    const [fullscreenRequested, setFullscreenRequested] = useAtom(fullscreenRequestedAtom);
    const handleOnFullscreenChange = () => {
        setFullscreenRequested(!!document.fullscreenElement);
        videoContainer === null || videoContainer === void 0 ? void 0 : videoContainer.focus();
    };
    useEffect(() => {
        document.addEventListener('fullscreenchange', handleOnFullscreenChange);
        return () => {
            document.removeEventListener('fullscreenchange', handleOnFullscreenChange);
        };
    }, [videoContainer]);
    useEffect(() => {
        const fullscreenRequestedChange = async () => {
            if (fullscreenRequested) {
                await (videoContainer === null || videoContainer === void 0 ? void 0 : videoContainer.requestFullscreen());
            }
            else if (document.fullscreenElement) {
                await document.exitFullscreen();
            }
        };
        fullscreenRequestedChange();
    }, [fullscreenRequested]);
    return fullscreenRequested;
};

/**
 * Conditionally render the FlyoutProvider for the Video Controls depending on if we're in fullscreen mode or not.
 *
 * When in fullscreen mode the Flyout needs to be attached to the Video Container element rather than the body
 * because users will not be able to see the Flyout's content as it will be rendered behind the full screen
 * container.
 *
 * When we're not in fullscreen mode the Flyout should be attached to the document.body in order to ensure it's
 * rendered over the rest of the application.
 *
 * Without this conditional logic, the Flyout's context will not be visible outside the Video Container.
 *
 * @param {ReactNode} children
 * @constructor
 */
function VideoContainer({ children }) {
    const ref = useRef(null);
    const flyoutElement = useFlyoutPortalElement();
    const isFullScreen = useFullScreenHandler(ref === null || ref === void 0 ? void 0 : ref.current);
    const isSoccerClip = useAtomValue(getIsSoccerClipAtom);
    const showMobileControls = useAtomValue(showMobileControlsAtom);
    const renderControlsBelowVideo = useAtomValue(renderControlsBelowVideoAtom);
    return (jsx(Root, { ref: ref, tabIndex: -1, "$allowAdditionalSpaceForTimelineButtons": isSoccerClip, "$showMobileControls": showMobileControls, "$showControlsBelowVideo": renderControlsBelowVideo, "$isFullScreen": isFullScreen, children: jsx(FlyoutProvider, { value: isFullScreen ? ref : { current: flyoutElement }, children: children }) }));
}
var VideoContainer$1 = memo(VideoContainer);

const Wrapper = styled.div `
  bottom: 0;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
`;
styled.video `
  height: 100%;
  width: 100%;
`;
const VideoLoaderWrapper = styled.div `
  position: relative;
  z-index: 1;
`;

const keyboardShortcutsAtom = atom(undefined);
keyboardShortcutsAtom.debugLabel = 'video.client.keyboardShortcuts';
const displayKeyboardShortcutsAtom = atom(false);
displayKeyboardShortcutsAtom.debugLabel =
    'video.client.displayKeyboardShortcuts';

var videoClient = {
	addToPlaylistTitle: "Add Tracks To Playlist",
	apply: "Apply",
	back: "Back",
	cancel: "Cancel",
	changePlaybackRate: "Change playback rate",
	checkBoxLabel: "select {{description}} for playlist",
	clearAll: "Clear all",
	close: "Close",
	confirmDeleteCutup: "Are you sure you want to delete {{cutupKey}}?",
	confirmDeleteFromPlaylist: "Are you sure you want to delete from this playlist?",
	confirmDeletePlaylist: "Are you sure you want to delete playlist {{playlistName}}?",
	createCutupLabel: "Export",
	createCutupInputLabel: "Name your video",
	createCutupInputPlaceholder: "Name your new video",
	createCutupTitle: "Export {{count}} Track(s)",
	createCutupVideoCount: "Export {{count}} video(s)",
	createdOn: "Created on {{date}}",
	cropClipEndTimestampTitle: "Crop end",
	cropClipEndTimestampTitleDisabledClipEndEqualsPlaybackTime: "Crop end. Clip end is equal to current playback time",
	cropClipEndTimestampTitleDisabledClipLowerLimitWouldBeExceeded: "Crop end. Clip must be min length",
	cropClipStartTimestampTitle: "Crop start",
	cropClipStartTimestampTitleDisabledClipStartEqualsPlaybackTime: "Crop start. Clip start is equal to current playback time",
	cropClipStartTimestampTitleDisabledClipLowerLimitWouldBeExceeded: "Crop start. Clip must be min length",
	currentlyPlayingDefaultPlaylistTitle: "Currently Playing",
	defaultButtonText: "Return home",
	defaultEmptyMessage: "Playlist Entries Will Appear Here",
	defaultError: "There was an error authenticating the user",
	defaultLoading: "Loading...",
	defaultPlaylistTitle: "My Playlists",
	defaultUnauthenticated: "The user is unauthenticated",
	defaultUnauthorised: "User is unauthorised for this component",
	"delete": "Delete",
	deleteCutup: "Delete Video",
	deleteCutupConfirmationTitle: "Delete {{cutupKey}}",
	deleteCutupTitle: "Delete Video {{cutupKey}}",
	deleteFromPlaylistTitle: "Delete {{count}} Track(s) From Playlist",
	deletePlaylist: "Delete Playlist",
	deletePlaylistConfirmationTitle: "Delete Playlist {{playlistName}}",
	download: "Download Video",
	editPlaylist: "Edit Playlist",
	errorAddingToPlaylist: "Playlist(s) update failed - please try again.",
	erroredOn: "Errored. Updated {{date}}",
	errorCreatingCutup: "Error creating video - please try again.",
	errorDeletingCutup: "Error deleting {{cutupKey}}",
	errorDeletingPlaylist: "Error deleting {{playlistName}}.",
	errorRemovingPlaylist: "Error removing from {{playlistName}}",
	errorRenamingPlaylist: "Error renaming playlist",
	errorRetrievingMyCutups: "Error retrieving videos",
	errorRetrievingPlaylists: "Error retrieving playlists",
	errorSavingCurrentPlaylist: "Error saving playlist order",
	errorSavingPlaylistEntryUpdate: "Error saving playlist entry update",
	exitFullscreen: "Exit fullscreen",
	extendClipEndTimestampTitle: "Extend end",
	extendClipEndTimestampTitleBoundaryDisabled: "Extend end. Clip cannot exceed video boundaries",
	extendClipEndTimestampTitleSizeLimitDisabled: "Extend end. Clip size limit reached",
	extendClipStartTimestampTitle: "Extend start",
	extendClipStartTimestampTitleBoundaryReachedDisabled: "Extend start. Clip cannot exceed video boundaries",
	extendClipStartTimestampTitleSizeLimitDisabled: "Extend start. Clip size limit reached",
	inProgressUpdatedOn: "In Progress. Updated {{time}}",
	highlightPlayersFull: "Highlight Players (On)",
	highlightPlayersOff: "Highlight Players (Off)",
	highlightPlayersSelectionTitle: "Select players to highlight",
	keybindingDisplayLabel: "View Keyboard Shortcuts",
	keybindingDisplayTitle: "Keyboard Shortcuts",
	keybindingActionTitlePlayPause: "Play/Pause",
	keybindingActionTitlePlaybackSpeedIncrease: "Playback Speed (Increase)",
	keybindingActionTitlePlaybackSpeedDecrease: "Playback Speed (Decrease)",
	keybindingActionTitleFullscreenToggle: "Fullscreen",
	keybindingActionTitleNext: "Skip (Next)",
	keybindingActionTitlePrevious: "Skip (Previous)",
	keybindingActionTitleVolumeIncrease: "Volume (Increase)",
	keybindingActionTitleVolumeDecrease: "Volume (Decrease)",
	keybindingActionTitleVolumeMute: "Volume (Mute)",
	keybindingActionTitleScrubForwardsFine: "Scrub Forwards (Fine)",
	keybindingActionTitleScrubBackwardsFine: "Scrub Backwards (Fine)",
	keybindingActionTitleScrubForwards: "Scrub Forwards",
	keybindingActionTitleScrubBackwards: "Scrub Backwards",
	keybindingActionTitleScrubForwardsSingleStep: "Scrub Forwards (Frame)",
	keybindingActionTitleScrubBackwardsSingleStep: "Scrub Backwards (Frame)",
	modifiedOn: "Modified on {{date}}",
	moreCutupActions: "Video actions",
	moreOptionsLabel: "More Video Options",
	morePlaylistActions: "Playlist actions",
	mute: "Mute",
	myCutupsTitle: "My Videos",
	next: "Next",
	noPlayerHighlightingAvailable: "No player highlighting available",
	numVideos: "{{number}} videos",
	pause: "Pause",
	pauseButtonTitle: "Pause {{description}}",
	play: "Play",
	playButtonTitle: "Play {{description}}",
	playbackRateLabel: "Playback Rate:",
	playlistInputPlaceholder: "Name your new playlist",
	playlistNameInputLabel: "Name your list",
	queuedOn: "Queued. Updated {{time}}",
	renamePlaylist: "Rename Playlist",
	reorderScreenReaderInstructions: "To pick up a sortable item, press enter. While sorting, use the arrow keys to move the item. Press enter again to drop the item in its new position, or press escape to cancel",
	save: "Save",
	saveCurrentlyPlaying: "Save currently playing",
	savePlaylistTitle: "Save {{count}} Track(s) To Playlist",
	saveVideoCount: "Save {{count}} video(s)",
	scrubBackSeconds: "Scrub back {{seconds}}s",
	scrubForwardSeconds: "Scrub forward {{seconds}}s",
	selectAll: "Select all",
	successfullyAddedToPlaylist: "Playlist(s) updated successfully.",
	successfullyCreatedPlaylist: "Playlist created successfully.",
	successfullyCreatedCutup: "Video creation requested. Please check 'My Clips'",
	successfullyDeletedCutup: "Deleted {{cutupKey}} successfully",
	successfullyDeletedFromPlaylist: "Deleted from {{playlistName}} successfully",
	successfullyDeletedPlaylist: "Deleted {{playlistName}} successfully",
	timesPlaybackRate: "x{{playbackRate}} playback rate",
	unmute: "Unmute",
	videoTimeline: "Video timeline",
	videos: "Videos",
	viewFullscreen: "View fullscreen",
	volumeControl: "Volume control",
	noPlaylists: "Nothing to see here yet",
	videoNotAvailableError: "Video is not currently available",
	videoNotFoundErrorDefault: "Video Not Found",
	videoNotFoundErrorLicense: "Video Not Available",
	videoNotFoundErrorUnknown: "Video Retrieval Failed"
};
var defaultTranslations = {
	videoClient: videoClient
};

const translationsAtom = atom(defaultTranslations === null || defaultTranslations === void 0 ? void 0 : defaultTranslations.videoClient);
translationsAtom.debugLabel = 'video.client.translations';
function mergeTranslations(newTranslations) {
    return { ...defaultTranslations === null || defaultTranslations === void 0 ? void 0 : defaultTranslations.videoClient, ...newTranslations };
}

const getMissingTranslationConsoleMessage = (key) => `Translation key "${key}" was not found.`;
const getMissingTranslationMessage = (key) => `*** Missing Translation:${key} ***`;

/**
 * Hook that retrieves all translations requested
 *
 * @returns The translations object.
 */
const useTranslations = (keys) => {
    const allTranslations = useAtomValue(translationsAtom);
    // if keys are passed in, use those to return what has been requested.
    return keys.reduce((acc, key) => {
        if (key in allTranslations) {
            acc[key] = allTranslations[key];
        }
        else {
            acc[key] = getMissingTranslationMessage(key);
            // eslint-disable-next-line no-console
            console.warn(getMissingTranslationConsoleMessage(key));
        }
        return acc;
    }, {});
};

const HeaderContent = styled.div `
  display: flex;
  justify-content: space-between;
  width: 100%;
`;
const StyledLi = styled.li `
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: 0.25rem 0rem;
`;
const KeyContainer = styled.span `
  display: flex;
  gap: 0.25rem;
  justify-content: space-between;
`;
const StyledKey = styled.span `
  background-color: ${({ theme }) => theme.colours.canvas.secondary.main};
  padding: 0.25rem 0.5rem;
`;

const byActionTitle = ([, a], [, b]) => a.localeCompare(b);
function KeyboardShortcutsDisplay() {
    const keyboardShortcuts = useAtomValue(keyboardShortcutsAtom);
    const [displayKeyboardShortcuts, setDisplayKeyboardShortcuts] = useAtom(displayKeyboardShortcutsAtom);
    const { close: closeLabel, keybindingDisplayLabel, keybindingDisplayTitle, } = useTranslations([
        'close',
        'keybindingDisplayLabel',
        'keybindingDisplayTitle',
    ]);
    const close = () => setDisplayKeyboardShortcuts(false);
    const open = () => setDisplayKeyboardShortcuts(true);
    return keyboardShortcuts ? (jsxs(Fragment, { children: [jsx(ButtonIcon, { variant: "ghost", size: "regular", displayText: "hidden", onClick: open, icon: "Support", title: keybindingDisplayLabel, children: keybindingDisplayLabel }), jsxs(Modal, { id: "keyboard-shortcuts-display", isOpen: displayKeyboardShortcuts, onCancel: undefined, onConfirm: close, onEscape: close, children: [jsx(ModalHeader, { id: "keyboard-shortcuts-display-header", children: jsxs(HeaderContent, { "data-testid": "keyboard-shortcuts-header", children: [jsx("span", { children: keybindingDisplayTitle }), jsx(ButtonIcon, { icon: "Close", onClick: close, size: "small", title: closeLabel, variant: "ghost" })] }) }), jsx(ModalBody, { children: jsx("ol", { children: keyboardShortcuts
                                .sort(byActionTitle)
                                .map(([keys, actionTitle]) => (jsxs(StyledLi, { children: [jsx("span", { children: jsx(Typography, { variant: "bodyRegular", children: actionTitle }) }), jsx("span", { children: jsx(KeyContainer, { children: keys.split('+').map((key) => (jsx(StyledKey, { children: jsx(Typography, { variant: "bodyRegular", children: key }) }, key))) }) })] }, actionTitle))) }) })] })] })) : null;
}

var SkipDirection;
(function (SkipDirection) {
    SkipDirection["FORWARD"] = "forward";
    SkipDirection["BACK"] = "back";
})(SkipDirection || (SkipDirection = {}));
var PlaylistType;
(function (PlaylistType) {
    PlaylistType[PlaylistType["CLIP"] = 0] = "CLIP";
    PlaylistType[PlaylistType["EVENT"] = 1] = "EVENT";
    PlaylistType[PlaylistType["FULL"] = 2] = "FULL";
    PlaylistType[PlaylistType["PLAY"] = 3] = "PLAY";
    PlaylistType[PlaylistType["SEQUENCE"] = 4] = "SEQUENCE";
})(PlaylistType || (PlaylistType = {}));

const checkedPlaylistEntriesAtom = atom([]);
checkedPlaylistEntriesAtom.debugLabel = 'video.client.checkedPlaylistEntries';
const selectedPlaylistEntriesVideoClipReferencesAtom = atom((get) => get(playlistEntriesAtom).filter((entry) => {
    var _a;
    return entry &&
        'videoClipReference' in entry &&
        ((_a = entry.videoClipReference) === null || _a === void 0 ? void 0 : _a.endTimestamp) &&
        get(checkedPlaylistEntriesAtom).includes(entry.id);
}));
selectedPlaylistEntriesVideoClipReferencesAtom.debugLabel =
    'video.client.selectedPlaylistEntriesVideoClipReferences';
const playlistIsEditModeAtom = atom(false);
playlistIsEditModeAtom.debugLabel = 'video.client.playlistIsEditMode';
/**
 * Atom that manages skip forward and back requests.
 * Read state returns a boolean to determine if skipping forward for backwards can happen.
 */
const skipAtom = atom((get) => {
    const playlistEntries = get(validPlaylistEntriesAtom);
    const currentlyPlayingIndex = get(selectedPlaylistEntryIndexAtom);
    return {
        back: playlistEntries[currentlyPlayingIndex - 1],
        forward: playlistEntries[currentlyPlayingIndex + 1],
    };
}, (get, set, direction = SkipDirection.FORWARD) => {
    const playlistEntries = get(validPlaylistEntriesAtom);
    const currentlyPlayingIndex = get(selectedPlaylistEntryIndexAtom);
    const playlistEntryIndexToUpdate = direction === SkipDirection.BACK
        ? currentlyPlayingIndex - 1
        : currentlyPlayingIndex + 1;
    // Note that this is setting the selectedPlaylistEntryAtom in playlistEntries
    set(selectedPlaylistEntryAtom, playlistEntries[playlistEntryIndexToUpdate].id);
});
skipAtom.debugLabel = 'video.client.playlist.skip';
/**
 * Toggle values by adding them in and removing them from the checkedPlaylistEntries atom
 * */
const toggleCheckedPlaylistEntriesAtom = atom((get) => get(checkedPlaylistEntriesAtom), (get, set, idToAddOrRemove) => {
    const currentSelection = get(checkedPlaylistEntriesAtom);
    const selectedIndex = currentSelection
        .map((selectedList) => selectedList)
        .indexOf(idToAddOrRemove);
    set(checkedPlaylistEntriesAtom, selectedIndex !== -1
        ? currentSelection.filter((id) => id !== idToAddOrRemove)
        : [...currentSelection, idToAddOrRemove]);
});
/**
 * Clears the checkedPlaylistEntries atom by resetting the array.
 * */
const clearCheckedPlaylistEntriesAtom = atom((get) => get(checkedPlaylistEntriesAtom), (_, set) => {
    set(checkedPlaylistEntriesAtom, []);
});
/**
 * Adds all the given playlist entries to the holdingList atom.
 * */
const addAllToCheckedPlaylistEntriesAtom = atom((get) => get(checkedPlaylistEntriesAtom), (_, set, playlist) => {
    const ids = playlist.map((entry) => entry.id);
    set(checkedPlaylistEntriesAtom, [...ids]);
});
/**
 * Remove checked playlist entries no longer present
 * */
const removeCheckedPlaylistEntriesNoLongerPresentAtom = atom((get) => get(checkedPlaylistEntriesAtom), (get, set) => {
    const playlisEntriesIds = get(playlistEntriesAtom).map((entry) => entry.id);
    const filteredCheckedEntries = get(checkedPlaylistEntriesAtom).filter((checkedId) => playlisEntriesIds.includes(checkedId));
    set(checkedPlaylistEntriesAtom, filteredCheckedEntries);
});
const playlistTypeAtom = atom(PlaylistType.EVENT);
playlistTypeAtom.debugLabel = 'video.client.playlist.type';

const PLAYER_BUTTON_VARIANT = 'ghost';
var Controls;
(function (Controls) {
    Controls["BackButton"] = "BackButton";
    Controls["ChangePlaybackRateButton"] = "ChangePlaybackRateButton";
    Controls["FullscreenButton"] = "FullscreenButton";
    Controls["HighlightPlayersButton"] = "HighlightPlayersButton";
    Controls["MuteButton"] = "MuteButton";
    Controls["NextButton"] = "NextButton";
    Controls["PlayerTimeDisplay"] = "PlayerTimeDisplay";
    Controls["PlayerTimeline"] = "PlayerTimeline";
    Controls["PlayPauseButton"] = "PlayPauseButton";
    Controls["SaveCurrentlyPlaying"] = "SaveCurrentlyPlaying";
    Controls["ScrubBack"] = "ScrubBack";
    Controls["ScrubBackFine"] = "ScrubBackFine";
    Controls["ScrubForward"] = "ScrubForward";
    Controls["ScrubForwardFine"] = "ScrubForwardFine";
    Controls["KeyboardShortcutsDisplay"] = "KeyboardShortcutsDisplay";
})(Controls || (Controls = {}));
const DEFAULT_CONTROLS = [
    Controls.BackButton,
    Controls.ChangePlaybackRateButton,
    Controls.FullscreenButton,
    // Only relevant for VideoPlay but the button is smart enough not to render if no data is available.
    Controls.HighlightPlayersButton,
    Controls.MuteButton,
    Controls.NextButton,
    Controls.PlayerTimeDisplay,
    Controls.PlayerTimeline,
    Controls.PlayPauseButton,
    Controls.KeyboardShortcutsDisplay,
];
const configureVideoControls = ({ controlsToIncludeInAdditionToDefault = [], controlsToExcludeFromDefault = [], }) => Array.from(new Set([
    ...DEFAULT_CONTROLS.filter((control) => !controlsToExcludeFromDefault.includes(control)),
    ...controlsToIncludeInAdditionToDefault,
]));

/**
 * Hook that retrieves the translation for the specified key.
 *
 * @param key - The key of the translation to retrieve.
 * @returns The translation string.
 */
const useTranslation = (key) => {
    const translations = useAtomValue(translationsAtom);
    if (!translations[key]) {
        // eslint-disable-next-line no-console
        console.warn(getMissingTranslationConsoleMessage(key));
        return getMissingTranslationMessage(key);
    }
    return translations[key];
};

function BackButton() {
    const toggleResetRequested = useSetAtom(resetRequestedAtom);
    const [canSkip, skip] = useAtom(skipAtom);
    const videoIsPlaying = useAtomValue(playRequestedAtom);
    const currentPlaybackTime = useAtomValue(currentPlaybackTimeAtom);
    const videoClipStartTimestamp = useAtomValue(getVideoClipStartTimestampAtom);
    const canPlay = useAtomValue(videoCanPlayAtom);
    const skipBack = () => skip(SkipDirection.BACK);
    const atStartOfPlayback = currentPlaybackTime === 0 ||
        currentPlaybackTime === videoClipStartTimestamp;
    const isDisabled = (!videoIsPlaying && atStartOfPlayback && !canSkip.back) || !canPlay;
    const clipRecentlyStarted = useAtomValue(getVideoClipRecentlyStartedAtom);
    const onBack = () => {
        if (canSkip.back && clipRecentlyStarted) {
            skipBack();
        }
        else {
            toggleResetRequested(true);
        }
    };
    const BACK_BUTTON_TITLE = useTranslation('back');
    return (jsx(ButtonIcon, { disabled: isDisabled, icon: "First", onClick: onBack, title: BACK_BUTTON_TITLE, variant: PLAYER_BUTTON_VARIANT }));
}

function FullscreenButton({ size = 'regular' }) {
    const canPlay = useAtomValue(videoCanPlayAtom);
    const [fullscreenRequested, setFullscreenRequested] = useAtom(fullscreenRequestedAtom);
    const toggleFullscreenMode = () => setFullscreenRequested(!fullscreenRequested);
    const buttonIcon = fullscreenRequested
        ? 'CloseFullscreen'
        : 'EnterFullscreen';
    const { exitFullscreen, viewFullscreen } = useTranslations([
        'viewFullscreen',
        'exitFullscreen',
    ]);
    const buttonLabel = fullscreenRequested ? exitFullscreen : viewFullscreen;
    return (jsx(ButtonIcon, { onClick: toggleFullscreenMode, variant: PLAYER_BUTTON_VARIANT, title: buttonLabel, icon: buttonIcon, disabled: !canPlay && !fullscreenRequested, size: size }));
}

var PlayerHighlightType;
(function (PlayerHighlightType) {
    PlayerHighlightType["PLAY"] = "PLAY";
})(PlayerHighlightType || (PlayerHighlightType = {}));
var OverlayType;
(function (OverlayType) {
    OverlayType["PLAYER_HIGHLIGHTS"] = "PLAYER_HIGHLIGHTS";
})(OverlayType || (OverlayType = {}));

const byActive$3 = ({ active }) => active;
const byPlayerHighlightingOverlay = (overlay) => overlay.type === OverlayType.PLAYER_HIGHLIGHTS;
const getPlayerHighlightingOverlay = (playlistEntry) => {
    var _a, _b, _c;
    return ((_c = (_b = (_a = playlistEntry === null || playlistEntry === void 0 ? void 0 : playlistEntry.videoClipReference) === null || _a === void 0 ? void 0 : _a.overlays) === null || _b === void 0 ? void 0 : _b.find(byPlayerHighlightingOverlay)) === null || _c === void 0 ? void 0 : _c.data) || null;
};
const playerHighlightingOverlayAtom = atom((get) => {
    const currentlySelected = get(selectedPlaylistEntryAtom);
    if (!currentlySelected)
        return null;
    return getPlayerHighlightingOverlay(currentlySelected);
});
playerHighlightingOverlayAtom.debugLabel =
    'video.client.playerHighlightingOverlay';
const showPlayerHighlightingAtom = atom((get) => {
    var _a;
    return !!(get(highlightPlayerPositionsAtom) &&
        ((_a = get(playerHighlightingOverlayAtom)) === null || _a === void 0 ? void 0 : _a.players.find(byActive$3)));
});
showPlayerHighlightingAtom.debugLabel = 'video.client.showPlayerHighlighting';
const updatePlayersVisibilityAtom = atom(null, (get, set, updatedPlayers) => {
    const playerHighlightingData = get(playerHighlightingOverlayAtom);
    if (!playerHighlightingData || !(updatedPlayers === null || updatedPlayers === void 0 ? void 0 : updatedPlayers.length))
        return;
    // Casting and using ! is safe here as we can't get this far if there is no playerHighlightingData
    const selectedEntryData = get(selectedPlaylistEntryAtom);
    const overlays = selectedEntryData.videoClipReference.overlays || [];
    const overlayIndex = overlays.findIndex(byPlayerHighlightingOverlay);
    const updatedEntry = {
        ...selectedEntryData,
        videoClipReference: {
            ...selectedEntryData.videoClipReference,
            overlays: selectedEntryData.videoClipReference.overlays.map((overlay, index) => {
                if (overlayIndex !== index)
                    return overlay;
                return {
                    ...overlay,
                    data: {
                        ...overlay.data,
                        players: updatedPlayers,
                    },
                };
            }),
        },
    };
    set(updatePlaylistEntryAtom, updatedEntry);
});
updatePlayersVisibilityAtom.debugLabel = 'video.client.togglePlayersVisibility';

/**
 * This component will pass the response of the API call down to its children.
 * This prevents issues calling the hook conditionally, for example if some props are missing, you might not want to
 * the API unnecessarily.
 *
 * @constructor
 */
function ChildrenWithPlayerTracksData({ id, children }) {
    const response = usePlayerTracksData(id);
    return children(response);
}

function HighlightPlayersButton({ size = 'small' }) {
    const [highlightPlayers, setHighlightPlayers] = useAtom(highlightPlayerPositionsAtom);
    const playerHighlightingData = useAtomValue(playerHighlightingOverlayAtom);
    const { highlightPlayersOff, highlightPlayersFull, noPlayerHighlightingAvailable, } = useTranslations([
        'highlightPlayersOff',
        'highlightPlayersFull',
        'noPlayerHighlightingAvailable',
    ]);
    if (!(playerHighlightingData === null || playerHighlightingData === void 0 ? void 0 : playerHighlightingData.id))
        return null;
    const LABELS = {
        [HighlightPlayers.VISIBLE]: highlightPlayersFull,
        [HighlightPlayers.HIDDEN]: highlightPlayersOff,
    };
    return (jsx(ChildrenWithPlayerTracksData, { id: playerHighlightingData.id, children: ({ data, error }) => {
            const isDisabled = !!(error || !data);
            const buttonLabel = isDisabled
                ? noPlayerHighlightingAvailable
                : LABELS[highlightPlayers];
            return (jsx(ButtonIcon, { disabled: isDisabled, icon: "PlayerHighlight", off: highlightPlayers === HighlightPlayers.HIDDEN, onClick: () => setHighlightPlayers(highlightPlayers === HighlightPlayers.HIDDEN
                    ? HighlightPlayers.VISIBLE
                    : HighlightPlayers.HIDDEN), size: size, title: buttonLabel, variant: "ghost" }));
        } }));
}

function NextButton() {
    var _a, _b;
    const [canSkip, skip] = useAtom(skipAtom);
    const canPlay = useAtomValue(videoCanPlayAtom);
    const currentlyPlaying = useAtomValue(selectedPlaylistEntryAtom);
    const videoClipEnded = useAtomValue(videoClipEndedAtom);
    const setPlaybackTimeRequested = useSetAtom(playbackTimeRequestedAtom);
    const setPlayRequested = useSetAtom(playRequestedAtom);
    const currentPlaybackItemDuration = useAtomValue(currentPlaybackItemDurationAtom);
    const clipEnd = currentlyPlaying &&
        'videoClipReference' in currentlyPlaying &&
        ((_a = currentlyPlaying.videoClipReference) === null || _a === void 0 ? void 0 : _a.endTimestamp)
        ? (_b = currentlyPlaying.videoClipReference) === null || _b === void 0 ? void 0 : _b.endTimestamp
        : currentPlaybackItemDuration;
    const onNext = () => {
        if (!canSkip.forward) {
            setPlayRequested(false);
            setPlaybackTimeRequested(clipEnd);
        }
        else {
            // Paused to prevent a race condition when handleRequestVideoFrameCallback runs after the clip ends
            setPlayRequested(false);
            skip(SkipDirection.FORWARD);
        }
    };
    const NEXT = useTranslation('next');
    return (jsx(ButtonIcon, { disabled: videoClipEnded || !canPlay, icon: "Last", onClick: onNext, title: NEXT, variant: PLAYER_BUTTON_VARIANT }));
}

const MobilePlayButtonWrapper = styled.div `
  top: calc((100% + ${getControlsHeightOffset}) / 2);
  left: 50%;
  transform: translate(-50%, -50%);
  position: absolute;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
`;

function PlayPauseButton({ isDisabled = false, handleClick, variant = 'primary', }) {
    const videoIsPlaying = useAtomValue(videoIsPlayingAtom);
    const clipEnded = useAtomValue(videoClipEndedAtom);
    return (jsx(ButtonIcon, { variant: variant, disabled: isDisabled, icon: !videoIsPlaying || clipEnded ? 'Play' : 'Pause', onClick: handleClick, title: !videoIsPlaying ? 'Play' : 'Pause', size: "large" }));
}

const mobilePlayButtonNoOpClick = () => { };
/**
 * Renders the mobile overlay buttons for the video player in a ThemeProvider to
 * ensure that the buttons are always displayed in dark mode.
 *
 * !Important!:
 * As per the designs, the mobile overlay buttons are always displayed in dark mode, regardless of the current theme.
 * Therefore, the buttons have been wrapped in a ThemeProvider in this component to ensure that this is always the case.
 *
 * @param {MobileOverlayButtonsProps} props - The component props.
 * @returns {JSX.Element} The rendered component.
 */
function MobileOverlayButtons({ isDisabled, showControls, renderControlsBelowVideo, videoIsPlaying, }) {
    return (jsx(ThemeProvider, { theme: videoTheme.dark, applyGlobalStyles: false, children: jsxs(MobilePlayButtonWrapper, { "$showControlsBelowVideo": renderControlsBelowVideo, "$showMobileControls": true, "$displayControls": !videoIsPlaying || showControls, children: [jsx(BackButton, {}), jsx(PlayPauseButton, { isDisabled: isDisabled, handleClick: mobilePlayButtonNoOpClick, variant: "secondary" }), jsx(NextButton, {})] }) }));
}

const allowedPlaybackRates = [0.5, 1, 1.5, 2];

function ChangePlaybackRateButton() {
    const canPlay = useAtomValue(videoCanPlayAtom);
    const [currentPlaybackRate, setPlaybackRate] = useAtom(playbackRateRequestedAtom);
    const playbackRateLabel = useTranslation('playbackRateLabel');
    const isSelected = (playbackRate) => currentPlaybackRate === playbackRate;
    return (jsxs(MenuItem, { size: "large", children: [jsx(MenuItemText, { children: playbackRateLabel }), jsx(ButtonGroup, { children: allowedPlaybackRates.map((playbackRate) => (jsxs(Button, { isDisabled: !canPlay, onClick: () => setPlaybackRate(playbackRate), size: "small", variant: isSelected(playbackRate) ? 'primary' : PLAYER_BUTTON_VARIANT, children: ["x", playbackRate] }, playbackRate))) })] }));
}

const byActive$2 = ({ active }) => active;
const byNumber = (a, b) => a.number - b.number;
function HighlightPlayersSelectionForm({ players, onCancel, onChange, onSubmit, onTabChange, open = false, teams = [], total = 0, }) {
    var _a;
    const translations = useTranslations([
        'apply',
        'cancel',
        'highlightPlayersSelectionTitle',
    ]);
    const displayTabs = !!teams.length && typeof onTabChange === 'function';
    const sortedPlayers = players.sort(byNumber);
    return (jsxs(Modal, { id: "highlight-players-selection", onCancel: onCancel, isOpen: open, children: [jsxs(ModalHeader, { id: "highlight-players-selection", children: [translations.highlightPlayersSelectionTitle, " \u00A0", jsxs(Typography, { variant: "bodyRegular", as: "span", children: ["(", total, ")"] })] }), jsxs("form", { onSubmit: onSubmit, children: [displayTabs && (jsx(Tabs, { activeTab: (_a = teams.filter(byActive$2)[0]) === null || _a === void 0 ? void 0 : _a.name, onTabChange: (e, tab) => onTabChange(e, tab.value), tabs: teams.map(({ name }) => ({
                            label: name,
                            value: name,
                        })) })), jsx(ModalBody, { children: jsx(ExperimentalFormGroup, { children: sortedPlayers.map(({ active, id, number, name }) => {
                                const checkboxId = `highlight-player-checkbox-${id}`;
                                return (jsx(Checkbox, { checked: active, id: checkboxId, label: `${number}. ${name}`, onChange: (e) => onChange(e, id) }, checkboxId));
                            }) }) }), jsxs(ModalFooter, { children: [jsx(Button, { onClick: onCancel, type: "reset", variant: "ghost", children: translations.cancel }), jsx(Button, { type: "submit", children: translations.apply })] })] })] }));
}

const getAllTeams = (players) => {
    const teams = new Set();
    players.forEach(({ team }) => {
        if (team)
            teams.add(team);
    });
    return [...teams].map((teamName, index) => ({
        active: index === 0,
        name: teamName,
    }));
};
const byActive$1 = (team) => !!team.active;
/**
 * Used to filter through all the players to find all the players associated by a given team name
 * In cases where the team name is not defined return the item anyway - this lets us show all players if the consumer is
 * not using team filtering.
 */
const byTeam = (teamName) => (player) => {
    if (!teamName)
        return true;
    return player.team === teamName;
};
function HighlightPlayersSelectionMenuItem() {
    var _a;
    const [isOpen, setIsOpen] = useAtom(showHighlightPlayersFormAtom);
    const setShowMoreOptions = useSetAtom(showMoreOptionsAtom);
    const playerHighlightingData = useAtomValue(playerHighlightingOverlayAtom);
    const updatePlayersVisibility = useSetAtom(updatePlayersVisibilityAtom);
    const setPauseOnClipEnd = useSetAtom(pauseOnClipEndAtom);
    const resetToStart = useSetAtom(resetRequestedAtom);
    const [players, setPlayers] = useState([]);
    const [teams, setTeams] = useState([]);
    const translations = useTranslations(['highlightPlayersSelectionTitle']);
    useEffect(() => {
        var _a;
        if ((_a = playerHighlightingData === null || playerHighlightingData === void 0 ? void 0 : playerHighlightingData.players) === null || _a === void 0 ? void 0 : _a.length) {
            setPlayers(playerHighlightingData.players);
            setTeams(getAllTeams(playerHighlightingData.players));
        }
    }, [playerHighlightingData]);
    // If we have no player highlighting data for the current clip then display nothing.
    if (!playerHighlightingData)
        return null;
    const handleMenuItemClick = () => {
        setIsOpen(true);
        setPauseOnClipEnd(true);
    };
    const handleConfirm = (e) => {
        e.preventDefault(); // Prevent form from refreshing the page
        updatePlayersVisibility(players);
        setIsOpen(false);
        setShowMoreOptions(false);
        resetToStart(true);
    };
    const handleCancel = () => {
        setIsOpen(false);
    };
    const handleChange = (_, playerId) => {
        const newPlayers = [...players];
        const index = players.findIndex(({ id }) => id === playerId);
        const player = newPlayers[index];
        newPlayers[index] = {
            ...player,
            active: !player.active,
        };
        setPlayers(newPlayers);
    };
    const handleTabChange = (_, teamName) => {
        const newTeams = teams.map(({ name }) => ({
            active: teamName === name,
            name,
        }));
        // Only update the state after the event loop has finished.
        // This will prevent the Modal from accidentally closing.
        // Without it, the component will rerender before the Flyout checks if the click originated from "outside" the Modal
        // and therefore consider the Tab "outside" because it will not find it in the Modal's markup.
        setTimeout(() => setTeams(newTeams), 0);
    };
    const hasPlayers = !!players.length;
    const playersByActiveTeam = players.filter(byTeam((_a = teams.find(byActive$1)) === null || _a === void 0 ? void 0 : _a.name));
    return (jsxs(Fragment, { children: [jsx(MenuItem, { disabled: !hasPlayers, onClick: handleMenuItemClick, children: jsx(MenuItemText, { children: translations.highlightPlayersSelectionTitle }) }), hasPlayers && (jsx(HighlightPlayersSelectionForm, { players: playersByActiveTeam, onCancel: handleCancel, onChange: handleChange, onSubmit: handleConfirm, onTabChange: handleTabChange, open: isOpen, teams: teams, total: players.filter(byActive$1).length }))] }));
}

const CONTROLS = [
    Controls.HighlightPlayersButton,
    Controls.ChangePlaybackRateButton,
];
function MoreOptions({ size = 'small', videoControls = [], }) {
    const [isOpen, setIsOpen] = useAtom(showMoreOptionsAtom);
    const { moreOptionsLabel } = useTranslations(['moreOptionsLabel']);
    const canPlay = useAtomValue(videoCanPlayAtom);
    const isEnabled = (control) => videoControls.includes(control);
    if (!CONTROLS.some(isEnabled)) {
        return null;
    }
    return (jsxs(ButtonWithMenu, { disableScrollWhenOpen: true, displayBackdrop: true, disabled: !canPlay, icon: "More", isOpen: isOpen, onClick: () => setIsOpen(true), onOutsideClick: () => setIsOpen(false), placement: "bottom-end", size: size, testId: "more-options", title: moreOptionsLabel, variant: PLAYER_BUTTON_VARIANT, children: [isEnabled(Controls.HighlightPlayersButton) && (jsx(HighlightPlayersSelectionMenuItem, {})), isEnabled(Controls.ChangePlaybackRateButton) && (jsx(ChangePlaybackRateButton, {}))] }));
}

function MuteButton({ size = 'regular' }) {
    const canPlay = useAtomValue(videoCanPlayAtom);
    const [isMuted, setMute] = useAtom(mutedRequestedAtom);
    const onMute = () => setMute(!isMuted);
    const { unmute: UNMUTE, mute: MUTE } = useTranslations(['unmute', 'mute']);
    return (jsx(ButtonIcon, { disabled: !canPlay, icon: "Speaker", off: isMuted, onClick: onMute, size: size, title: isMuted ? UNMUTE : MUTE, variant: PLAYER_BUTTON_VARIANT }));
}

const Time = styled.span `
  flex-grow: 1;
  font-variant-numeric: tabular-nums;
  overflow: hidden;
  text-overflow: ellipsis;

  time {
    white-space: nowrap;
  }
`;

function useTimelineDetails() {
    const currentPlaybackItemDuration = useAtomValue(currentPlaybackItemDurationAtom);
    const currentPlaybackTime = useAtomValue(currentPlaybackTimeAtom);
    const startTimestamp = useAtomValue(getVideoClipStartTimestampAtom);
    const endTimestamp = useAtomValue(getVideoClipEndTimestampAtom) ||
        startTimestamp + currentPlaybackItemDuration;
    const duration = endTimestamp - startTimestamp;
    const positionInClip = endTimestamp
        ? currentPlaybackTime - startTimestamp
        : currentPlaybackTime;
    return {
        duration,
        endTimestamp,
        position: currentPlaybackTime,
        positionInClip,
        startTimestamp,
    };
}

function PlayerTimeDisplay() {
    const { duration, positionInClip } = useTimelineDetails();
    const positionFormatted = convertMircosecondsToDisplayTimestamp((positionInClip > 0 && positionInClip) || 0);
    const durationFormatted = duration && Number.isFinite(duration)
        ? convertMircosecondsToDisplayTimestamp((duration > 0 && duration) || 0)
        : '--';
    const position = positionInClip > duration ? durationFormatted : positionFormatted;
    return (jsx(Time, { children: jsx(Typography, { as: "time", variant: "bodyRegular", children: `${position} / ${durationFormatted}` }) }));
}

const extendCurrentlySelectedEntryStartTimeAtom = atom(undefined, (get, set, duration) => {
    var _a;
    const playlistEntries = get(playlistEntriesAtom);
    const currentlyPlayingIndex = playlistEntries.findIndex(bySelected);
    const currentEntry = playlistEntries[currentlyPlayingIndex];
    if (currentEntry &&
        'videoClipReference' in currentEntry &&
        ((_a = currentEntry.videoClipReference) === null || _a === void 0 ? void 0 : _a.startTimestamp)) {
        const newStartTime = currentEntry.videoClipReference.startTimestamp + duration;
        currentEntry.videoClipReference.startTimestamp =
            newStartTime > 0 ? newStartTime : 0;
        set(playlistEntriesAtom, playlistEntries);
    }
});
extendCurrentlySelectedEntryStartTimeAtom.debugLabel =
    'video-client.playlist-entries.extend-currently-selected-entry-start-timestamp';
const extendCurrentlySelectedEntryEndTimeAtom = atom(undefined, (get, set, duration) => {
    var _a;
    const playlistEntries = get(playlistEntriesAtom);
    const currentlyPlayingIndex = playlistEntries.findIndex(bySelected);
    const currentEntry = playlistEntries[currentlyPlayingIndex];
    if (currentEntry &&
        'videoClipReference' in currentEntry &&
        ((_a = currentEntry.videoClipReference) === null || _a === void 0 ? void 0 : _a.endTimestamp)) {
        const newEndTime = currentEntry.videoClipReference.endTimestamp + duration;
        currentEntry.videoClipReference.endTimestamp =
            newEndTime > get(currentPlaybackItemDurationAtom)
                ? get(currentPlaybackItemDurationAtom)
                : newEndTime;
        set(playlistEntriesAtom, playlistEntries);
    }
});
extendCurrentlySelectedEntryEndTimeAtom.debugLabel =
    'video-client.playlist-entries.extend-currently-selected-entry-end-timestamp';
const setCurrentlySelectedEntryStartTimeToCurrentPlaybackTimeAtom = atom(undefined, (get, set) => {
    const playlistEntries = get(playlistEntriesAtom);
    const currentlyPlayingIndex = playlistEntries.findIndex(bySelected);
    const currentEntry = playlistEntries[currentlyPlayingIndex];
    if (currentEntry &&
        'videoClipReference' in currentEntry &&
        currentEntry.videoClipReference) {
        currentEntry.videoClipReference.startTimestamp = get(currentPlaybackTimeAtom);
        set(playlistEntriesAtom, playlistEntries);
    }
});
setCurrentlySelectedEntryStartTimeToCurrentPlaybackTimeAtom.debugLabel =
    'video-client.playlist-entries.set-currently-selected-entry-start-time-to-current-playback-time';
const setCurrentlySelectedEntryEndTimeToCurrentPlaybackTimeAtom = atom(undefined, (get, set) => {
    var _a;
    const playlistEntries = get(playlistEntriesAtom);
    const currentlyPlayingIndex = playlistEntries.findIndex(bySelected);
    const currentEntry = playlistEntries[currentlyPlayingIndex];
    if (currentEntry &&
        'videoClipReference' in currentEntry &&
        ((_a = currentEntry.videoClipReference) === null || _a === void 0 ? void 0 : _a.endTimestamp)) {
        currentEntry.videoClipReference.endTimestamp = get(currentPlaybackTimeAtom);
        set(playlistEntriesAtom, playlistEntries);
    }
});
setCurrentlySelectedEntryEndTimeToCurrentPlaybackTimeAtom.debugLabel =
    'video-client.playlist-entries.set-currently-selected-entry-end-time-to-current-playback-time';

const messagePlaylistActionsAtom = atom(null);
messagePlaylistActionsAtom.debugLabel = 'video.client.messagePlaylistActions';
const messageMyPlaylistsActionsAtom = atom(null);
messageMyPlaylistsActionsAtom.debugLabel =
    'video.client.messageMyPlaylistsActions';
const messageMyCutupsActionsAtom = atom(null);
messageMyCutupsActionsAtom.debugLabel = 'video.client.messageMyCutupsActions';

const serialisePlaylistEntry = (entry) => {
    var _a;
    return convertKeys({
        ...entry.videoClipReference,
        description: 'description' in entry && entry.description,
        description_line_three: ('descriptionLineThree' in entry && entry.descriptionLineThree) ||
            undefined,
        description_line_two: ('descriptionLineTwo' in entry && entry.descriptionLineTwo) ||
            undefined,
        end_timestamp: 'videoClipReference' in entry && ((_a = entry.videoClipReference) === null || _a === void 0 ? void 0 : _a.endTimestamp),
        id: 'id' in entry && entry.id,
    }, Case.Snake);
};
const serialisePlaylistEntries = (playlistEntries) => playlistEntries.map((entry) => serialisePlaylistEntry(entry));
const sortPlaylists = (playlists) => playlists.sort((a, b) => new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime());

// A private atom to store myPlaylists
const myPlaylistsPrimitiveAtom = atom([]);
myPlaylistsPrimitiveAtom.debugLabel = 'video.client.myPlaylistsPrimitive';
// Dervied atom to allow a single atom to handle retrieval and setting (with sorting) of myPlaylists
const myPlaylistsAtom = atom((get) => get(myPlaylistsPrimitiveAtom), (_, set, data) => set(myPlaylistsPrimitiveAtom, sortPlaylists(data)));
myPlaylistsAtom.debugLabel = 'video.client.myPlaylists';
const selectedPlaylistsAtom = atom([]);
selectedPlaylistsAtom.debugLabel = 'video.client.selectedPlaylists';
const toggleSelectedPlaylistAtom = atom((get) => get(selectedPlaylistsAtom), (get, set, selection) => {
    const currentSelection = get(selectedPlaylistsAtom);
    const selectedIndex = currentSelection
        .map((selectedList) => selectedList.id)
        .indexOf(selection.id);
    set(selectedPlaylistsAtom, selectedIndex !== -1
        ? currentSelection.filter((playlist) => selection.id !== playlist.id)
        : [...currentSelection, selection]);
});
const myPlaylistsNamesAtom = atom([]);
myPlaylistsNamesAtom.debugLabel = 'video.client.myPlaylistsNames';
const selectedMyPlaylistIdAtom = atom(null);
selectedMyPlaylistIdAtom.debugLabel = 'video.client.selectedMyPlaylistId';
const selectedMyPlaylistAtom = atom((get) => get(myPlaylistsAtom).find((playlist) => playlist.id === get(selectedMyPlaylistIdAtom)));
selectedMyPlaylistAtom.debugLabel = 'video.client.selectedMyPlaylist';

const playlistNameAtom = atom('');
playlistNameAtom.debugLabel = 'video.client.playlistName';
const cutupNameAtom = atom('');
cutupNameAtom.debugLabel = 'video.client.cutupNameAtom';
const displaySavePlaylistConfirmationAtom = atom(false);
displaySavePlaylistConfirmationAtom.debugLabel =
    'video.client.displaySavePlaylistConfirmation';
const displaySaveSingleEntryToPlaylistConfirmationAtom = atom(false);
displaySaveSingleEntryToPlaylistConfirmationAtom.debugLabel =
    'video.client.displaySaveSingleEntryToPlaylistConfirmation';
const displayDeletePlaylistConfirmationAtom = atom(false);
displayDeletePlaylistConfirmationAtom.debugLabel =
    'video.client.displayDeletePlaylistConfirmation';
const displayDeleteFromPlaylistConfirmationAtom = atom(false);
displayDeleteFromPlaylistConfirmationAtom.debugLabel =
    'video.client.displayDeleteFromPlaylistConfirmation';
const displayCreateCutupConfirmationAtom = atom(false);
displayCreateCutupConfirmationAtom.debugLabel =
    'video.client.displayCreateCutupConfirmation';
const playlistContextMenuOpenAtom = atom(null);
playlistContextMenuOpenAtom.debugLabel =
    'video.client.playlistIdContextMenuOpen';
const playlistBeingRenamedAtom = atom(null);
playlistBeingRenamedAtom.debugLabel = 'video.client.playlistBeingRenamed';

const MESSAGE_TYPES = {
    DEFAULT: 'info',
    ERROR: 'error',
    INFO: 'info',
    SUCCESS: 'success',
    WARNING: 'warning',
};

function formatTemplatedMessage(template, variables) {
    return template.replace(/\{{(.*?)}}/g, (_, variable) => variables[variable.trim()]);
}

/**
 * API helper function to allow calling the video service API
 */
function useApi() {
    const baseUrlVideoService = useAtomValue(baseUrlVideoServiceAtom);
    const authState = useKitbagAuth();
    const call = async (endpoint, options = {}) => {
        let response;
        let data;
        let error = null;
        const url = new URL(`${baseUrlVideoService}/${endpoint}`);
        if (options.params) {
            url.search = new URLSearchParams(options.params).toString();
        }
        const authToken = await authState.getAccessTokenSilently();
        const headers = {
            Authorization: `Bearer ${authToken}`,
        };
        try {
            response = await fetch(url, {
                ...options,
                headers,
            });
            data = convertKeys(await response.json());
        }
        catch (err) {
            error = err.message;
        }
        return { data, error };
    };
    const createAlias = (method) => (endpoint, options) => call(endpoint, {
        ...options,
        method,
    });
    const api = {
        delete: createAlias('DELETE'),
        get: createAlias('GET'),
        head: createAlias('HEAD'),
        options: createAlias('OPTIONS'),
        patch: createAlias('PATCH'),
        post: createAlias('POST'),
        put: createAlias('PUT'),
    };
    return { ...api };
}

/**
 * Type guard for determining if an Endpoint exists
 * `any` type is valid for the type guard.
 *
 * @param {*} value
 * @returns {boolean}
 */
const isEndpoint = (value) => Object.values(Endpoint).includes(value);
/**
 * Hook that serves as a wrapper for the useSWRConfig hook but also provides a function to invalidate the cache for
 * Video Service endpoints
 */
function useSWRConfig() {
    const { mutate, ...rest } = useSWRConfig$1();
    const apiUrl = useAtomValue(baseUrlVideoServiceAtom);
    const invalidateCacheForApi = (endpointOrId) => {
        const swrKey = (key) => key.includes(isEndpoint(endpointOrId) ? `${apiUrl}/${endpointOrId}` : endpointOrId);
        return mutate(swrKey);
    };
    return {
        ...rest,
        invalidateCacheForApi,
        mutate,
    };
}

function useCacheActions() {
    const { invalidateCacheForApi } = useSWRConfig();
    const removePlaylistEntriesServerResponse = useSetAtom(removePlaylistEntriesServerResponseAtom);
    const invalidateCacheForSelectedPlaylistEntry = async (id) => {
        await invalidateCacheForApi(id);
        removePlaylistEntriesServerResponse(id);
    };
    return { invalidateCacheForSelectedPlaylistEntry };
}

function useMyPlaylists$1() {
    const api = useApi();
    const { invalidateCacheForApi } = useSWRConfig();
    const { invalidateCacheForSelectedPlaylistEntry } = useCacheActions();
    const { successfullyDeletedPlaylist, errorDeletingPlaylist } = useTranslations(['successfullyDeletedPlaylist', 'errorDeletingPlaylist']);
    const myPlaylists = useAtomValue(myPlaylistsAtom);
    const setPlaylistActionsMessage = useSetAtom(messagePlaylistActionsAtom);
    const setMyPlaylistActionsMessage = useSetAtom(messageMyPlaylistsActionsAtom);
    const selectedMyPlaylist = useAtomValue(selectedMyPlaylistAtom);
    const playlistName = useAtomValue(playlistNameAtom);
    const selectedMyPlaylistId = useAtomValue(selectedMyPlaylistIdAtom);
    const selectedPlaylistEntriesVideoClipReferences = useAtomValue(selectedPlaylistEntriesVideoClipReferencesAtom);
    const selectedPlaylists = useAtomValue(selectedPlaylistsAtom);
    const [myPlaylistsNames, setMyPlaylistsNames] = useAtom(myPlaylistsNamesAtom);
    const playlistEntries = useCallbackSafeAtomValue(playlistEntriesAtom);
    const currentlySelected = useCallbackSafeAtomValue(selectedPlaylistEntryAtom);
    // we ideally want to use the myPlaylist array as the primary source to check against,
    // so when that has been updated, we want to set our myPlaylistNames against the myPlaylist data.
    useEffect(() => {
        setMyPlaylistsNames(myPlaylists.map((p) => p.name));
    }, [myPlaylists]);
    const { errorAddingToPlaylist, errorRenamingPlaylist, errorRemovingPlaylist, errorSavingCurrentPlaylist, errorSavingPlaylistEntryUpdate, successfullyAddedToPlaylist, successfullyCreatedPlaylist, successfullyDeletedFromPlaylist, } = useTranslations([
        'errorAddingToPlaylist',
        'errorRemovingPlaylist',
        'errorRenamingPlaylist',
        'errorSavingCurrentPlaylist',
        'errorSavingPlaylistEntryUpdate',
        'successfullyAddedToPlaylist',
        'successfullyCreatedPlaylist',
        'successfullyDeletedFromPlaylist',
    ]);
    const displaySuccessMessage = (successMessage) => setPlaylistActionsMessage({
        description: successMessage,
        type: MESSAGE_TYPES.SUCCESS,
    });
    const displayErrorMessage = (errorMessage) => setPlaylistActionsMessage({
        description: errorMessage,
        type: MESSAGE_TYPES.ERROR,
    });
    const patchPlaylist = async (id, data, successMessage, errorMessage) => {
        const response = await api.patch(`${Endpoint.MY_PLAYLISTS}/${id}`, {
            body: JSON.stringify(data),
        });
        if (response.data) {
            if (successMessage) {
                displaySuccessMessage(successMessage);
            }
        }
        else if (errorMessage) {
            displayErrorMessage(errorMessage);
        }
        return response;
    };
    const deleteMyPlaylist = async (id) => {
        const playlistToDelete = myPlaylists.find((playlist) => playlist.id === id);
        const response = await api.delete(Endpoint.MY_PLAYLISTS, {
            params: { playlist_id: playlistToDelete === null || playlistToDelete === void 0 ? void 0 : playlistToDelete.id },
        });
        if (response.data) {
            await invalidateCacheForApi(Endpoint.MY_PLAYLISTS);
            setMyPlaylistActionsMessage({
                description: formatTemplatedMessage(successfullyDeletedPlaylist, {
                    playlistName: playlistToDelete === null || playlistToDelete === void 0 ? void 0 : playlistToDelete.name,
                }),
                type: MESSAGE_TYPES.SUCCESS,
            });
        }
        else {
            displayErrorMessage(formatTemplatedMessage(errorDeletingPlaylist, {
                playlistName: playlistToDelete === null || playlistToDelete === void 0 ? void 0 : playlistToDelete.name,
            }));
        }
    };
    const deleteSelectedPlaylistEntriesFromSelectedPlaylist = async (onSuccess) => {
        const errorSavingPlaylistNames = [];
        if (selectedMyPlaylistId) {
            const response = await patchPlaylist(selectedMyPlaylistId, {
                video_clip_references_to_delete: serialisePlaylistEntries(selectedPlaylistEntriesVideoClipReferences),
            }, formatTemplatedMessage(successfullyDeletedFromPlaylist, {
                playlistName: selectedMyPlaylist === null || selectedMyPlaylist === void 0 ? void 0 : selectedMyPlaylist.name,
            }), formatTemplatedMessage(errorRemovingPlaylist, {
                playlistName: selectedMyPlaylist === null || selectedMyPlaylist === void 0 ? void 0 : selectedMyPlaylist.name,
            }));
            if (response === null || response === void 0 ? void 0 : response.data) {
                await invalidateCacheForApi(Endpoint.MY_PLAYLISTS);
                onSuccess();
            }
            else if (selectedMyPlaylist) {
                errorSavingPlaylistNames.push(selectedMyPlaylist.name);
            }
        }
    };
    const dedupePlaylistName = () => {
        const duplicatePlaylistNamePattern = new RegExp(`^${playlistName} \\(\\d+\\)$|^${playlistName}$`);
        const existingPlaylists = myPlaylistsNames.filter((myPlaylist) => duplicatePlaylistNamePattern.test(myPlaylist));
        if (existingPlaylists.length > 0) {
            return `${playlistName} (${existingPlaylists.length})`;
        }
        return playlistName;
    };
    const saveToNewPlaylist = async (playlistEntriesToSave, onSuccess) => {
        const savePlaylistName = dedupePlaylistName();
        const data = JSON.stringify({
            playlist: {
                name: savePlaylistName,
                video_clip_references: serialisePlaylistEntries(playlistEntriesToSave),
            },
        });
        const response = await api.post(Endpoint.MY_PLAYLISTS, {
            body: data,
        });
        if (response.data) {
            await invalidateCacheForApi(Endpoint.MY_PLAYLISTS);
            displaySuccessMessage(successfullyCreatedPlaylist);
            // add item to playlistNames so it is there to check for dedupes immediately.
            setMyPlaylistsNames((prevPlaylistNames) => [
                ...prevPlaylistNames,
                savePlaylistName,
            ]);
            if (onSuccess) {
                onSuccess();
            }
        }
        else {
            displayErrorMessage(errorAddingToPlaylist);
        }
    };
    const renamePlaylistEntry = async (playlistId, name, onSuccess) => {
        if (playlistId && name) {
            const response = await patchPlaylist(playlistId, {
                name,
            }, undefined, errorRenamingPlaylist);
            if (response.data) {
                await invalidateCacheForApi(Endpoint.MY_PLAYLISTS);
                onSuccess();
            }
        }
    };
    const updateAllPlaylistEntriesInCurrentlySelectedPlaylist = async () => {
        if (selectedMyPlaylistId) {
            await patchPlaylist(selectedMyPlaylistId, {
                video_clip_references_to_update: serialisePlaylistEntries(playlistEntries()),
            }, undefined, errorSavingCurrentPlaylist);
            await invalidateCacheForApi(Endpoint.MY_PLAYLISTS);
        }
    };
    const saveModifiedPlaylistEntryInCurrentlySelectedPlaylist = async () => {
        const updatedPlaylistEntry = playlistEntries().find((entry) => { var _a; return entry.id === ((_a = currentlySelected()) === null || _a === void 0 ? void 0 : _a.id); });
        if (selectedMyPlaylistId && updatedPlaylistEntry) {
            const { data } = await patchPlaylist(selectedMyPlaylistId, {
                video_clip_references_to_update: [
                    serialisePlaylistEntry(updatedPlaylistEntry),
                ],
            }, undefined, errorSavingPlaylistEntryUpdate);
            if (data) {
                await invalidateCacheForSelectedPlaylistEntry(currentlySelected().id);
            }
        }
    };
    const saveToSelectedPlaylists = async (playlistEntriesToSave, onSuccess) => {
        const successfullySavedToPlaylistNames = [];
        const errorSavingPlaylistNames = [];
        await Promise.all(selectedPlaylists.map(async (selectedPlaylist) => {
            const response = await patchPlaylist(selectedPlaylist.id, {
                video_clip_references: serialisePlaylistEntries(playlistEntriesToSave),
            });
            if (response.data) {
                // clear the list of saved playlists
                setMyPlaylistsNames([]);
                // add to successful playlists. currently not used, but will be used when adding icons to
                // the playlists as success / failure indicators
                successfullySavedToPlaylistNames.push(selectedPlaylist.name);
                if (onSuccess) {
                    onSuccess();
                }
            }
            else {
                // add to error playlists
                errorSavingPlaylistNames.push(selectedPlaylist.name);
            }
            return response;
        }));
        await invalidateCacheForApi(Endpoint.MY_PLAYLISTS);
        if (errorSavingPlaylistNames.length > 0) {
            displayErrorMessage(errorAddingToPlaylist);
        }
        else {
            displaySuccessMessage(successfullyAddedToPlaylist);
        }
    };
    return {
        deleteMyPlaylist,
        deleteSelectedPlaylistEntriesFromSelectedPlaylist,
        myPlaylists,
        renamePlaylistEntry,
        saveModifiedPlaylistEntryInCurrentlySelectedPlaylist,
        saveToNewPlaylist,
        saveToSelectedPlaylists,
        updateAllPlaylistEntriesInCurrentlySelectedPlaylist,
    };
}

const getDisabledTitle$3 = (clipIsPlaybackTimeReached, extendClipTimestampTitleBoundaryDisabled, extendClipTimestampTitleSizeLimitDisabled) => clipIsPlaybackTimeReached
    ? extendClipTimestampTitleBoundaryDisabled
    : extendClipTimestampTitleSizeLimitDisabled;
function CropEndTimestamp() {
    const { saveModifiedPlaylistEntryInCurrentlySelectedPlaylist } = useMyPlaylists$1();
    const { cropClipEndTimestampTitle, cropClipEndTimestampTitleDisabledClipEndEqualsPlaybackTime, cropClipEndTimestampTitleDisabledClipLowerLimitWouldBeExceeded, } = useTranslations([
        'cropClipEndTimestampTitle',
        'cropClipEndTimestampTitleDisabledClipEndEqualsPlaybackTime',
        'cropClipEndTimestampTitleDisabledClipLowerLimitWouldBeExceeded',
    ]);
    const currentPlaybackTime = useAtomValue(currentPlaybackTimeAtom);
    const videoClipEndTimestamp = useAtomValue(getVideoClipEndTimestampAtom);
    const canPlay = useAtomValue(videoCanPlayAtom);
    const setCurrentlySelectedEntryEndTimeToCurrentPlaybackTime = useSetAtom(setCurrentlySelectedEntryEndTimeToCurrentPlaybackTimeAtom);
    const setPauseOnClipEnd = useSetAtom(pauseOnClipEndAtom);
    const setPlayRequested = useSetAtom(playRequestedAtom);
    const getVideoClipRecentlyStarted = useAtomValue(getVideoClipRecentlyStartedAtom);
    const isSoccerClip = useAtomValue(getIsSoccerClipAtom);
    const clipEndIsPlaybackTimeReached = videoClipEndTimestamp === currentPlaybackTime;
    const disabled = !canPlay || clipEndIsPlaybackTimeReached || getVideoClipRecentlyStarted;
    const disabledReasonTitle = getDisabledTitle$3(clipEndIsPlaybackTimeReached, cropClipEndTimestampTitleDisabledClipEndEqualsPlaybackTime, cropClipEndTimestampTitleDisabledClipLowerLimitWouldBeExceeded);
    const onCropEnd = async () => {
        setPlayRequested(false);
        setPauseOnClipEnd(true);
        setCurrentlySelectedEntryEndTimeToCurrentPlaybackTime();
        await saveModifiedPlaylistEntryInCurrentlySelectedPlaylist();
    };
    return isSoccerClip ? (jsx(Button, { isDisabled: disabled, onClick: onCropEnd, shape: "circle", size: "small", title: disabled ? disabledReasonTitle : cropClipEndTimestampTitle, variant: PLAYER_BUTTON_VARIANT, children: "]" })) : null;
}

const getDisabledTitle$2 = (clipIsPlaybackTimeReached, extendClipTimestampTitleBoundaryDisabled, extendClipTimestampTitleSizeLimitDisabled) => clipIsPlaybackTimeReached
    ? extendClipTimestampTitleBoundaryDisabled
    : extendClipTimestampTitleSizeLimitDisabled;
function CropStartTimestamp() {
    const { saveModifiedPlaylistEntryInCurrentlySelectedPlaylist } = useMyPlaylists$1();
    const { cropClipStartTimestampTitle, cropClipStartTimestampTitleDisabledClipStartEqualsPlaybackTime, cropClipStartTimestampTitleDisabledClipLowerLimitWouldBeExceeded, } = useTranslations([
        'cropClipStartTimestampTitle',
        'cropClipStartTimestampTitleDisabledClipStartEqualsPlaybackTime',
        'cropClipStartTimestampTitleDisabledClipLowerLimitWouldBeExceeded',
    ]);
    const currentPlaybackTime = useAtomValue(currentPlaybackTimeAtom);
    const videoClipStartTimestamp = useAtomValue(getVideoClipStartTimestampAtom);
    const canPlay = useAtomValue(videoCanPlayAtom);
    const setCurrentlySelectedEntryStartTimeToCurrentPlaybackTime = useSetAtom(setCurrentlySelectedEntryStartTimeToCurrentPlaybackTimeAtom);
    const getVideoClipNearlyEnding = useAtomValue(getVideoClipNearlyEndingAtom);
    const isSoccerClip = useAtomValue(getIsSoccerClipAtom);
    const clipStartIsPlaybackTimeReached = videoClipStartTimestamp === currentPlaybackTime;
    const disabled = !canPlay || clipStartIsPlaybackTimeReached || getVideoClipNearlyEnding;
    const disabledReasonTitle = getDisabledTitle$2(clipStartIsPlaybackTimeReached, cropClipStartTimestampTitleDisabledClipStartEqualsPlaybackTime, cropClipStartTimestampTitleDisabledClipLowerLimitWouldBeExceeded);
    const onCropStart = async () => {
        setCurrentlySelectedEntryStartTimeToCurrentPlaybackTime();
        await saveModifiedPlaylistEntryInCurrentlySelectedPlaylist();
    };
    return isSoccerClip ? (jsx(Button, { isDisabled: disabled, onClick: onCropStart, shape: "circle", size: "small", title: disabled ? disabledReasonTitle : cropClipStartTimestampTitle, variant: PLAYER_BUTTON_VARIANT, children: "[" })) : null;
}

const DEFAULT_TIME_FOR_CLIP_EXTENSION$1 = secondsToMicroseconds(10);
const CLIP_TIME_LIMIT$1 = secondsToMicroseconds(180);
const getDisabledTitle$1 = (maxClipTimeExceeded, extendClipTimestampTitleSizeLimitDisabled, extendClipTimestampTitleBoundaryDisabled) => maxClipTimeExceeded
    ? extendClipTimestampTitleSizeLimitDisabled
    : extendClipTimestampTitleBoundaryDisabled;
function ExtendEndTimestamp() {
    const { saveModifiedPlaylistEntryInCurrentlySelectedPlaylist } = useMyPlaylists$1();
    const { extendClipEndTimestampTitle, extendClipEndTimestampTitleBoundaryDisabled, extendClipEndTimestampTitleSizeLimitDisabled, } = useTranslations([
        'extendClipEndTimestampTitle',
        'extendClipEndTimestampTitleBoundaryDisabled',
        'extendClipEndTimestampTitleSizeLimitDisabled',
    ]);
    const videoClipEndTimestamp = useAtomValue(getVideoClipEndTimestampAtom);
    const currentVideoDuration = useAtomValue(currentPlaybackItemDurationAtom);
    const getVideoClipDuration = useAtomValue(getVideoClipDurationAtom);
    const canPlay = useAtomValue(videoCanPlayAtom);
    const extendCurrentlySelectedEntryEndTime = useSetAtom(extendCurrentlySelectedEntryEndTimeAtom);
    const clipTimeLimitExceeded = getVideoClipDuration > CLIP_TIME_LIMIT$1;
    const disabled = !canPlay ||
        videoClipEndTimestamp >= currentVideoDuration ||
        clipTimeLimitExceeded;
    const disabledReasonTitle = getDisabledTitle$1(clipTimeLimitExceeded, extendClipEndTimestampTitleSizeLimitDisabled, extendClipEndTimestampTitleBoundaryDisabled);
    const onExtendEnd = () => {
        if (videoClipEndTimestamp < currentVideoDuration) {
            extendCurrentlySelectedEntryEndTime(DEFAULT_TIME_FOR_CLIP_EXTENSION$1);
            saveModifiedPlaylistEntryInCurrentlySelectedPlaylist();
        }
    };
    const isSoccerClip = useAtomValue(getIsSoccerClipAtom);
    return isSoccerClip ? (jsx(ButtonIcon, { disabled: disabled, icon: "Forward10", onClick: onExtendEnd, title: disabled ? disabledReasonTitle : extendClipEndTimestampTitle, variant: PLAYER_BUTTON_VARIANT })) : null;
}

const DEFAULT_TIME_FOR_CLIP_EXTENSION = secondsToMicroseconds(10);
const CLIP_TIME_LIMIT = secondsToMicroseconds(180);
const getDisabledTitle = (maxClipTimeExceeded, extendClipTimestampTitleSizeLimitDisabled, extendClipTimestampTitleBoundaryDisabled) => maxClipTimeExceeded
    ? extendClipTimestampTitleSizeLimitDisabled
    : extendClipTimestampTitleBoundaryDisabled;
function ExtendStartTimestamp() {
    const { saveModifiedPlaylistEntryInCurrentlySelectedPlaylist } = useMyPlaylists$1();
    const { extendClipStartTimestampTitle, extendClipStartTimestampTitleBoundaryReachedDisabled, extendClipStartTimestampTitleSizeLimitDisabled, } = useTranslations([
        'extendClipStartTimestampTitle',
        'extendClipStartTimestampTitleBoundaryReachedDisabled',
        'extendClipStartTimestampTitleSizeLimitDisabled',
    ]);
    const videoClipStartTimestamp = useAtomValue(getVideoClipStartTimestampAtom);
    const canPlay = useAtomValue(videoCanPlayAtom);
    const setResetRequested = useSetAtom(resetRequestedAtom);
    const extendCurrentlySelectedEntryStartTime = useSetAtom(extendCurrentlySelectedEntryStartTimeAtom);
    const getVideoClipDuration = useAtomValue(getVideoClipDurationAtom);
    const isSoccerClip = useAtomValue(getIsSoccerClipAtom);
    const clipTimeLimitExceeded = getVideoClipDuration > CLIP_TIME_LIMIT;
    const disabled = !canPlay || !videoClipStartTimestamp || clipTimeLimitExceeded;
    const disabledReasonTitle = getDisabledTitle(clipTimeLimitExceeded, extendClipStartTimestampTitleSizeLimitDisabled, extendClipStartTimestampTitleBoundaryReachedDisabled);
    const onExtendStart = () => {
        if (videoClipStartTimestamp > 0) {
            extendCurrentlySelectedEntryStartTime(-DEFAULT_TIME_FOR_CLIP_EXTENSION);
            saveModifiedPlaylistEntryInCurrentlySelectedPlaylist();
        }
        setResetRequested(true);
    };
    return isSoccerClip ? (jsx(ButtonIcon, { disabled: disabled, icon: "Back10", onClick: onExtendStart, title: disabled ? disabledReasonTitle : extendClipStartTimestampTitle, variant: PLAYER_BUTTON_VARIANT })) : null;
}

const NUMBER_OF_DESIRED_STEPS = 1000;
const CLASSNAME_TO_ALLOW_KEY_EVENTS_TO_FIRE = 'mousetrap';
function PlayerTimeline({ disableClipEditing }) {
    const setPlaybackTimeRequested = useSetAtom(playbackTimeRequestedAtom);
    const { duration, endTimestamp, position, startTimestamp } = useTimelineDetails();
    const onTimelinePress = (event) => setPlaybackTimeRequested(+event.target.value);
    const step = Math.round(duration / NUMBER_OF_DESIRED_STEPS);
    const videoTimeline = useTranslation('videoTimeline');
    return (Number.isFinite(duration) && (jsxs(TimelineContainer, { children: [!disableClipEditing && (jsxs(Fragment, { children: [jsx(ExtendStartTimestamp, {}), jsx(CropStartTimestamp, {})] })), jsx(StyledSlider, { "aria-label": videoTimeline, className: CLASSNAME_TO_ALLOW_KEY_EVENTS_TO_FIRE, max: endTimestamp, min: startTimestamp, onInput: onTimelinePress, step: step, type: "range", value: position || 0 }), !disableClipEditing && (jsxs(Fragment, { children: [jsx(CropEndTimestamp, {}), jsx(ExtendEndTimestamp, {})] }))] })));
}

function useMyPlaylists() {
    const [myPlaylists, setMyPlaylists] = useAtom(myPlaylistsAtom);
    const setPlaylistActionsMessage = useSetAtom(messagePlaylistActionsAtom);
    const { data, error, ...swrResponse } = useSWRWithAuth(Endpoint.MY_PLAYLISTS);
    const errorRetrievingPlaylists = useTranslation('errorRetrievingPlaylists');
    useEffect(() => {
        // useEffect Required to prevent setState in render errors
        if (data === null || data === void 0 ? void 0 : data.playlists) {
            setMyPlaylists(data.playlists);
        }
    }, [data]);
    useEffect(() => {
        if (error) {
            setPlaylistActionsMessage({
                description: errorRetrievingPlaylists,
                type: 'error',
            });
        }
    }, [error]);
    return { ...swrResponse, data: myPlaylists, error };
}

const MyPlaylistsSelectionHeader = styled.div `
  padding: 0.5rem 0 0 0;
`;
const MyPlaylistsSelectionList = styled.ol `
  padding-top: 0;
  overflow-y: scroll;
  max-height: 15rem;
`;
const MyPlaylistsSelectionItem = styled.li `
  padding: 0.25rem 0;
`;

// Action buttons wrapper in playlist header
const PlaylistActionsWrapper = styled.div `
  display: flex;
  gap: 0.5rem;
  justify-content: end;
  flex-shrink: 0;
`;
const PlaylistActionsButtonContainer = styled.span `
  flex-shrink: 0;
`;
const ContainerBase = styled.div `
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0 1rem;
  min-width: 21rem;
  max-width: 90vw;
  max-height: 20rem;
`;
const DeleteWarningContainer = styled(ContainerBase) ``;
const SaveOptionsContainer = styled(ContainerBase) `
  min-height: 11rem;
`;
const CreateCutupOptionsContainer = styled(ContainerBase) ``;
const ContentIndentation = styled.div `
  padding-left: 0.25rem;
`;

const DEFAULT_TITLE = 'My Playlists';
function MyPlaylistsSelection({ selectedPlaylists, onItemSelection, title = DEFAULT_TITLE, isDisabled = false, }) {
    const { data: myPlaylists } = useMyPlaylists();
    const selectedMyPlaylistId = useAtomValue(selectedMyPlaylistIdAtom);
    const isChecked = (playlist) => selectedPlaylists
        .map((selectedList) => selectedList.id)
        .includes(playlist.id);
    const byNotAlreadyTheSelectedPlaylist = (playlist) => playlist.id !== selectedMyPlaylistId;
    const myPlaylistsToSaveToOptions = myPlaylists.filter(byNotAlreadyTheSelectedPlaylist);
    return myPlaylistsToSaveToOptions.length > 0 ? (jsxs(Fragment, { children: [jsx(MyPlaylistsSelectionHeader, { children: jsx(Typography, { variant: "headingMedium", children: title }) }), jsx(ContentIndentation, { children: jsx(MyPlaylistsSelectionList, { children: myPlaylistsToSaveToOptions.map((playlist) => {
                        const { id, name } = playlist;
                        return (jsx(MyPlaylistsSelectionItem, { children: jsx(Checkbox, { id: id, disabled: isDisabled, checked: isChecked(playlist), label: name, onChange: (event) => {
                                    onItemSelection(event, playlist);
                                } }) }, id));
                    }) }) })] })) : null;
}

function PlaylistSelection({ onClose, onSavePlaylistSuccess, playlistEntries, }) {
    const { cancel: cancelLabel, playlistInputPlaceholder, playlistNameInputLabel, save: saveLabel, saveVideoCount, } = useTranslations([
        'cancel',
        'playlistInputPlaceholder',
        'playlistNameInputLabel',
        'save',
        'saveVideoCount',
    ]);
    const [selectedPlaylists, setSelectedPlaylists] = useAtom(selectedPlaylistsAtom);
    const [playlistName, setPlaylistName] = useAtom(playlistNameAtom);
    const toggleSelectedPlaylist = useSetAtom(toggleSelectedPlaylistAtom);
    const { saveToNewPlaylist, saveToSelectedPlaylists } = useMyPlaylists$1();
    const clearSelectedPlaylists = () => {
        setSelectedPlaylists([]);
    };
    const onPlaylistClick = (_, selection) => {
        toggleSelectedPlaylist(selection);
    };
    const resetPlaylistSelections = () => {
        clearSelectedPlaylists();
        setPlaylistName('');
    };
    const clearAndExecuteOnSuccess = () => {
        if (onSavePlaylistSuccess) {
            onSavePlaylistSuccess();
        }
        resetPlaylistSelections();
    };
    const onSaveClipsToSelectedPlaylists = async () => {
        if (playlistName) {
            await saveToNewPlaylist(playlistEntries, clearAndExecuteOnSuccess);
        }
        else {
            await saveToSelectedPlaylists(playlistEntries, clearAndExecuteOnSuccess);
        }
    };
    const onCancel = () => {
        resetPlaylistSelections();
        onClose();
    };
    return (jsxs(Fragment, { children: [jsx(ModalHeader, { id: "save-playlist-modal-header", children: formatTemplatedMessage(saveVideoCount, {
                    count: playlistEntries.length,
                }) }), jsx(SaveOptionsContainer, { children: jsxs(ContentIndentation, { children: [jsx(TextField, { id: "save-playlist-name", label: playlistNameInputLabel, onChange: (event) => {
                                setPlaylistName(event.target.value);
                                clearSelectedPlaylists();
                            }, value: playlistName, placeholder: playlistInputPlaceholder, size: "regular", variant: "primary", isDisabled: selectedPlaylists.length > 0 }), jsx(MyPlaylistsSelection, { selectedPlaylists: selectedPlaylists, onItemSelection: onPlaylistClick, isDisabled: playlistName.length > 0 })] }) }), jsxs(ModalFooter, { children: [jsx(Button, { variant: "ghost", onClick: onCancel, children: cancelLabel }), jsx(Button, { onClick: onSaveClipsToSelectedPlaylists, children: saveLabel })] })] }));
}

function SaveCurrentlyPlaying() {
    const saveCurrentlyPlaying = useTranslation('saveCurrentlyPlaying');
    const canPlay = useAtomValue(videoCanPlayAtom);
    const selectedPlaylistEntry = useAtomValue(selectedPlaylistEntryAtom);
    const videoClipEndTimestamp = useAtomValue(getVideoClipEndTimestampAtom);
    const setPauseOnClipEnd = useSetAtom(pauseOnClipEndAtom);
    const [displayConfirmation, setDisplayConfirmation] = useAtom(displaySaveSingleEntryToPlaylistConfirmationAtom);
    const isClip = !!videoClipEndTimestamp;
    const handleDialogConfirmation = (isOpen) => {
        setDisplayConfirmation(isOpen);
        if (isOpen) {
            setPauseOnClipEnd(true);
        }
    };
    const onClick = () => {
        handleDialogConfirmation(true);
    };
    const onClose = () => {
        handleDialogConfirmation(false);
    };
    return (isClip && (jsx(ButtonWithFlyout, { disabled: !canPlay || !(selectedPlaylistEntry === null || selectedPlaylistEntry === void 0 ? void 0 : selectedPlaylistEntry.id), icon: "Save", isOpen: displayConfirmation, onClick: onClick, onOutsideClick: onClose, title: saveCurrentlyPlaying, placement: "bottom-end", variant: PLAYER_BUTTON_VARIANT, children: selectedPlaylistEntry && (jsx(PlaylistSelection, { onClose: onClose, onSavePlaylistSuccess: onClose, playlistEntries: [selectedPlaylistEntry] })) })));
}

function useScrub(scrubDuration) {
    const { positionInClip, startTimestamp, endTimestamp, duration } = useTimelineDetails();
    const canPlay = useAtomValue(videoCanPlayAtom);
    const toggleResetRequested = useSetAtom(resetRequestedAtom);
    const setPlaybackTimeRequested = useSetAtom(playbackTimeRequestedAtom);
    const videoIsPlaying = useAtomValue(playRequestedAtom);
    const currentPlaybackTime = useAtomValue(currentPlaybackTimeAtom);
    const videoClipStartTimestamp = useAtomValue(getVideoClipStartTimestampAtom);
    const videoClipEnded = useAtomValue(videoClipEndedAtom);
    const startTime = startTimestamp || 0;
    const endTime = endTimestamp || duration;
    const atStartOfPlayback = currentPlaybackTime === 0 ||
        currentPlaybackTime === videoClipStartTimestamp;
    const isScrubBackDisabled = !(canPlay &&
        (videoIsPlaying || !atStartOfPlayback));
    const isScrubForwardDisabled = !(canPlay && !videoClipEnded);
    const onScrubBack = () => {
        const scrubBackToPosition = positionInClip - scrubDuration;
        if (scrubBackToPosition < 0) {
            toggleResetRequested(true);
        }
        else {
            setPlaybackTimeRequested(startTime + scrubBackToPosition);
        }
    };
    const onScrubForward = () => {
        const scrubForwardToPosition = startTime + (positionInClip + scrubDuration);
        if (scrubForwardToPosition > endTime) {
            setPlaybackTimeRequested(endTime);
        }
        else {
            setPlaybackTimeRequested(scrubForwardToPosition);
        }
    };
    return {
        isScrubBackDisabled,
        isScrubForwardDisabled,
        onScrubBack,
        onScrubForward,
    };
}

function ScrubBack() {
    const scrubDuration = useAtomValue(scrubDurationAtom);
    const { isScrubBackDisabled, onScrubBack } = useScrub(scrubDuration);
    const scrubBackSeconds = useTranslation('scrubBackSeconds');
    return (jsx(ButtonIcon, { disabled: isScrubBackDisabled, icon: "DoubleChevronLeft", onClick: onScrubBack, title: formatTemplatedMessage(scrubBackSeconds, {
            seconds: microsecondsToSeconds(scrubDuration),
        }), variant: PLAYER_BUTTON_VARIANT }));
}

function ScrubBackFine() {
    const scrubFineDuration = useAtomValue(scrubFineDurationAtom);
    const { isScrubBackDisabled, onScrubBack } = useScrub(scrubFineDuration);
    const scrubBackSeconds = useTranslation('scrubBackSeconds');
    return (jsx(ButtonIcon, { disabled: isScrubBackDisabled, icon: "ChevronLeft", onClick: onScrubBack, title: formatTemplatedMessage(scrubBackSeconds, {
            seconds: microsecondsToSeconds(scrubFineDuration),
        }), variant: PLAYER_BUTTON_VARIANT }));
}

function ScrubForward() {
    const scrubDuration = useAtomValue(scrubDurationAtom);
    const { isScrubForwardDisabled, onScrubForward } = useScrub(scrubDuration);
    const scrubForwardSeconds = useTranslation('scrubForwardSeconds');
    return (jsx(ButtonIcon, { disabled: isScrubForwardDisabled, icon: "DoubleChevronRight", onClick: onScrubForward, title: formatTemplatedMessage(scrubForwardSeconds, {
            seconds: microsecondsToSeconds(scrubDuration),
        }), variant: PLAYER_BUTTON_VARIANT }));
}

function ScrubForwardFine() {
    const scrubFineDuration = useAtomValue(scrubFineDurationAtom);
    const { isScrubForwardDisabled, onScrubForward } = useScrub(scrubFineDuration);
    const scrubForwardSeconds = useTranslation('scrubForwardSeconds');
    return (jsx(ButtonIcon, { disabled: isScrubForwardDisabled, icon: "ChevronRight", onClick: onScrubForward, title: formatTemplatedMessage(scrubForwardSeconds, {
            seconds: microsecondsToSeconds(scrubFineDuration),
        }), variant: PLAYER_BUTTON_VARIANT }));
}

const MAX_VOLUME = 1;
const MIN_VOLUME = 0;
const VOLUME_STEP = 0.01;
function Volume() {
    const [currentVolume, setVolume] = useAtom(volumeRequestedAtom);
    const isMuted = useAtomValue(mutedRequestedAtom);
    const onVolumeChange = (volumeRequested) => {
        setVolume(volumeRequested);
    };
    const volumeControl = useTranslation('volumeControl');
    return !isMuted ? (jsx(VolumeWrapper, { children: jsx(SliderContainer, { children: jsx(StyledVolumeSlider, { "aria-label": volumeControl, max: MAX_VOLUME, min: MIN_VOLUME, onChange: (event) => onVolumeChange(+event.target.value), step: VOLUME_STEP, type: "range", value: currentVolume }) }) })) : null;
}

function VideoPlayerControls({ videoControls, overlayControls, }) {
    function toDisplay(componentName, Component) {
        return videoControls.includes(componentName) ? Component : null;
    }
    const [showControls, setShowControls] = useAtom(showControlsAtom);
    const [showMobileControls, setShowMobileControls] = useAtom(showMobileControlsAtom);
    const setShowControlsWithTimeout = useSetAtom(showControlsWithTimeoutAtom);
    const [renderControlsBelowVideo, setRenderControlsBelowVideo] = useAtom(renderControlsBelowVideoAtom);
    const canPlay = useAtomValue(videoCanPlayAtom);
    const [playRequested, setPlayRequested] = useAtom(playRequestedAtom);
    const videoIsPlaying = useAtomValue(playRequestedAtom);
    const videoClipEnded = useAtomValue(videoClipEndedAtom);
    const [elementRef, { width }] = useRect();
    useLayoutEffect(() => {
        setShowMobileControls(width < BREAKPOINTS.sm);
    }, [width]);
    const handleControlsVisibility = () => {
        setShowControls(true);
        if (videoIsPlaying) {
            setShowControlsWithTimeout(false);
        }
    };
    const isDisabled = !canPlay ||
        // Play request made but video isn't playing yet
        (playRequested && !videoIsPlaying);
    const onPlay = () => canPlay && !isDisabled && setPlayRequested(true);
    const onPause = () => {
        setPlayRequested(false);
    };
    const setResetRequested = useSetAtom(resetRequestedAtom);
    const pauseOrPlay = () => {
        if (videoClipEnded) {
            setResetRequested(true);
        }
        else if (!videoIsPlaying) {
            onPlay();
        }
        else {
            onPause();
        }
    };
    const isFullScreen = useAtomValue(fullscreenRequestedAtom);
    // We need to set the theme to be dark all the time if in inline mode (i.e. controls are not rendered below).
    // otherwise honour the theme from the parent.
    const theme = useTheme();
    const currentVideoTheme = !renderControlsBelowVideo ? videoTheme.dark : theme;
    const isSoccerClip = useAtomValue(getIsSoccerClipAtom);
    useLayoutEffect(() => {
        setRenderControlsBelowVideo(!isFullScreen && !overlayControls);
    }, [isFullScreen, overlayControls]);
    return (jsx(ThemeProvider, { theme: currentVideoTheme, applyGlobalStyles: false, children: jsxs(PlayerWrapper, { onMouseMove: handleControlsVisibility, onKeyUp: handleControlsVisibility, onMouseOut: () => setShowControls(false), ref: elementRef, "$showMobileControls": showMobileControls, "$showControlsBelowVideo": renderControlsBelowVideo, "$displayControls": !videoIsPlaying || showControls, "data-testid": `theme-${currentVideoTheme.name.toLowerCase().replace(' ', '-')}`, children: [jsx(ClickableWrapper, { "$showControlsBelowVideo": renderControlsBelowVideo, "$showMobileControls": showMobileControls, "$isFullScreen": isFullScreen, "$displayControls": !videoIsPlaying || showControls, onClick: pauseOrPlay, "data-testid": "video-overlay", children: showMobileControls && (jsx(MobileOverlayButtons, { isDisabled: isDisabled, showControls: showControls, renderControlsBelowVideo: renderControlsBelowVideo, videoIsPlaying: videoIsPlaying })) }), jsxs(DesktopWrapper, { "$showControlsBelowVideo": renderControlsBelowVideo, "$isFullScreen": isFullScreen, "$showMobileControls": showMobileControls, "$displayControls": !videoIsPlaying || showControls, "$allowAdditionalSpaceForTimelineButtons": isSoccerClip, children: [toDisplay(Controls.PlayerTimeline, jsx(PlayerTimeline, {})), jsxs(ControlsWrapper, { "$displayControls": !videoIsPlaying || showControls, "$showMobileControls": showMobileControls && overlayControls, "$showControlsBelowVideo": renderControlsBelowVideo, children: [jsxs(ButtonGroupWrapper, { children: [!showMobileControls && (jsxs(Fragment, { children: [toDisplay(Controls.BackButton, jsx(BackButton, {})), toDisplay(Controls.ScrubBack, jsx(ScrubBack, {})), toDisplay(Controls.ScrubBackFine, jsx(ScrubBackFine, {})), toDisplay(Controls.PlayPauseButton, jsx(PlayPauseButton, { isDisabled: isDisabled, handleClick: pauseOrPlay, variant: showMobileControls ? 'secondary' : 'primary' })), toDisplay(Controls.ScrubForwardFine, jsx(ScrubForwardFine, {})), toDisplay(Controls.ScrubForward, jsx(ScrubForward, {})), toDisplay(Controls.NextButton, jsx(NextButton, {}))] })), toDisplay(Controls.MuteButton, jsxs(VolumeControlsWrapper, { children: [jsx(MuteButton, { size: showMobileControls ? 'small' : 'regular' }), jsx(Volume, {})] }))] }), toDisplay(Controls.PlayerTimeDisplay, jsx(PlayerTimeDisplay, {})), jsxs(ButtonGroupWrapper, { children: [!showMobileControls &&
                                            toDisplay(Controls.SaveCurrentlyPlaying, jsx(SaveCurrentlyPlaying, {})), !showMobileControls &&
                                            toDisplay(Controls.KeyboardShortcutsDisplay, jsx(KeyboardShortcutsDisplay, {})), toDisplay(Controls.HighlightPlayersButton, jsx(HighlightPlayersButton, { size: showMobileControls ? 'small' : 'regular' })), jsx(MoreOptions, { size: showMobileControls ? 'small' : 'regular', videoControls: videoControls }), toDisplay(Controls.FullscreenButton, jsx(FullscreenButton, { size: showMobileControls ? 'small' : 'regular' }))] })] })] })] }) }));
}

const onCanPlay = (setCanPlay) => {
    setCanPlay(true);
};

const SUPPORTED_STREAM_FORMATS = ['.m3u8', '.mpd'];
const LOADER_TRANSITION_DURATION = '500ms';
const hasBrowserSupportForFrameCallbacks = () => 'requestVideoFrameCallback' in HTMLVideoElement.prototype;
function VideoPlayer({ autoPlay = false, children = undefined, controls = true, config = undefined, debug = false, desiredStartTime = undefined, gslCredentialsRequired, muted = true, onFrameChange = undefined, onError = () => { }, src, testId = 'video', overlayControls = false, }) {
    const frame = useRef();
    const playerRef = useRef(null);
    const [canPlay, setCanPlay] = useAtom(videoCanPlayAtom);
    const [clipStartLocated, setClipStartLocated] = useState(false);
    const [playbackPreviouslyInitiated, setPlaybackPreviouslyInitiated] = useState(false);
    const desiredPlayerState = useAtomValue(desiredPlayerStateAtom);
    const licenseProvidedByConsumer = useAtomValue(theoPlayerLicenseAtom);
    const libraryLocation = useAtomValue(theoPlayerLibraryLocationAtom);
    const [seeking, setSeeking] = useAtom(videoSeekingAtom);
    const videoControls = controls === true ? [...DEFAULT_CONTROLS] : controls;
    const videoClipEnded = useAtomValue(videoClipEndedAtom);
    const setAutoSkipEnabled = useSetAtom(autoSkipEnabledAtom);
    const setCurrentPlaybackTime = useSetAtom(currentPlaybackTimeAtom);
    const setCurrentPlaybackItemDuration = useSetAtom(currentPlaybackItemDurationAtom);
    const setIsPlaying = useSetAtom(videoIsPlayingAtom);
    const setMuteRequested = useSetAtom(mutedRequestedAtom);
    const setPauseOnClipEnd = useSetAtom(pauseOnClipEndAtom);
    const setPlaybackTimeRequested = useSetAtom(playbackTimeRequestedAtom);
    const setPlayRequested = useSetAtom(playRequestedAtom);
    const setResetRequested = useSetAtom(resetRequestedAtom);
    const setScrubDuration = useSetAtom(scrubDurationAtom);
    const setScrubFineDuration = useSetAtom(scrubFineDurationAtom);
    const setVideoPlaybackFullDurationReached = useSetAtom(videoPlaybackFullDurationReachedAtom);
    const setVideoResolution = useSetAtom(videoResolutionAtom);
    const { gslAuthComplete, theoPlayerLicense: licenseProvidedByServer } = usePlaybackAuth();
    const isLoading = !!src;
    const isLoadingFinished = isLoading &&
        canPlay &&
        (!gslCredentialsRequired || gslAuthComplete) &&
        (clipStartLocated || !desiredStartTime);
    // Initial configuration support start
    useEffect(() => {
        setMuteRequested(muted);
    }, [muted]);
    useEffect(() => {
        if (config === null || config === void 0 ? void 0 : config.scrubDuration)
            setScrubDuration(config.scrubDuration);
        if (config === null || config === void 0 ? void 0 : config.scrubFineDuration)
            setScrubFineDuration(config.scrubFineDuration);
    }, [config]);
    // Initial configuration support end
    // Player Actions Start
    const requestToPlayIfRequired = () => {
        if (autoPlay || playbackPreviouslyInitiated) {
            setPlayRequested(true);
        }
    };
    const resetClipTime = () => {
        setAutoSkipEnabled(false);
        if (Number.isInteger(desiredStartTime)) {
            setPlaybackTimeRequested(desiredStartTime);
        }
    };
    const play = () => {
        if (playerRef.current) {
            try {
                playerRef.current.play();
                setIsPlaying(true);
                if (!playbackPreviouslyInitiated) {
                    setPlaybackPreviouslyInitiated(true);
                }
            }
            catch (error) {
                // Swallow the error but log it to console.
                // eslint-disable-next-line no-console
                console.error(error);
                setPlayRequested(false);
            }
        }
    };
    const setPlayerToDesiredState = async () => {
        if (playerRef.current && canPlay) {
            /* eslint-disable no-param-reassign */
            playerRef.current.playbackRate = desiredPlayerState.playbackRate;
            playerRef.current.volume = desiredPlayerState.volume;
            playerRef.current.muted = desiredPlayerState.muted;
            if (desiredPlayerState.playbackTime && playerRef.current.readyState > 0) {
                playerRef.current.currentTime = microsecondsToSeconds(desiredPlayerState.playbackTime);
                setCurrentPlaybackTime(desiredPlayerState.playbackTime);
                setPlaybackTimeRequested(0);
            }
            const fullDurationReached = playerRef.current.currentTime >= playerRef.current.duration;
            /* eslint-enable no-param-reassign */
            if (!fullDurationReached) {
                if (desiredPlayerState.playing) {
                    const isVideoPlaying = !!(playerRef.current.currentTime > 0 &&
                        !playerRef.current.paused &&
                        !playerRef.current.ended);
                    if (!isVideoPlaying && canPlay) {
                        play();
                    }
                }
                else {
                    playerRef.current.pause();
                    setIsPlaying(false);
                    if (!videoClipEnded) {
                        setCurrentPlaybackTime(secondsToMicroseconds(playerRef.current.currentTime));
                    }
                }
            }
            else {
                setVideoPlaybackFullDurationReached(true);
            }
            if (desiredPlayerState.reset) {
                setResetRequested(false);
                if (desiredStartTime) {
                    resetClipTime();
                }
                else {
                    playerRef.current.currentTime = 0;
                }
                setPlayRequested(true);
            }
        }
    };
    useEffect(() => {
        async function handleChanges() {
            await setPlayerToDesiredState();
        }
        handleChanges();
    }, [
        desiredPlayerState.muted,
        desiredPlayerState.playbackRate,
        desiredPlayerState.playbackTime,
        desiredPlayerState.playing,
        desiredPlayerState.reset,
        desiredPlayerState.volume,
        canPlay,
    ]);
    // Player Actions End
    // Event handlers start
    const handleCanPlay = () => onCanPlay(setCanPlay);
    const handleRequestVideoFrameCallback = (timestamp, metadata) => {
        var _a, _b, _c;
        if (metadata) {
            const mediaTime = secondsToMicroseconds(metadata.mediaTime);
            // FF users don't panic! If requestVideoFrameCallback is not detected
            // then we fallback to using timeupdate to set the current microsecond.
            if (hasBrowserSupportForFrameCallbacks() &&
                !((_a = playerRef.current) === null || _a === void 0 ? void 0 : _a.paused) &&
                !((_b = playerRef.current) === null || _b === void 0 ? void 0 : _b.ended) &&
                !((_c = playerRef.current) === null || _c === void 0 ? void 0 : _c.seeking)) {
                setCurrentPlaybackTime(mediaTime);
            }
            if (onFrameChange) {
                onFrameChange(timestamp, metadata);
            }
            if (playerRef.current) {
                // Re-register the callback so we're notified about the next frame update.
                frame.current = playerRef.current.canvas.requestVideoFrameCallback(handleRequestVideoFrameCallback);
            }
        }
    };
    const handleDebug = (event) => {
        console.log(event.type, event); // eslint-disable-line no-console
    };
    const handleDurationChange = (event) => {
        setCurrentPlaybackItemDuration(secondsToMicroseconds(event.duration));
    };
    const handleEnded = () => {
        setVideoPlaybackFullDurationReached(true);
    };
    const onTimeUpdateCallback = useCallback((event) => {
        const { currentTime } = event;
        const microseconds = secondsToMicroseconds(currentTime);
        // If requestVideoFrameCallback is unsupported then use timeupdate to set the current playback time
        // We use requestVideoFrameCallback when possible as it's more accurate
        if (!hasBrowserSupportForFrameCallbacks()) {
            setCurrentPlaybackTime(microseconds);
        }
    }, []);
    useEffect(() => {
        if (canPlay && playerRef.current && typeof frame.current === 'undefined') {
            frame.current = playerRef.current.canvas.requestVideoFrameCallback(handleRequestVideoFrameCallback);
        }
        return () => {
            var _a;
            if (frame.current) {
                (_a = playerRef.current) === null || _a === void 0 ? void 0 : _a.canvas.cancelVideoFrameCallback(frame.current);
                frame.current = undefined;
            }
        };
    }, [canPlay, playerRef.current]);
    const handleLoadedMetadata = () => {
        if (playerRef.current) {
            setVideoResolution([
                playerRef.current.videoWidth,
                playerRef.current.videoHeight,
            ]);
        }
    };
    const handleSeeked = () => {
        var _a, _b;
        setSeeking(false);
        if (!clipStartLocated) {
            setClipStartLocated(true);
        }
        if (playerRef.current) {
            setVideoPlaybackFullDurationReached(((_a = playerRef.current) === null || _a === void 0 ? void 0 : _a.currentTime) >= ((_b = playerRef.current) === null || _b === void 0 ? void 0 : _b.duration));
        }
    };
    const handleSeeking = () => {
        setSeeking(true);
    };
    const handlePlaying = () => {
        if (!seeking) {
            setAutoSkipEnabled(true);
            setPauseOnClipEnd(false);
            setVideoPlaybackFullDurationReached(false);
        }
    };
    const handleError = (error) => {
        onError(error);
        if (debug) {
            // Display due to debugging in progress
            // eslint-disable-next-line no-console
            console.error(error);
        }
    };
    const addPlayerHandlers = () => {
        if (playerRef.current) {
            playerRef.current.addEventListener('canplay', handleCanPlay);
            playerRef.current.addEventListener('durationchange', handleDurationChange);
            playerRef.current.addEventListener('ended', handleEnded);
            playerRef.current.addEventListener('error', handleError);
            playerRef.current.addEventListener('loadedmetadata', handleLoadedMetadata);
            playerRef.current.addEventListener('playing', handlePlaying);
            playerRef.current.addEventListener('seeked', handleSeeked);
            playerRef.current.addEventListener('seeking', handleSeeking);
            playerRef.current.addEventListener('timeupdate', onTimeUpdateCallback);
            if (debug) {
                VIDEO_EVENTS.forEach((eventName) => {
                    playerRef.current.addEventListener(eventName, handleDebug);
                });
            }
        }
    };
    const removePlayerHandlers = () => {
        if (playerRef.current) {
            playerRef.current.removeEventListener('canplay', handleCanPlay);
            playerRef.current.removeEventListener('durationchange', handleDurationChange);
            playerRef.current.removeEventListener('ended', handleEnded);
            playerRef.current.removeEventListener('error', handleError);
            playerRef.current.removeEventListener('loadedmetadata', handleLoadedMetadata);
            playerRef.current.removeEventListener('playing', handlePlaying);
            playerRef.current.removeEventListener('seeked', handleSeeked);
            playerRef.current.removeEventListener('seeking', handleSeeking);
            playerRef.current.removeEventListener('timeupdate', onTimeUpdateCallback);
            if (debug) {
                VIDEO_EVENTS.forEach((eventName) => {
                    var _a;
                    (_a = playerRef.current) === null || _a === void 0 ? void 0 : _a.removeEventListener(eventName, handleDebug);
                });
            }
        }
    };
    // Event handlers end
    // Player configuration start
    const configuration = {
        libraryLocation,
        license: licenseProvidedByConsumer || licenseProvidedByServer,
    };
    const isAStream = src && SUPPORTED_STREAM_FORMATS.some((format) => src.includes(format));
    const source = {
        sources: [
            {
                src,
                type: isAStream ? 'application/x-mpegurl' : 'video/mp4',
                useCredentials: gslCredentialsRequired, // Ensure cookies are included
            },
        ],
    };
    // eslint-disable no-param-reassign start
    const onMount = useCallback((node) => {
        if (node) {
            if (!playerRef.current) {
                const newPlayer = new ChromelessPlayer(node, configuration);
                newPlayer.muted = muted;
                newPlayer.preload = 'auto';
                setCanPlay(false);
                setAutoSkipEnabled(false);
                setClipStartLocated(false);
                newPlayer.source = source;
                resetClipTime();
                playerRef.current = newPlayer;
                addPlayerHandlers();
            }
            else {
                setCanPlay(false);
                setAutoSkipEnabled(false);
                setClipStartLocated(false);
                playerRef.current.source = source;
                resetClipTime();
                requestToPlayIfRequired();
            }
        }
    }, [src, configuration.license]);
    useEffect(() => {
        setAutoSkipEnabled(false);
        resetClipTime();
        requestToPlayIfRequired();
    }, [desiredStartTime]);
    // Player configuration end
    // Cleanup
    useEffect(() => () => {
        if (playerRef.current) {
            removePlayerHandlers();
            playerRef.current.destroy();
            playerRef.current = null; // Required to prevent THEO exceptions in strict mode
        }
    }, []);
    return (jsxs(VideoContainer$1, { children: [jsx(VideoLoaderWrapper, { children: jsx(VideoLoader, { loading: isLoading, finished: isLoadingFinished, transitionDuration: LOADER_TRANSITION_DURATION }) }), jsxs(Wrapper, { children: [gslAuthComplete && configuration.license && (jsx(TheoPlayer, { testId: testId, onMount: onMount })), children && jsx(VideoOverlay, { children: children }), videoControls && (jsx(VideoPlayerControls, { overlayControls: overlayControls, videoControls: videoControls }))] })] }));
}

function useMyCutups() {
    const [myCutups, setMyCutups] = useAtom(myCutupsAtom);
    const setMyCutupsMessage = useSetAtom(messageMyCutupsActionsAtom);
    const { data, error, ...swrResponse } = useSWRWithAuth(Endpoint.CUTUPS, {
        search: {
            include_errors: 'true',
            include_in_progress: 'true',
            include_queued: 'true',
        },
    });
    const errorRetrievingMyCutups = useTranslation('errorRetrievingMyCutups');
    useEffect(() => {
        // useEffect Required to prevent setState in render errors
        if (data === null || data === void 0 ? void 0 : data.listings) {
            setMyCutups(data.listings);
        }
    }, [data]);
    useEffect(() => {
        if (error) {
            setMyCutupsMessage({
                description: errorRetrievingMyCutups,
                type: 'error',
            });
        }
    }, [error]);
    return { ...swrResponse, data: myCutups, error };
}

const BORDER_WIDTH$2 = 1;
const MyPlaylistsEmptyStateWrapper = styled.div `
  align-items: center;
  background-color: ${({ theme }) => theme.colours.graphics.primary.ink};
  border-top: ${BORDER_WIDTH$2}px solid
    ${({ theme }) => theme.colours.canvas.primary.main};
  display: flex;
  height: 100%;
  justify-content: center;
`;
const MyPlaylistsEmptyStateMessageWrapper = styled.div `
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const DEFAULT_REDIRECT_URI = window.location.origin;
function MyPlaylistsEmptyState({ children }) {
    const { defaultButtonText, noPlaylists } = useTranslations([
        'defaultButtonText',
        'noPlaylists',
    ]);
    return (jsx(MyPlaylistsEmptyStateWrapper, { children: jsx(MyPlaylistsEmptyStateMessageWrapper, { children: children || (jsxs(Fragment, { children: [jsx(Typography, { variant: "headingLarge", children: noPlaylists }), jsx(Button, { as: "a", href: DEFAULT_REDIRECT_URI, children: defaultButtonText })] })) }) }));
}

const PAUSE_ICON = 'Pause';
const PLAY_ICON = 'Play';
function usePlayPauseIconTitleAndIconText(description) {
    const playRequested = useCallbackSafeAtomValue(playRequestedAtom);
    const { pauseButtonTitle, playButtonTitle } = useTranslations([
        'pauseButtonTitle',
        'playButtonTitle',
    ]);
    return {
        buttonIcon: playRequested() ? PAUSE_ICON : PLAY_ICON,
        buttonTitle: formatTemplatedMessage(playRequested() ? pauseButtonTitle : playButtonTitle, { description }),
    };
}

var Status;
(function (Status) {
    Status["IN_PROGRESS"] = "IN_PROGRESS";
    Status["ERROR"] = "ERROR";
    Status["QUEUED"] = "QUEUED";
})(Status || (Status = {}));

const dateOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
};
const formatDate = (dateString) => Intl.DateTimeFormat(undefined, dateOptions)
    .format(new Date(dateString))
    .replace(/\//g, '-');
const timeOptions = {
    hour: '2-digit',
    hour12: false,
    minute: '2-digit',
    second: '2-digit',
};
const formatTime = (dateString) => Intl.DateTimeFormat(undefined, timeOptions).format(new Date(dateString));

function useCutups() {
    const api = useApi();
    const { invalidateCacheForApi } = useSWRConfig();
    const setPlaylistActionsMessage = useSetAtom(messagePlaylistActionsAtom);
    const setCutupsActionsMessage = useSetAtom(messageMyCutupsActionsAtom);
    const { errorCreatingCutup, errorDeletingCutup, successfullyCreatedCutup, successfullyDeletedCutup, } = useTranslations([
        'errorCreatingCutup',
        'errorDeletingCutup',
        'successfullyCreatedCutup',
        'successfullyDeletedCutup',
    ]);
    const displaySuccessMessage = (successMessage) => setPlaylistActionsMessage({
        description: successMessage,
        type: MESSAGE_TYPES.SUCCESS,
    });
    const displayErrorMessage = (errorMessage) => setPlaylistActionsMessage({
        description: errorMessage,
        type: MESSAGE_TYPES.ERROR,
    });
    const name = useAtomValue(cutupNameAtom);
    const setCutupContextMenuOpen = useSetAtom(cutupContextMenuOpenAtom);
    const createCutup = async (playlistEntries, onSuccess) => {
        const videoClipReferences = serialisePlaylistEntries(playlistEntries);
        const data = JSON.stringify({
            include_overlays: 'true',
            name,
            video_clip_references: videoClipReferences,
        });
        const response = await api.post(Endpoint.CUTUPS, {
            body: data,
        });
        if (response.data && !response.data.error) {
            displaySuccessMessage(successfullyCreatedCutup);
            if (onSuccess) {
                onSuccess();
            }
        }
        else {
            displayErrorMessage(errorCreatingCutup);
        }
    };
    const deleteCutup = async (cutup) => {
        const response = await api.delete(`${Endpoint.CUTUPS}/${cutup.name}`);
        if (response.data) {
            await invalidateCacheForApi(Endpoint.CUTUPS);
            setCutupsActionsMessage({
                description: formatTemplatedMessage(successfullyDeletedCutup, {
                    cutupKey: cutup === null || cutup === void 0 ? void 0 : cutup.name,
                }),
                type: MESSAGE_TYPES.SUCCESS,
            });
            setCutupContextMenuOpen(null);
        }
        else {
            setCutupsActionsMessage({
                description: formatTemplatedMessage(errorDeletingCutup, {
                    cutupKey: cutup === null || cutup === void 0 ? void 0 : cutup.name,
                }),
                type: MESSAGE_TYPES.ERROR,
            });
        }
    };
    return {
        createCutup,
        deleteCutup,
    };
}

function DeleteMyCutup({ cutupToDelete }) {
    const { confirmDeleteCutup, delete: deleteLabel, deleteCutup: deleteCutupTitle, deleteCutupConfirmationTitle, } = useTranslations([
        'confirmDeleteCutup',
        'delete',
        'deleteCutup',
        'deleteCutupConfirmationTitle',
    ]);
    const [displayConfirmation, setDisplayConfirmation] = useAtom(displayDeleteCutupConfirmationAtom);
    const { deleteCutup } = useCutups();
    const confirmText = formatTemplatedMessage(confirmDeleteCutup, {
        cutupKey: cutupToDelete === null || cutupToDelete === void 0 ? void 0 : cutupToDelete.name,
    });
    const dialogTitle = formatTemplatedMessage(deleteCutupConfirmationTitle, {
        cutupKey: cutupToDelete === null || cutupToDelete === void 0 ? void 0 : cutupToDelete.name,
    });
    const onDelete = () => {
        deleteCutup(cutupToDelete);
    };
    const toggleDisplayDeleteConfirmationDialog = () => setDisplayConfirmation(!displayConfirmation);
    return (jsxs(MenuItem, { onClick: toggleDisplayDeleteConfirmationDialog, children: [jsx(MenuItemText, { children: deleteCutupTitle }), jsx(ConfirmationDialog, { confirmLabel: deleteLabel, id: "delete-dialog", isDisabledCancel: false, isOpen: displayConfirmation, onCancel: toggleDisplayDeleteConfirmationDialog, onConfirm: onDelete, title: dialogTitle, children: displayConfirmation && (jsx(Typography, { variant: "bodyRegular", children: confirmText })) })] }));
}

const StyledMenuItemLink = styled.a `
  width: 100%;
`;

function DownloadMyCutup({ cutupToDownload }) {
    const download = useTranslation('download');
    return (jsx(MenuItem, { children: jsx(StyledMenuItemLink, { href: cutupToDownload.presignedUrl, target: "_blank", rel: "noreferrer", children: jsx(MenuItemText, { children: download }) }) }));
}

function MyCutupActions({ cutup }) {
    const [cutupContextMenuOpen, setCutupContextMenuOpen] = useAtom(cutupContextMenuOpenAtom);
    const { moreCutupActions } = useTranslations(['moreCutupActions']);
    const isOpen = cutupContextMenuOpen === cutup.name;
    return (jsx(ButtonWithMenu, { disableScrollWhenOpen: true, displayBackdrop: true, icon: "More", isOpen: isOpen, onClick: () => setCutupContextMenuOpen(cutup.name), onOutsideClick: () => setCutupContextMenuOpen(null), placement: "bottom-end", title: moreCutupActions, testId: `${cutup.name}-actions`, variant: "ghost", children: isOpen && (jsxs(Fragment, { children: [jsx(DeleteMyCutup, { cutupToDelete: cutup }), jsx(DownloadMyCutup, { cutupToDownload: cutup })] })) }));
}

const CutupDetailWrapper = styled.span `
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: center;
  min-width: 0;
  padding-left: 0.5rem;
  text-align: left;
`;
const CutupDetailText = styled.div `
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  width: 100%;
`;
const CutupMarker = styled.span `
  align-self: center;
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  width: 3rem;
`;
// wrapper around the clickable area of the playlist entry
const ClickableArea$1 = styled.button `
  display: flex;
  flex-grow: 1;
  justify-content: flex-start;
  min-width: 0;
`;
const MyCutupsItem = styled.li `
  align-items: center;
  align-self: stretch;
  background-color: ${({ theme }) => theme.colours.canvas.tertiary.main};
  border: 1px solid ${({ theme }) => theme.colours.canvas.secondary.main};
  display: flex;
  justify-content: space-between;
  padding: 0.5rem;

  ${({ $disabled, theme }) => $disabled && `opacity: ${theme.opacity.disabled};`}

  ${({ $disabled, theme }) => !$disabled &&
    `&:hover {
    background-color: ${theme.colours.canvas.tertiary.strong};
    cursor: pointer;
  }`}
`;

const calculateUpdateMessage = (cutup, erroredOn, modifiedOn, queuedOn, inProgressUpdatedOn) => {
    const updateMessage = formatTemplatedMessage(modifiedOn, {
        date: formatDate(cutup.lastUpdated),
    });
    if (cutup.status === Status.QUEUED) {
        return formatTemplatedMessage(queuedOn, {
            time: formatTime(cutup.lastUpdated),
        });
    }
    if (cutup.status === Status.IN_PROGRESS) {
        return formatTemplatedMessage(inProgressUpdatedOn, {
            time: formatTime(cutup.lastUpdated),
        });
    }
    if (cutup.status === Status.ERROR) {
        return formatTemplatedMessage(erroredOn, {
            date: formatDate(cutup.lastUpdated),
        });
    }
    return updateMessage;
};
function MyCutupsEntry({ cutup, index }) {
    const { erroredOn, inProgressUpdatedOn, modifiedOn, queuedOn } = useTranslations([
        'erroredOn',
        'inProgressUpdatedOn',
        'modifiedOn',
        'queuedOn',
    ]);
    const updateMessage = calculateUpdateMessage(cutup, erroredOn, modifiedOn, queuedOn, inProgressUpdatedOn);
    const [selectedCutup, setSelectedCutup] = useAtom(selectedCutupAtom);
    const [playRequested, setPlayRequested] = useAtom(playRequestedAtom);
    const videoClipEnded = useAtomValue(videoClipEndedAtom);
    const setResetRequested = useSetAtom(resetRequestedAtom);
    const { buttonIcon, buttonTitle } = usePlayPauseIconTitleAndIconText(selectedCutup === null || selectedCutup === void 0 ? void 0 : selectedCutup.name);
    const handleClick = () => {
        if (selectedCutup !== cutup) {
            setSelectedCutup(cutup);
        }
        else if (videoClipEnded) {
            setResetRequested(true);
        }
        else {
            setPlayRequested(!playRequested);
        }
    };
    const isSelected = (selectedCutup === null || selectedCutup === void 0 ? void 0 : selectedCutup.name) === cutup.name;
    const disabled = !cutup.presignedUrl;
    return (jsxs(MyCutupsItem, { "$disabled": disabled, children: [jsxs(ClickableArea$1, { disabled: disabled, onClick: handleClick, children: [jsx(CutupMarker, { children: jsx(Typography, { as: "span", variant: "bodyMedium", children: isSelected ? (jsx(ButtonIcon, { as: "i", size: "small", icon: buttonIcon, title: buttonTitle, variant: "primary" })) : (index + 1) }) }), jsxs(CutupDetailWrapper, { children: [jsx(Typography, { as: CutupDetailText, variant: "headingLarge", children: cutup.name }), cutup.lastUpdated && (jsx(Typography, { as: CutupDetailText, variant: "bodyRegular", children: updateMessage }))] })] }), jsx(MyCutupActions, { cutup: cutup })] }));
}

function MyCutups({ emptyStateContent }) {
    const { myCutupsTitle } = useTranslations(['myCutupsTitle']);
    const { data: myCutups } = useMyCutups();
    return (jsxs(MyPlaylistsWrapper, { children: [jsx(MyPlaylistsHeader, { children: jsx(Typography, { variant: "headingLarge", as: "h1", children: myCutupsTitle }) }), myCutups && myCutups.length > 0 ? (jsx(MyPlaylistsOrderedList, { children: myCutups === null || myCutups === void 0 ? void 0 : myCutups.map((cutup, index) => (jsx(MyCutupsEntry, { cutup: cutup, index: index }, cutup.name))) })) : (jsx(MyPlaylistsEmptyState, { children: emptyStateContent }))] }));
}

function ActionMessage({ message, onClose }) {
    const { description, title, type } = message;
    return ReactDOM.createPortal(jsx(Toast, { isOpen: !!title || !!description, onClose: onClose, title: title, type: type || MESSAGE_TYPES.DEFAULT, children: description }), document.body);
}

function MyCutupsMessages() {
    const [message, setMessage] = useAtom(messageMyCutupsActionsAtom);
    const onClose = () => setMessage(null);
    return message ? jsx(ActionMessage, { message: message, onClose: onClose }) : null;
}

function MyCutupsContainer({ emptyStateContent }) {
    const selectedCutup = useAtomValue(selectedCutupAtom);
    const [elementRef, { width }] = useRect();
    const displayVideo = width >= MY_PLAYLISTS_CONTAINER_BREAKPOINT || selectedCutup;
    return (jsxs(PlayerAndMyPlaylistContainer, { ref: elementRef, children: [jsxs(PlayerAndPlaylistWrapper, { children: [jsx(MyCutups, { emptyStateContent: emptyStateContent }), displayVideo && (jsx(VideoPlayer, { src: (selectedCutup === null || selectedCutup === void 0 ? void 0 : selectedCutup.presignedUrl) || '', autoPlay: true }))] }), jsx(MyCutupsMessages, {})] }));
}

const useHandleClipEnd = () => {
    var _a;
    const autoSkipEnabled = useAtomValue(autoSkipEnabledAtom);
    const pauseOnClipEnd = useAtomValue(pauseOnClipEndAtom);
    const setPlayRequested = useSetAtom(playRequestedAtom);
    const [canSkip, skip] = useAtom(skipAtom);
    const videoClipEnded = useAtomValue(videoClipEndedAtom);
    const videoPlaybackFullDurationReached = useAtomValue(videoPlaybackFullDurationReachedAtom);
    const pause = () => {
        setPlayRequested(false);
    };
    const upNextId = (_a = canSkip.forward) === null || _a === void 0 ? void 0 : _a.id;
    const pauseAndThenSkipIfPossible = () => {
        // Always paused to prevent a race condition when handleRequestVideoFrameCallback runs after the clip ends
        pause();
        if (upNextId) {
            skip(SkipDirection.FORWARD);
        }
    };
    useEffect(() => {
        if (autoSkipEnabled && videoClipEnded && !pauseOnClipEnd) {
            pauseAndThenSkipIfPossible();
        }
        else if (videoPlaybackFullDurationReached ||
            (videoClipEnded && pauseOnClipEnd)) {
            pause();
        }
    }, [videoClipEnded]);
};

const getSinglePlayUrl = (playId) => `${Endpoint.PLAYS}/${playId}`;
const getSinglePlaylistEntryUrl = (playlistEntryId) => `${Endpoint.PLAYLIST_ENTRY}/${playlistEntryId}`;
const getSingleEventUrl = (eventId) => `event/${eventId}`;
const getSingleSequenceUrl = (sequenceId) => `sequence/${sequenceId}`;
const getSingleFullVideoUrl = (videoReference) => `matches/${videoReference.sport}/${videoReference.matchId}/videos/` +
    `${videoReference.period}/${videoReference.track}/${videoReference.version}`;
/**
 * Gets the correct API Endpoint for a single entry given the playlist type.
 *
 * @param {PlaylistType} playlistType
 * @param {PlaylistEntryId} id
 * @param {boolean} ballInPlay
 * @returns {string}
 */
const getSinglePlaylistEntryApiEndpoint = (playlistType, id, ballInPlay, streamsOnly) => {
    var _a;
    switch (playlistType) {
        case PlaylistType.EVENT: {
            const search = new URLSearchParams({
                include_mp4s: (!streamsOnly).toString(),
                include_streams: 'true',
            });
            return `${getSingleEventUrl(id)}?${search}`;
        }
        case PlaylistType.SEQUENCE: {
            const search = new URLSearchParams({
                include_streams: 'true',
            });
            return `${getSingleSequenceUrl(id)}?${search}`;
        }
        case PlaylistType.PLAY:
            return getSinglePlayUrl(id);
        case PlaylistType.CLIP: {
            const search = new URLSearchParams({
                include_streams: 'true',
            });
            return `${getSinglePlaylistEntryUrl(id)}?${search}`;
        }
        case PlaylistType.FULL:
        default: {
            const [sport, matchId, period, track, version] = id.split(':');
            const search = new URLSearchParams({
                ball_in_play: (_a = (ballInPlay || false)) === null || _a === void 0 ? void 0 : _a.toString(),
                id,
                include_streams: 'true',
            });
            return `${getSingleFullVideoUrl({
                matchId,
                period: +period,
                sport: sport,
                track: track,
                version: +version,
            })}?${search}`;
        }
    }
};
/**
 * Get the endpoint we need to hit for multiple entries of the playlist type.
 * VideoFull playlists are not supported.
 *
 * @param {PlaylistEntry} playlistType
 * @returns {string|undefined}
 */
const getMultiplePlaylistEntryApiEndpoint = (playlistType) => {
    switch (playlistType) {
        case PlaylistType.EVENT: {
            const search = new URLSearchParams({
                include_streams: 'true',
            });
            return `${Endpoint.EVENTS}?${search}`;
        }
        case PlaylistType.PLAY:
            return Endpoint.PLAYS;
        case PlaylistType.SEQUENCE: {
            const search = new URLSearchParams({
                include_streams: 'true',
            });
            return `${Endpoint.SEQUENCES}?${search}`;
        }
        case PlaylistType.CLIP:
            return Endpoint.PLAYLIST_ENTRY;
        case PlaylistType.FULL:
        default:
            return undefined;
    }
};

/**
 * Does the playlistItem have a presigned url?
 *
 * @param {PlaylistEntry} playlistItem
 * @returns {Boolean}
 */
const hasPresignedUrl = (playlistItem) => 'presignedUrl' in playlistItem;
const hasStreamUrl = (playlistItem) => 'streamUrl' in playlistItem;
/**
 * Hook that will request the server data for a playlist item if a presigned url is not already present.
 *
 * @param {PlaylistEntry} item
 */
function usePlaylistItem(item) {
    const addServerResponse = useSetAtom(addPlaylistEntriesServerResponseAtom);
    const playlistType = useAtomValue(playlistTypeAtom);
    const addItemsToPlaylist = useSetAtom(playlistEntriesAtom);
    const streamsOnly = useAtomValue(streamsOnlyAtom);
    const endpoint = item && !hasPresignedUrl(item) && !hasStreamUrl(item)
        ? `${getSinglePlaylistEntryApiEndpoint(playlistType, item.id, item.ballInPlay, streamsOnly)}`
        : undefined;
    const { data, ...swrResponse } = useSWRWithAuth(endpoint);
    const isMultipleApiResponse = (response) => response.playlistEntries !== undefined;
    useEffect(() => {
        if (data) {
            if (isMultipleApiResponse(data)) {
                addItemsToPlaylist(data.playlistEntries);
                addServerResponse(data.playlistEntries);
            }
            else {
                addServerResponse([data]);
            }
        }
    }, [item === null || item === void 0 ? void 0 : item.id, data]);
    return {
        ...swrResponse,
        data,
    };
}

function ErrorMessage({ error }) {
    const { videoNotFoundErrorDefault, videoNotFoundErrorLicense, videoNotFoundErrorUnknown, } = useTranslations([
        'videoNotFoundErrorDefault',
        'videoNotFoundErrorLicense',
        'videoNotFoundErrorUnknown',
    ]);
    if ((error === null || error === void 0 ? void 0 : error.code) === 404) {
        return videoNotFoundErrorDefault;
    }
    if ((error === null || error === void 0 ? void 0 : error.code) === 403) {
        return videoNotFoundErrorLicense;
    }
    return videoNotFoundErrorUnknown;
}

const VideoErrorContainer = styled.div `
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colours.canvas.secondary.main};
`;

function VideoError({ error }) {
    return (jsx(VideoContainer$1, { children: jsx(Wrapper, { children: jsx(VideoErrorContainer, { children: jsx(Typography, { variant: "headingLarge", children: jsx(ErrorMessage, { error: error }) }) }) }) }));
}

const byActive = ({ active }) => active;
/**
 * To be called within the ChildrenWithPlayerTracksData.
 * Takes the response from the API and makes it accessible to the children.
 * See the content of ChildrenWithPlayerTracksData for more information.
 *
 * @param {TrackingApiResponse} response
 * @returns {React.ReactNode}
 */
function renderHighlightPlayerPositionsWithCurrentTime(response) {
    const { error, data } = response;
    const currentTime = useVideoCurrentTime();
    const playerHighlighting = useAtomValue(playerHighlightingOverlayAtom);
    if (error || !data || !playerHighlighting)
        return null;
    return (jsx(HighlightPlayerPositions$2, { currentTimestamp: currentTime, players: playerHighlighting.players
            .filter(byActive)
            .map(({ colour, id, physicalMetrics }) => ({
            colour,
            physicalMetrics,
            playerId: id,
        })), playId: playerHighlighting.id }));
}

function HighlightPlayerPositions() {
    const playerHighlightingData = useAtomValue(playerHighlightingOverlayAtom);
    const showPlayerHighlighting = useAtomValue(showPlayerHighlightingAtom);
    if (!showPlayerHighlighting)
        return null;
    return (
    /* ! safe below as showPlayerHighlighting will be false if no playerHighlightingData is available */
    jsx(ChildrenWithPlayerTracksData, { id: playerHighlightingData.id, children: renderHighlightPlayerPositionsWithCurrentTime }));
}
var HighlightPlayerPositions$1 = memo(HighlightPlayerPositions);

function VideoCurrentlySelected({ autoPlay, ...props }) {
    const { mutate } = useSWRConfig$1();
    const currentlySelected = useAtomValue(selectedPlaylistEntryAtom);
    const selectedPlaylistEntryIndex = useAtomValue(selectedPlaylistEntryIndexAtom);
    const startTimestamp = useAtomValue(getVideoClipStartTimestampAtom);
    const [timestampRecovered, setTimestampRecovered] = useState(0);
    usePlaylistItem(currentlySelected);
    useHandleClipEnd();
    const handleError = useAtomCallback(useCallback(async (get, set, errorEvent) => {
        const { error } = errorEvent;
        const current = get(selectedPlaylistEntryAtom);
        if (current && (error === null || error === void 0 ? void 0 : error.code) === (error === null || error === void 0 ? void 0 : error.MEDIA_ERR_NETWORK)) {
            // Delete any instances of this presigned url in our atoms and the SWR cache
            await set(expirePresignedUrlsAtom, {
                id: current.id,
                mutate,
            });
            // Store the timestamp when the error occurred for use below.
            setTimestampRecovered(get(currentPlaybackTimeAtom));
        }
    }, []));
    if (currentlySelected &&
        'error' in currentlySelected &&
        currentlySelected.error) {
        return jsx(VideoError, { error: currentlySelected.error });
    }
    const presignedUrl = currentlySelected &&
        'presignedUrl' in currentlySelected &&
        (currentlySelected === null || currentlySelected === void 0 ? void 0 : currentlySelected.presignedUrl);
    const streamUrl = currentlySelected &&
        'streamUrl' in currentlySelected &&
        (currentlySelected === null || currentlySelected === void 0 ? void 0 : currentlySelected.streamUrl);
    const gslCredentialsRequired = currentlySelected &&
        'gslCredentialsRequired' in currentlySelected &&
        (currentlySelected === null || currentlySelected === void 0 ? void 0 : currentlySelected.gslCredentialsRequired);
    return (jsx(VideoPlayer
    // eslint-disable-next-line react/jsx-props-no-spreading
    , { ...props, autoPlay: autoPlay || selectedPlaylistEntryIndex > 0, 
        // timestamp should be the start timestamp of the playlist item if an error hasn't occurred.
        desiredStartTime: timestampRecovered || startTimestamp, gslCredentialsRequired: !!gslCredentialsRequired, onError: handleError, src: streamUrl || presignedUrl || '', children: jsx(HighlightPlayerPositions$1, {}) }));
}
var VideoCurrentlySelected$1 = memo(VideoCurrentlySelected);

const itemsInViewAtom = atom(1);
itemsInViewAtom.debugLabel = 'video-client.lazy-loading.in-view';
const loadNextAtom = atom(null, (get, set) => set(itemsInViewAtom, get(itemsInViewAtom) + 1));
loadNextAtom.debugLabel = 'video-client.lazy-loading.load-next';
const resetAtom = atom(null, (_, set) => set(itemsInViewAtom, 1));
resetAtom.debugLabel = 'video-client.lazy-loading.reset';

const CHUNK_SIZE = 4;

function useAutoLoadNextPlaylistEntries() {
    const currentlyPlayingIndex = useAtomValue(selectedPlaylistEntryIndexAtom);
    const itemsInView = useAtomValue(itemsInViewAtom);
    const loadNext = useSetAtom(loadNextAtom);
    const playlistEntriesReady = itemsInView * CHUNK_SIZE - 1; // Offset by one to account for 0 indexing
    const autoloadNextPlaylistEntries = currentlyPlayingIndex === playlistEntriesReady;
    useEffect(() => {
        loadNext();
    }, [autoloadNextPlaylistEntries]);
}

var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};

// src/observe.ts
var observerMap = /* @__PURE__ */ new Map();
var RootIds = /* @__PURE__ */ new WeakMap();
var rootId = 0;
var unsupportedValue = void 0;
function getRootId(root) {
  if (!root)
    return "0";
  if (RootIds.has(root))
    return RootIds.get(root);
  rootId += 1;
  RootIds.set(root, rootId.toString());
  return RootIds.get(root);
}
function optionsToId(options) {
  return Object.keys(options).sort().filter(
    (key) => options[key] !== void 0
  ).map((key) => {
    return `${key}_${key === "root" ? getRootId(options.root) : options[key]}`;
  }).toString();
}
function createObserver(options) {
  const id = optionsToId(options);
  let instance = observerMap.get(id);
  if (!instance) {
    const elements = /* @__PURE__ */ new Map();
    let thresholds;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        var _a;
        const inView = entry.isIntersecting && thresholds.some((threshold) => entry.intersectionRatio >= threshold);
        if (options.trackVisibility && typeof entry.isVisible === "undefined") {
          entry.isVisible = inView;
        }
        (_a = elements.get(entry.target)) == null ? void 0 : _a.forEach((callback) => {
          callback(inView, entry);
        });
      });
    }, options);
    thresholds = observer.thresholds || (Array.isArray(options.threshold) ? options.threshold : [options.threshold || 0]);
    instance = {
      id,
      observer,
      elements
    };
    observerMap.set(id, instance);
  }
  return instance;
}
function observe(element, callback, options = {}, fallbackInView = unsupportedValue) {
  if (typeof window.IntersectionObserver === "undefined" && fallbackInView !== void 0) {
    const bounds = element.getBoundingClientRect();
    callback(fallbackInView, {
      isIntersecting: fallbackInView,
      target: element,
      intersectionRatio: typeof options.threshold === "number" ? options.threshold : 0,
      time: 0,
      boundingClientRect: bounds,
      intersectionRect: bounds,
      rootBounds: bounds
    });
    return () => {
    };
  }
  const { id, observer, elements } = createObserver(options);
  const callbacks = elements.get(element) || [];
  if (!elements.has(element)) {
    elements.set(element, callbacks);
  }
  callbacks.push(callback);
  observer.observe(element);
  return function unobserve() {
    callbacks.splice(callbacks.indexOf(callback), 1);
    if (callbacks.length === 0) {
      elements.delete(element);
      observer.unobserve(element);
    }
    if (elements.size === 0) {
      observer.disconnect();
      observerMap.delete(id);
    }
  };
}

// src/InView.tsx
function isPlainChildren(props) {
  return typeof props.children !== "function";
}
var InView = class extends React2.Component {
  constructor(props) {
    super(props);
    __publicField(this, "node", null);
    __publicField(this, "_unobserveCb", null);
    __publicField(this, "handleNode", (node) => {
      if (this.node) {
        this.unobserve();
        if (!node && !this.props.triggerOnce && !this.props.skip) {
          this.setState({ inView: !!this.props.initialInView, entry: void 0 });
        }
      }
      this.node = node ? node : null;
      this.observeNode();
    });
    __publicField(this, "handleChange", (inView, entry) => {
      if (inView && this.props.triggerOnce) {
        this.unobserve();
      }
      if (!isPlainChildren(this.props)) {
        this.setState({ inView, entry });
      }
      if (this.props.onChange) {
        this.props.onChange(inView, entry);
      }
    });
    this.state = {
      inView: !!props.initialInView,
      entry: void 0
    };
  }
  componentDidMount() {
    this.unobserve();
    this.observeNode();
  }
  componentDidUpdate(prevProps) {
    if (prevProps.rootMargin !== this.props.rootMargin || prevProps.root !== this.props.root || prevProps.threshold !== this.props.threshold || prevProps.skip !== this.props.skip || prevProps.trackVisibility !== this.props.trackVisibility || prevProps.delay !== this.props.delay) {
      this.unobserve();
      this.observeNode();
    }
  }
  componentWillUnmount() {
    this.unobserve();
  }
  observeNode() {
    if (!this.node || this.props.skip)
      return;
    const {
      threshold,
      root,
      rootMargin,
      trackVisibility,
      delay,
      fallbackInView
    } = this.props;
    this._unobserveCb = observe(
      this.node,
      this.handleChange,
      {
        threshold,
        root,
        rootMargin,
        // @ts-ignore
        trackVisibility,
        // @ts-ignore
        delay
      },
      fallbackInView
    );
  }
  unobserve() {
    if (this._unobserveCb) {
      this._unobserveCb();
      this._unobserveCb = null;
    }
  }
  render() {
    const { children } = this.props;
    if (typeof children === "function") {
      const { inView, entry } = this.state;
      return children({ inView, entry, ref: this.handleNode });
    }
    const {
      as,
      triggerOnce,
      threshold,
      root,
      rootMargin,
      onChange,
      skip,
      trackVisibility,
      delay,
      initialInView,
      fallbackInView,
      ...props
    } = this.props;
    return React2.createElement(
      as || "div",
      { ref: this.handleNode, ...props },
      children
    );
  }
};
function useInView({
  threshold,
  delay,
  trackVisibility,
  rootMargin,
  root,
  triggerOnce,
  skip,
  initialInView,
  fallbackInView,
  onChange
} = {}) {
  var _a;
  const [ref, setRef] = React2.useState(null);
  const callback = React2.useRef();
  const [state, setState] = React2.useState({
    inView: !!initialInView,
    entry: void 0
  });
  callback.current = onChange;
  React2.useEffect(
    () => {
      if (skip || !ref)
        return;
      let unobserve;
      unobserve = observe(
        ref,
        (inView, entry) => {
          setState({
            inView,
            entry
          });
          if (callback.current)
            callback.current(inView, entry);
          if (entry.isIntersecting && triggerOnce && unobserve) {
            unobserve();
            unobserve = void 0;
          }
        },
        {
          root,
          rootMargin,
          threshold,
          // @ts-ignore
          trackVisibility,
          // @ts-ignore
          delay
        },
        fallbackInView
      );
      return () => {
        if (unobserve) {
          unobserve();
        }
      };
    },
    // We break the rule here, because we aren't including the actual `threshold` variable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      // If the threshold is an array, convert it to a string, so it won't change between renders.
      Array.isArray(threshold) ? threshold.toString() : threshold,
      ref,
      root,
      rootMargin,
      triggerOnce,
      skip,
      trackVisibility,
      fallbackInView,
      delay
    ]
  );
  const entryTarget = (_a = state.entry) == null ? void 0 : _a.target;
  const previousEntryTarget = React2.useRef();
  if (!ref && entryTarget && !triggerOnce && !skip && previousEntryTarget.current !== entryTarget) {
    previousEntryTarget.current = entryTarget;
    setState({
      inView: !!initialInView,
      entry: void 0
    });
  }
  const result = [setRef, state.inView, state.entry];
  result.ref = result[0];
  result.inView = result[1];
  result.entry = result[2];
  return result;
}

const PIXELS_ADDED_TO_BOUNDING_BOX = 200;
/**
 * Lazy Load a list of items when the previous item is in view.
 *
 * Example usage:
 *
 * ```jsx
 * import LazyLoadList from '../components/LazyLoadList';
 *
 * <LazyLoadList>
 *  {(index, ref) => <ListItem ref="ref">Item {index}</ListItem>}
 * </LazyLoadList>
 * ```
 *
 * @param {RenderFunction} children - The render function used for each entry in the list
 * @constructor
 */
function LazyLoadList({ children }) {
    const itemsInView = useAtomValue(itemsInViewAtom);
    const loadNext = useSetAtom(loadNextAtom);
    const reset = useSetAtom(resetAtom);
    const onInView = (inView, index) => {
        if (index >= itemsInView - 1 && inView) {
            loadNext();
        }
    };
    useEffect(
    // On unmount reset the counter
    () => () => {
        reset();
    }, []);
    return Array(itemsInView)
        .fill(0)
        .map((_, index) => (
    // Safe to use index as key here because the re-rendering is predictable.
    // Components are added to the list only when the previous item is in view.
    // Each key remains associated with the same position in the list across re-renders until new items are added.
    jsx(InView
    // eslint-disable-next-line react/no-array-index-key
    , { onChange: (inView) => onInView(inView, index), rootMargin: `${PIXELS_ADDED_TO_BOUNDING_BOX}px 0px ${PIXELS_ADDED_TO_BOUNDING_BOX}px 0px`, children: ({ ref }) => children({ index, ref }) }, index)));
}

const BORDER_WIDTH$1 = 1;
const getSelectedBorderColour = ({ $dragging, $dragInProgress, $selectedEntry, theme, $isEditMode, }) => {
    if ($isEditMode)
        return 'transparent';
    return $selectedEntry && (!$dragInProgress || $dragging)
        ? theme.colours.interface.main
        : 'transparent';
};
// background is default in edit mode for the currently selected/playing item
const getSelectedBackgroundColor = (defaultColour, selectedColour, isEditMode) => (isEditMode ? defaultColour : selectedColour);
const getBackgroundColour = ({ $selectedEntry, theme, $isEditMode }, defaultColour) => $selectedEntry
    ? getSelectedBackgroundColor(defaultColour, theme.applyOpacity(theme.colours.interface.main, 'highlight'), $isEditMode)
    : defaultColour;
const getOddBackgroundColour = (props) => getBackgroundColour(props, props.theme.colours.canvas.secondary.main);
const getEvenBackgroundColour = (props) => getBackgroundColour(props, props.theme.colours.canvas.tertiary.main);
const getOverlayBackgroundColour = (props) => props.theme.applyOpacity(props.theme.colours.interface.main, props.$selectedEntry ? 'overlay' : 'highlight');
const PlaylistContents = styled.div `
  display: flex;
  padding: 0.25rem 0.5rem 0.25rem 0;
  position: relative;
  text-align: left;
  width: 100%;
`;
const PlaylistMarker = styled.div `
  align-self: center;
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  width: 3rem;
`;
const HighlightOverlay = styled.div `
  position: absolute;
  left: 0;
  width: 100%;
  top: -${BORDER_WIDTH$1}px; // top -1px to hide border of previous item when two items are adjacent
  height: calc(
    100% + ${BORDER_WIDTH$1 * 2}px
  ); // height +2px to hide border of next item when two items are adjacent
  background: ${getOverlayBackgroundColour};
  pointer-events: none;
  display: none;
`;
// wrapper around the clickable area of the playlist entry
const ClickableArea = styled.div `
  display: flex;
  flex-grow: 1;
  justify-content: flex-start;
  min-width: 0;
`;
// wrapper around checkbox in playlist
const PlaylistCheckboxWrapper = styled.div `
  align-self: center;
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  padding-left: 0.5rem;
`;
const PlaylistListItem = styled.li.attrs(({ $transition, $transform }) => ({
    style: {
        transform: CSS.Transform.toString($transform || null),
        transition: $transition,
    },
})) `
  ${({ $enableReordering }) => $enableReordering && `touch-action: none;`}

  ${({ $dragging, theme }) => $dragging &&
    `cursor: grab;
    box-shadow: ${theme.effects.dropShadow.uber};`};

  isolation: isolate;
  position: relative;

  ${({ $disabled, theme }) => ({
    opacity: $disabled ? theme.opacity.disabled : 1,
})}

  border-top: ${BORDER_WIDTH$1}px solid ${getSelectedBorderColour};
  border-bottom: ${BORDER_WIDTH$1}px solid ${getSelectedBorderColour};

  &:last-child {
    border-bottom: ${BORDER_WIDTH$1}px solid ${getSelectedBorderColour};
  }

  box-sizing: border-box;

  &:nth-of-type(odd) {
    background-color: ${getOddBackgroundColour};
  }

  &:nth-of-type(even) {
    background-color: ${getEvenBackgroundColour};
  }

  // If item is not checked, apply a border to the top - default is transparent
  &:not(.checked) {
    border-top: ${BORDER_WIDTH$1}px solid ${getSelectedBorderColour};
  }

  &:first-child.checked,
  &.checked + &:not(.checked),
  &:not(.checked) + &.checked {
    border-top: ${BORDER_WIDTH$1}px solid
      ${({ theme }) => theme.colours.interface.main};
    ${HighlightOverlay} {
      top: 0;
      height: calc(100% + ${BORDER_WIDTH$1}px);
    }
  }

  // if the item is the last child and is checked, apply a border to the bottom
  &:last-child.checked {
    border-bottom: ${BORDER_WIDTH$1}px solid
      ${({ theme }) => theme.colours.interface.main};
  }

  &.checked {
    ${PlaylistContents} {
      // when the overlay is applied, we need to bring our contents to the front for appearance clarity
      z-index: 1;
      position: relative;
    }

    // when item is checked, apply an opaque overlay
    ${HighlightOverlay} {
      display: block;
    }
  }

  ${({ $selectedEntry, $dragging, theme }) => $selectedEntry &&
    $dragging &&
    `
    &::before, &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: -1; 
    }
    &::before {
      background-color: ${theme.colours.canvas.tertiary.main};
    }
    &::after {
      background-color: ${theme.applyOpacity(theme.colours.interface.main, 'highlight')};
    }`}
`;
const PlaylistDescriptionWrapper = styled.div `
  min-width: 0;
`;

const PlaylistEntry$1 = forwardRef(({ buttonIcon, buttonTitle, checked, description, descriptionLineTwo, descriptionLineThree, disabled, dragging, dragInProgress, enableReordering, error, hovered, onCheckBoxChange, onClick, onMouseEnter, onMouseLeave, id, isEditMode, marker, selectedEntry, }, intersectionRef) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
        id,
    });
    const checkBoxLabel = formatTemplatedMessage(useTranslation('checkBoxLabel'), {
        description,
    });
    return (jsxs(PlaylistListItem, { className: checked ? 'checked' : '', "$disabled": disabled, "$dragging": dragging, "$dragInProgress": dragInProgress, "$enableReordering": enableReordering, "$isEditMode": isEditMode, onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave, "$selectedEntry": selectedEntry, ref: setNodeRef, ...attributes, "$transform": transform, "$transition": transition, ...attributes, ...(enableReordering ? listeners : {}), children: [jsxs(PlaylistContents, { ref: intersectionRef, children: [isEditMode && !disabled && !dragging && onCheckBoxChange && (jsx(PlaylistCheckboxWrapper, { children: jsx(Checkbox, { id: `checkbox-holding-list-${id}`, disabled: dragInProgress, checked: checked, withLabel: false, label: checkBoxLabel, onChange: onCheckBoxChange }) })), jsxs(ClickableArea, { onClick: !disabled ? onClick : undefined, children: [jsx(PlaylistMarker, { children: dragging || (hovered && enableReordering && !selectedEntry) ? (jsx(Icon, { size: "small", variant: "DragHandle", colour: "primary" })) : (jsx(Typography, { as: "span", variant: "bodyMedium", children: !disabled &&
                                        selectedEntry &&
                                        !dragInProgress &&
                                        buttonIcon ? (jsx(ButtonIcon, { size: "small", icon: buttonIcon, title: buttonTitle, variant: checked ? 'secondary' : 'primary' })) : (marker) })) }), description || error ? (jsxs(PlaylistDescriptionWrapper, { children: [jsx(Typography, { as: "div", variant: "headingMedium", children: jsx(Truncate, { position: "end", children: description || jsx(ErrorMessage, { error: error }) }) }), descriptionLineTwo && (jsx(Typography, { as: "div", variant: "bodyMedium", children: jsx(Truncate, { position: "end", children: descriptionLineTwo }) })), descriptionLineThree && (jsx(Typography, { as: "div", variant: "bodyMedium", children: jsx(Truncate, { position: "end", children: descriptionLineThree }) }))] })) : (jsx(PlaylistDescriptionWrapper, { children: jsx(Loader, {}) }))] })] }), jsx(HighlightOverlay, { "$selectedEntry": selectedEntry })] }));
});

const Base = styled.ul `
  list-style: none;
`;

function DragOverlay({ id, description, descriptionLineTwo, descriptionLineThree, selectedEntry, error, }) {
    return createPortal(jsx(DragOverlay$1, { children: jsx(Base, { children: jsx(PlaylistEntry$1, { dragging: true, enableReordering: true, id: id, description: description, descriptionLineTwo: descriptionLineTwo, descriptionLineThree: descriptionLineThree, dragInProgress: true, error: error, selectedEntry: selectedEntry }) }) }), document.body);
}

function PlaylistDragProvider({ children }) {
    const playlistEntries = useAtomValue(playlistEntriesAtom);
    const selectedPlaylistEntry = useAtomValue(selectedPlaylistEntryAtom);
    const reorderPlaylist = useSetAtom(reorderPlaylistAtom);
    const [playlistEntryBeingDraggedUniqueIdentifier, setPlaylistEntryBeingDraggedUniqueIdentifier,] = useAtom(currentlyDraggingPlaylistEntryIdAtom);
    const { updateAllPlaylistEntriesInCurrentlySelectedPlaylist } = useMyPlaylists$1();
    const draggedEntry = playlistEntries === null || playlistEntries === void 0 ? void 0 : playlistEntries.find((entry) => entry.id === playlistEntryBeingDraggedUniqueIdentifier);
    const onDragStart = (event) => {
        setPlaylistEntryBeingDraggedUniqueIdentifier(event.active.id);
    };
    const sensors = useSensors(useSensor(PointerSensor, {
        activationConstraint: {
            delay: 150,
            tolerance: 5,
        },
    }), useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
    }));
    const onDragEnd = (event) => {
        const { active, over } = event;
        if ((over === null || over === void 0 ? void 0 : over.id) && (active === null || active === void 0 ? void 0 : active.id) !== (over === null || over === void 0 ? void 0 : over.id)) {
            reorderPlaylist({
                activeId: active.id,
                overId: over.id,
            });
            updateAllPlaylistEntriesInCurrentlySelectedPlaylist();
        }
        setPlaylistEntryBeingDraggedUniqueIdentifier(undefined);
    };
    const error = (draggedEntry && 'error' in draggedEntry && draggedEntry.error) ||
        undefined;
    const { reorderScreenReaderInstructions } = useTranslations([
        'reorderScreenReaderInstructions',
    ]);
    const screenReaderInstructions = {
        draggable: reorderScreenReaderInstructions,
    };
    return (jsxs(DndContext, { collisionDetection: closestCenter, onDragEnd: onDragEnd, onDragStart: onDragStart, modifiers: [restrictToVerticalAxis], accessibility: { screenReaderInstructions }, sensors: sensors, children: [jsx(SortableContext, { items: playlistEntries.map((entry) => entry.id), strategy: verticalListSortingStrategy, children: children }), draggedEntry && (jsx(DragOverlay, { id: draggedEntry.id, description: draggedEntry.description, descriptionLineTwo: draggedEntry.descriptionLineTwo, descriptionLineThree: draggedEntry.descriptionLineThree, error: error, selectedEntry: (selectedPlaylistEntry === null || selectedPlaylistEntry === void 0 ? void 0 : selectedPlaylistEntry.id) === draggedEntry.id }))] }));
}

/**
 * Filter by if a playlist item has not had a response from the server yet.
 *
 * @param {PlaylistEntry} playlistItem
 * @returns {boolean}
 */
const byNoServerResponse = (playlistItem) => !('presignedUrl' in playlistItem || 'error' in playlistItem);

const DELAY_MS = 500;
function useAbortSignalWhenInView(inView = false, enabled = true) {
    const ref = useRef(new AbortController());
    useEffect(() => {
        if (enabled && !inView) {
            ref.current.abort();
            // Create another abort controller ready for the next fetch when the user scrolls into view.
            ref.current = new AbortController();
        }
    }, [enabled, inView]);
    return ref.current.signal;
}
/**
 * A hook that will only send a request to the server (via SWR) if the element is in view.
 * Optionally, abort the request if the user scrolls out of view before the fetch request has had time to resolove.
 *
 * @param {string} [endpoint]
 * @param {Config} [config]
 * @param {IntersectionOptions} [inViewOptions] - The delay defined here is the delay used to debounce the inview value.
 */
function useSWRWithAuthWhenInView(endpoint, config = {}, inViewOptions = {
    threshold: 0.1,
}) {
    const { delay, ...inViewConfig } = inViewOptions;
    const { abort = false, ...swrConfig } = config;
    const { ref, inView } = useInView(inViewConfig);
    const [debouncedInView] = useDebounce(inView, delay || DELAY_MS);
    const signal = useAbortSignalWhenInView(debouncedInView, abort);
    const key = debouncedInView ? endpoint : undefined;
    const response = useSWRWithAuth(key, {
        ...swrConfig,
        signal,
    });
    return [ref, response];
}

/**
 * Get the id param needed to request multiple events.
 *
 * @param {PlaylistType} playlistType
 * @returns {string}
 */
const getIdParam = (playlistType) => {
    switch (playlistType) {
        case PlaylistType.EVENT:
            return 'event_ids';
        case PlaylistType.SEQUENCE:
            return 'sequence_ids';
        case PlaylistType.PLAY:
            return 'play_ids';
        case PlaylistType.CLIP:
        default:
            return 'playlist_entry_ids';
    }
};
/**
 * Hook to fetch the server responses for playlist entries.
 *
 * @param {number} from - from index
 * @param {number} to - end index
 */
function usePlaylistEntries(from, to) {
    const playlistEntries = useAtomValue(playlistEntriesAtom);
    const playlistType = useAtomValue(playlistTypeAtom);
    const streamsOnly = useAtomValue(streamsOnlyAtom);
    const addServerResponse = useSetAtom(addPlaylistEntriesServerResponseAtom);
    const endpoint = getMultiplePlaylistEntryApiEndpoint(playlistType);
    const idParam = getIdParam(playlistType);
    const playlistEntriesChunked = playlistEntries.slice(from, to);
    // Filter out items that already have a server response
    const itemsToFetch = playlistEntriesChunked.filter(byNoServerResponse);
    const getEndpoint = () => {
        if (itemsToFetch.length === 0)
            return undefined;
        return endpoint;
    };
    const [ref, { data, ...swrResponse }] = useSWRWithAuthWhenInView(getEndpoint(), {
        // To prevent SWR from spamming the backend with data it might not need we're disabling revalidation on focus and
        // reconnection.
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        search: {
            [idParam]: itemsToFetch.map(({ id }) => id).join(','),
            // String true is deliberate.
            include_errors: 'true',
            include_mp4s: (!streamsOnly).toString(),
            include_streams: 'true',
            include_urls: 'true',
        },
    });
    // When new data is fetched, add server responses to the atom
    useEffect(() => {
        // useEffect is required here otherwise we see errors around actioning state changes from within a render method.
        if (data === null || data === void 0 ? void 0 : data.playlistEntries) {
            addServerResponse(data.playlistEntries);
        }
    }, [data]);
    return [
        ref,
        {
            ...swrResponse,
            data: playlistEntriesChunked,
        },
    ];
}

/**
 * Assigns a DOM element to a given ref, handling both function and object refs.
 */
function refCallback(ref, target) {
    if (typeof ref === 'function')
        ref(target);
    else if (ref && 'current' in ref) {
        // TS gumpf
        // TS may treat ref as readonly especially when using forwardedRef.
        // However, it's safe to mutate here because we're controlling the context.
        const mutableRef = ref;
        mutableRef.current = target;
    }
}
/**
 * Combines multiple refs (function or object) into a single stable ref callback.
 * Useful when a component needs to forward its ref to multiple consumers.
 *
 * Example:
 *   const combinedRef = useCombinedRef(ref1, ref2);
 *   return <Component ref={combinedRef} />
 *
 * The returned function is memoized, so it remains stable across re-renders.
 */
function useCombinedRef(...refs) {
    return useCallback((target) => {
        refs.forEach((ref) => refCallback(ref, target));
    }, refs);
}

/**
 * @param {PlaylistEntry} playlistItem
 */
const getDescription = (playlistItem) => {
    if (!(!!playlistItem && 'description' in playlistItem))
        return {};
    const { description, descriptionLineTwo, descriptionLineThree } = playlistItem;
    return { description, descriptionLineThree, descriptionLineTwo };
};
/**
 * Represents a single entry in the playlist.
 *
 * @component
 * @param {PlaylistEntryProps} props including the index of the playlist item
 * @returns {React.ReactElement} The rendered PlaylistEntry component.
 */
var PlaylistEntry = forwardRef(({ dragging, enableReordering, entry, index }, ref) => {
    const { id, selectedEntry } = entry;
    const [hovered, setHovered] = useState(false);
    const [currentlyPlaying, setSelectedPlaylistEntry] = useAtom(selectedPlaylistEntryAtom);
    const playlistEntryBeingDraggedUniqueIdentifier = useAtomValue(currentlyDraggingPlaylistEntryIdAtom);
    const videoClipEnded = useAtomValue(videoClipEndedAtom);
    const setResetRequested = useSetAtom(resetRequestedAtom);
    const isEditMode = useAtomValue(playlistIsEditModeAtom);
    const [checkedPlaylistEntries, toggleCheckedPlaylistEntries] = useAtom(toggleCheckedPlaylistEntriesAtom);
    const [playRequested, setPlayRequested] = useAtom(playRequestedAtom);
    const onClick = () => {
        if ((currentlyPlaying === null || currentlyPlaying === void 0 ? void 0 : currentlyPlaying.id) !== id) {
            // Pausing to prevent race condition where frame update sets timestamp as video skips to start of new selection
            setPlayRequested(false);
            setSelectedPlaylistEntry(id);
        }
        else if (videoClipEnded) {
            setResetRequested(true);
        }
        else {
            setPlayRequested(!playRequested);
        }
    };
    const onCheckBoxChange = () => toggleCheckedPlaylistEntries(id);
    const { description, descriptionLineTwo, descriptionLineThree } = getDescription(entry);
    const { buttonIcon, buttonTitle } = usePlayPauseIconTitleAndIconText(description);
    const error = (entry && 'error' in entry && entry.error) || undefined;
    return (jsx(PlaylistEntry$1, { buttonIcon: buttonIcon, buttonTitle: buttonTitle, checked: checkedPlaylistEntries.indexOf(id) > -1, description: description, descriptionLineTwo: descriptionLineTwo, descriptionLineThree: descriptionLineThree, disabled: !!error, dragging: !!dragging, dragInProgress: !!playlistEntryBeingDraggedUniqueIdentifier, enableReordering: enableReordering, error: error, hovered: hovered, onCheckBoxChange: onCheckBoxChange, onClick: onClick, onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false), id: id, isEditMode: isEditMode, marker: (index + 1).toString(), ref: ref, selectedEntry: !!selectedEntry }));
});

function PlaylistEntries({ enableReordering, index: position }, ref) {
    const start = position * CHUNK_SIZE;
    const to = start + CHUNK_SIZE;
    const [fetchRef, { data: playlistEntries }] = usePlaylistEntries(start, to);
    // Combine the refs from two intersection observers.
    // The first one (passed in as a forwarded ref) is used to increase the length of the playlist and add the dom nodes
    // The second one is used to assert if the dom node intersects and fetch the data from the server.
    const combinedRef = useCombinedRef(ref, fetchRef);
    return playlistEntries === null || playlistEntries === void 0 ? void 0 : playlistEntries.map((playlistEntry, index) => {
        // Spy on the first item in the list to determine when it's "in view".
        const intersectionRef = index === 0 ? combinedRef : undefined;
        return (jsx(PlaylistEntry, { ref: intersectionRef, enableReordering: enableReordering, entry: playlistEntry, index: index + start }, playlistEntry.id));
    });
}
var PlaylistEntries$1 = forwardRef(PlaylistEntries);

function MyPlaylistsSubtitle({ selectedMyPlaylist }) {
    const { numVideos, modifiedOn } = useTranslations([
        'numVideos',
        'modifiedOn',
    ]);
    const videoCount = selectedMyPlaylist.playlistEntries.length;
    const videoCountString = `${formatTemplatedMessage(numVideos, { number: videoCount })} | `;
    return (jsxs(Truncate, { position: "end", children: [jsx(Typography, { variant: "headingMedium", as: "span", children: videoCountString }), jsx(Typography, { variant: "bodyRegular", as: "span", children: formatTemplatedMessage(modifiedOn, {
                    date: formatDate(selectedMyPlaylist.lastUpdated),
                }) })] }));
}

function DeleteMyPlaylist({ id }) {
    const { confirmDeletePlaylist, delete: deleteLabel, deletePlaylist, deletePlaylistConfirmationTitle, } = useTranslations([
        'confirmDeletePlaylist',
        'delete',
        'deletePlaylist',
        'deletePlaylistConfirmationTitle',
    ]);
    const { deleteMyPlaylist } = useMyPlaylists$1();
    const myPlaylists = useAtomValue(myPlaylistsAtom);
    const setPlaylistIdContextMenuOpen = useSetAtom(playlistContextMenuOpenAtom);
    const [displayDeletePlaylistConfirmation, setDisplayDeletePlaylistConfirmation,] = useAtom(displayDeletePlaylistConfirmationAtom);
    const onDelete = () => {
        deleteMyPlaylist(id);
        setPlaylistIdContextMenuOpen(null);
        setDisplayDeletePlaylistConfirmation(false);
    };
    const playlistToDelete = myPlaylists.find((playlist) => playlist.id === id);
    const confirmText = formatTemplatedMessage(confirmDeletePlaylist, {
        playlistName: playlistToDelete === null || playlistToDelete === void 0 ? void 0 : playlistToDelete.name,
    });
    const dialogTitle = formatTemplatedMessage(deletePlaylistConfirmationTitle, {
        playlistName: playlistToDelete === null || playlistToDelete === void 0 ? void 0 : playlistToDelete.name,
    });
    const toggleDisplayDeleteConfirmationDialog = () => setDisplayDeletePlaylistConfirmation(!displayDeletePlaylistConfirmation);
    return (jsxs(MenuItem, { onClick: toggleDisplayDeleteConfirmationDialog, children: [jsx(MenuItemText, { children: deletePlaylist }), displayDeletePlaylistConfirmation && (jsx(ConfirmationDialog, { confirmLabel: deleteLabel, id: "delete-dialog", isDisabledCancel: false, isOpen: displayDeletePlaylistConfirmation, onCancel: toggleDisplayDeleteConfirmationDialog, onConfirm: onDelete, title: dialogTitle, children: jsx(Typography, { variant: "bodyRegular", children: confirmText }) }))] }));
}

function RenamePlaylistMenuOption({ id }) {
    const { renamePlaylist } = useTranslations(['renamePlaylist']);
    const setPlaylistBeingRenamed = useSetAtom(playlistBeingRenamedAtom);
    const setPlaylistIdContextMenuOpen = useSetAtom(playlistContextMenuOpenAtom);
    const onRename = () => {
        setPlaylistIdContextMenuOpen(null);
        setPlaylistBeingRenamed(id);
    };
    return (jsx(MenuItem, { onClick: onRename, children: jsx(MenuItemText, { children: renamePlaylist }) }));
}

function PlaylistActions({ playlistId }) {
    const { morePlaylistActions } = useTranslations(['morePlaylistActions']);
    const [playlistIdContextMenuOpen, setPlaylistIdContextMenuOpen] = useAtom(playlistContextMenuOpenAtom);
    return (jsxs(ButtonWithMenu, { disableScrollWhenOpen: true, displayBackdrop: true, icon: "More", isOpen: playlistIdContextMenuOpen === playlistId, onClick: () => setPlaylistIdContextMenuOpen(playlistId), onOutsideClick: () => setPlaylistIdContextMenuOpen(null), placement: "bottom-end", size: "regular", title: morePlaylistActions, variant: "ghost", children: [jsx(DeleteMyPlaylist, { id: playlistId }), jsx(RenamePlaylistMenuOption, { id: playlistId })] }));
}

function CreateCutup({ playlistEntries }) {
    const { createCutup } = useCutups();
    const { cancel: cancelLabel, createCutupLabel, createCutupInputLabel, createCutupInputPlaceholder, createCutupTitle, createCutupVideoCount, } = useTranslations([
        'cancel',
        'createCutupLabel',
        'createCutupInputLabel',
        'createCutupInputPlaceholder',
        'createCutupTitle',
        'createCutupVideoCount',
    ]);
    const [displayConfirmation, setDisplayConfirmation] = useAtom(displayCreateCutupConfirmationAtom);
    const [cutupName, setCutupName] = useAtom(cutupNameAtom);
    const handleDialogConfirmation = (isOpen) => {
        document.body.style.overflow = isOpen ? 'hidden' : 'auto';
        setDisplayConfirmation(isOpen);
    };
    const onCreateCutup = () => {
        handleDialogConfirmation(true);
    };
    const onClose = () => {
        handleDialogConfirmation(false);
        setCutupName('');
    };
    const onCreateCutupSuccess = () => {
        handleDialogConfirmation(false);
        setCutupName('');
    };
    const onExport = () => createCutup(playlistEntries, onCreateCutupSuccess);
    return (jsxs(ButtonWithFlyout, { disabled: playlistEntries.length < 1, icon: "Forward", isOpen: displayConfirmation, onClick: onCreateCutup, onOutsideClick: onClose, placement: "bottom-end", size: "regular", title: formatTemplatedMessage(createCutupTitle, {
            count: playlistEntries.length,
        }), variant: "ghost", children: [jsx(ModalHeader, { id: "create-cutup-modal-header", children: formatTemplatedMessage(createCutupVideoCount, {
                    count: playlistEntries.length,
                }) }), jsx(CreateCutupOptionsContainer, { children: jsx(ContentIndentation, { children: jsx(TextField, { id: "cutup-name", label: createCutupInputLabel, onChange: (event) => {
                            setCutupName(event.target.value);
                        }, value: cutupName, placeholder: createCutupInputPlaceholder, size: "regular", variant: "primary" }) }) }), jsxs(ModalFooter, { children: [jsx(Button, { variant: "ghost", onClick: onClose, children: cancelLabel }), jsx(Button, { onClick: onExport, isDisabled: !cutupName, children: createCutupLabel })] })] }));
}

function DeletePlaylistEntries() {
    const { confirmDeleteFromPlaylist, delete: deleteLabel, deleteFromPlaylistTitle, } = useTranslations([
        'confirmDeleteFromPlaylist',
        'delete',
        'deleteFromPlaylistTitle',
    ]);
    const [displayDeleteFromPlaylistConfirmation, setDisplayDeleteFromPlaylistConfirmation,] = useAtom(displayDeleteFromPlaylistConfirmationAtom);
    const checkedPlaylistEntries = useAtomValue(checkedPlaylistEntriesAtom);
    const setSelectedPlaylists = useSetAtom(selectedPlaylistsAtom);
    const setPlaylistActionsMessage = useSetAtom(messagePlaylistActionsAtom);
    const setPlaylistIsEditMode = useSetAtom(playlistIsEditModeAtom);
    const removeFromPlaylist = useSetAtom(removeFromPlaylistAtom);
    const { deleteSelectedPlaylistEntriesFromSelectedPlaylist } = useMyPlaylists$1();
    const onDeleteSelectedClips = () => {
        setSelectedPlaylists([]);
        setPlaylistActionsMessage(null);
        setDisplayDeleteFromPlaylistConfirmation(true);
    };
    const onDeleteCancel = () => {
        setSelectedPlaylists([]);
        setDisplayDeleteFromPlaylistConfirmation(false);
    };
    const clearCheckedPlaylistEntries = useSetAtom(clearCheckedPlaylistEntriesAtom);
    const onSuccessfulDeletion = () => {
        clearCheckedPlaylistEntries();
        setPlaylistIsEditMode(false);
        removeFromPlaylist(checkedPlaylistEntries);
    };
    const onDeleteClipsFromSelectedPlaylist = async () => {
        await deleteSelectedPlaylistEntriesFromSelectedPlaylist(onSuccessfulDeletion);
        setDisplayDeleteFromPlaylistConfirmation(false);
    };
    return (jsxs(Fragment, { children: [jsx(ButtonIcon, { variant: "ghost", size: "regular", onClick: onDeleteSelectedClips, disabled: checkedPlaylistEntries.length < 1, title: formatTemplatedMessage(deleteFromPlaylistTitle, {
                    count: checkedPlaylistEntries.length,
                }), icon: "Delete" }), jsx(ConfirmationDialog, { confirmLabel: deleteLabel, id: "delete-dialog", isOpen: displayDeleteFromPlaylistConfirmation, onCancel: onDeleteCancel, onConfirm: onDeleteClipsFromSelectedPlaylist, title: formatTemplatedMessage(deleteFromPlaylistTitle, {
                    count: checkedPlaylistEntries.length,
                }), children: jsx(DeleteWarningContainer, { "data-testid": "delete-playlist-entry-content", children: jsx(Typography, { variant: "bodyRegular", children: confirmDeleteFromPlaylist }) }) })] }));
}

/**
 * Component for saving playlist entries.
 *
 * This component allows users to save playlist entries by providing a
 * playlist name and selecting the entries to be saved.
 * It also provides functionality for adding entries to an existing
 * playlist and removing entries from a playlist.
 *
 * @returns The SavePlaylistEntries component.
 */
function SavePlaylistEntries() {
    const { savePlaylistTitle } = useTranslations(['savePlaylistTitle']);
    // multi save holding list of selected playlist items
    const checkedPlaylistEntries = useAtomValue(checkedPlaylistEntriesAtom);
    const selectedPlaylistEntriesVideoClipReferences = useAtomValue(selectedPlaylistEntriesVideoClipReferencesAtom);
    const [displayConfirmation, setDisplayConfirmation] = useAtom(displaySavePlaylistConfirmationAtom);
    const setSelectedPlaylists = useSetAtom(selectedPlaylistsAtom);
    const setPlaylistIsEditMode = useSetAtom(playlistIsEditModeAtom);
    const clearCheckedPlaylistEntries = useSetAtom(clearCheckedPlaylistEntriesAtom);
    const handleDialogConfirmation = (isOpen) => {
        setDisplayConfirmation(isOpen);
    };
    const onSavePlaylistSuccess = () => {
        clearCheckedPlaylistEntries();
        setSelectedPlaylists([]);
        setPlaylistIsEditMode(false);
        handleDialogConfirmation(false);
    };
    const onSaveSelectedClips = () => {
        handleDialogConfirmation(true);
    };
    const onClose = () => {
        handleDialogConfirmation(false);
    };
    return (jsx(ButtonWithFlyout, { disabled: checkedPlaylistEntries.length < 1, icon: "Save", isOpen: displayConfirmation, onClick: onSaveSelectedClips, onOutsideClick: onClose, placement: "bottom-end", size: "regular", title: formatTemplatedMessage(savePlaylistTitle, {
            count: checkedPlaylistEntries.length,
        }), variant: "ghost", children: jsx(PlaylistSelection, { onClose: onClose, onSavePlaylistSuccess: onSavePlaylistSuccess, playlistEntries: selectedPlaylistEntriesVideoClipReferences }) }));
}

function PlaylistModification({ enableDelete, enableCreateCutup, enableSelectAll, }) {
    const { close, clearAll, editPlaylist, selectAll } = useTranslations([
        'clearAll',
        'close',
        'editPlaylist',
        'selectAll',
    ]);
    const playlistEntries = useAtomValue(playlistEntriesAtom);
    const selectedMyPlaylistId = useAtomValue(selectedMyPlaylistIdAtom);
    // multi save holding list of selected playlist items
    const checkedPlaylistEntries = useAtomValue(checkedPlaylistEntriesAtom);
    const clearCheckedPlaylistEntries = useSetAtom(clearCheckedPlaylistEntriesAtom);
    const addAllToCheckedPlaylistEntries = useSetAtom(addAllToCheckedPlaylistEntriesAtom);
    const removeCheckedPlaylistEntriesNoLongerPresent = useSetAtom(removeCheckedPlaylistEntriesNoLongerPresentAtom);
    const setMyPlaylistsNames = useSetAtom(myPlaylistsNamesAtom);
    const myPlaylists = useAtomValue(myPlaylistsAtom);
    const [playlistIsEditMode, setPlaylistIsEditMode] = useAtom(playlistIsEditModeAtom);
    useEffect(() => {
        removeCheckedPlaylistEntriesNoLongerPresent();
    }, [playlistEntries]);
    // we ideally want to use the myPlaylist array as the primary source to check against,
    // so when that has been updated, we want to set our myPlaylistNames against the myPlaylist data.
    useEffect(() => {
        setMyPlaylistsNames(myPlaylists.map((p) => p.name));
    }, [myPlaylists]);
    return (jsx(PlaylistActionsWrapper, { children: playlistIsEditMode ? (jsxs(Fragment, { children: [enableSelectAll &&
                    (playlistEntries.length === checkedPlaylistEntries.length ? (jsx(Button, { variant: "ghost", size: "small", onClick: clearCheckedPlaylistEntries, children: clearAll })) : (jsx(Button, { variant: "ghost", size: "small", onClick: () => {
                            addAllToCheckedPlaylistEntries(playlistEntries);
                        }, children: selectAll }))), jsx(SavePlaylistEntries, {}), enableDelete && jsx(DeletePlaylistEntries, {}), enableCreateCutup && (jsx(CreateCutup, { playlistEntries: playlistEntries.filter((entry) => checkedPlaylistEntries.includes(entry.id)) })), jsx(ButtonIcon, { icon: "Close", onClick: () => {
                        clearCheckedPlaylistEntries();
                        setPlaylistIsEditMode(false);
                    }, size: "regular", title: close, variant: "ghost" })] })) : (jsxs(Fragment, { children: [jsx(ButtonIcon, { icon: "Checklist", variant: "ghost", onClick: () => setPlaylistIsEditMode(true), size: "regular", title: editPlaylist }), selectedMyPlaylistId && (jsx(PlaylistActions, { playlistId: selectedMyPlaylistId }))] })) }));
}

const RenamePlaylistContainer = styled.form `
  align-items: center;
  display: flex;
  flex-grow: 1;
  gap: 0.25rem;
  justify-content: space-between;
`;

function RenamePlaylist({ id }) {
    const { close: closeLabel, renamePlaylist, save: saveLabel, } = useTranslations(['close', 'renamePlaylist', 'save']);
    const { myPlaylists, renamePlaylistEntry } = useMyPlaylists$1();
    const defaultPlaylistName = (myPlaylists.find((playlist) => playlist.id === id) || {}).name;
    const [newPlaylistName, setNewPlaylistName] = useState('');
    useEffect(() => {
        if (defaultPlaylistName)
            setNewPlaylistName(defaultPlaylistName);
    }, [defaultPlaylistName]);
    const setPlaylistBeingRenamed = useSetAtom(playlistBeingRenamedAtom);
    const onSuccessfulRename = () => {
        setPlaylistBeingRenamed(null);
    };
    const onRename = (e) => {
        e.preventDefault(); // Stop the form from posting back to the current url.
        return renamePlaylistEntry(id, newPlaylistName, onSuccessfulRename);
    };
    const onClose = () => {
        setPlaylistBeingRenamed(null);
    };
    const testId = `rename-playlist-${id}`;
    return (jsxs(RenamePlaylistContainer, { "data-testid": testId, onSubmit: onRename, children: [jsx(TextField, { id: testId, label: renamePlaylist, labelPosition: "none", onChange: (event) => {
                    setNewPlaylistName(event.target.value);
                }, value: newPlaylistName, size: "regular", variant: "primary" }), jsx(Button, { type: "submit", shape: "pill", size: "regular", children: saveLabel }), jsx(ButtonIcon, { icon: "Close", onClick: onClose, size: "regular", title: closeLabel, variant: "ghost" })] }));
}

function PlaylistHeader({ enableEditMode, enableCreateCutup, enableSelectAll, title, onBack, enableDelete, }) {
    const { currentlyPlayingDefaultPlaylistTitle, back: backLabel } = useTranslations(['currentlyPlayingDefaultPlaylistTitle', 'back']);
    const selectedMyPlaylist = useAtomValue(selectedMyPlaylistAtom);
    const playlistBeingRenamed = useAtomValue(playlistBeingRenamedAtom);
    const playlistTitleDisplay = (selectedMyPlaylist === null || selectedMyPlaylist === void 0 ? void 0 : selectedMyPlaylist.name) || title || currentlyPlayingDefaultPlaylistTitle;
    return (jsx(StyledPlaylistHeader, { "data-testid": "playlistHeader", children: jsxs(PlaylistBackButtonAndTitleWrapper, { children: [onBack && (jsx(PlaylistBackButtonWrapper, { children: jsx(ButtonIcon, { icon: "ChevronLeft", onClick: onBack, title: backLabel, size: "regular", variant: "secondary" }) })), jsxs(PlaylistTitle, { children: [playlistBeingRenamed ? (jsx(RenamePlaylist, { id: playlistBeingRenamed })) : (jsx(Typography, { variant: "headingRegular", as: "h1", children: jsx(Truncate, { position: "end", children: playlistTitleDisplay }) })), selectedMyPlaylist && (jsx(MyPlaylistsSubtitle, { selectedMyPlaylist: selectedMyPlaylist }))] }), !playlistBeingRenamed && enableEditMode && (jsx(PlaylistModification, { enableCreateCutup: enableCreateCutup, enableSelectAll: enableSelectAll, enableDelete: enableDelete }))] }) }));
}

function PlaylistMessages() {
    const [message, setMessage] = useAtom(messagePlaylistActionsAtom);
    const clearMessage = () => setMessage(null);
    return message ? (jsx(ActionMessage, { message: message, onClose: clearMessage })) : null;
}

function Playlist({ enableEditMode, enableCreateCutup, enableReordering, enableSelectAll, title, onBack, enableDelete, }) {
    const hasPlaylistEntries = useAtomValue(hasPlaylistEntriesAtom);
    const setMessage = useSetAtom(messagePlaylistActionsAtom);
    const setPlaylistIdContextMenuOpen = useSetAtom(playlistContextMenuOpenAtom);
    const { defaultEmptyMessage } = useTranslations(['defaultEmptyMessage']);
    const clearMessage = () => setMessage(null);
    const reset = () => {
        clearMessage();
        setPlaylistIdContextMenuOpen(null);
    };
    useEffect(reset, []);
    useAutoLoadNextPlaylistEntries();
    return (jsxs(Fragment, { children: [jsxs(PlaylistWrapper, { children: [jsx(PlaylistHeader, { enableEditMode: enableEditMode, enableCreateCutup: enableCreateCutup, enableSelectAll: enableSelectAll, title: title, onBack: onBack, enableDelete: enableDelete }), hasPlaylistEntries ? (jsx(PlaylistDragProvider, { children: jsx(PlaylistOrderedList, { children: jsx(LazyLoadList, { children: ({ index, ref }) => (jsx(PlaylistEntries$1, { enableReordering: enableReordering, index: index, ref: ref })) }) }) })) : (jsx(StyledEmptyPlayList, { children: jsx(Typography, { variant: "bodyMedium", children: defaultEmptyMessage }) }))] }), jsx(PlaylistMessages, {})] }));
}
var Playlist$1 = memo(Playlist);

const BORDER_WIDTH = 1;
const getVideoNumberBackground = ({ theme }) => theme.colours.canvas.secondary.strong;
const getPlaylistItemBorderColor = ({ theme }) => theme.colours.canvas.secondary.main;
const getPlaylistItemBackground = ({ theme }) => theme.colours.canvas.tertiary.main;
const getHoveredPlaylistItemBackground = ({ theme }) => theme.colours.canvas.tertiary.strong;
const MyPlaylistsItem = styled.li `
  align-items: center;
  align-self: stretch;
  background-color: ${getPlaylistItemBackground};
  border: ${BORDER_WIDTH}px solid ${getPlaylistItemBorderColor};
  display: flex;
  justify-content: space-between;
  padding: 0.5rem;

  &:hover {
    background-color: ${getHoveredPlaylistItemBackground};
    cursor: pointer;
  }
`;
const MyPlaylistsEntryContents = styled.div `
  align-items: center;
  align-self: stretch;
  display: flex;
  flex: 1 0 0;
  gap: 1rem;
  width: 100%;
`;
const MyPlaylistsEntryVideoNumberWrapper = styled.div `
  align-items: center;
  background: ${getVideoNumberBackground};
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0.25rem 1rem;
`;
const MyPlaylistsEntryDetailWrapper = styled.div `
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: center;
  min-width: 0;

  div {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    width: 100%;
  }
`;

function MyPlaylistsEntry({ playlist }) {
    var _a;
    const setPlaylistType = useSetAtom(playlistTypeAtom);
    const setSelectedMyPlaylistId = useSetAtom(selectedMyPlaylistIdAtom);
    const addItemsToPlaylist = useSetAtom(playlistEntriesAtom);
    const [playlistBeingRenamed, setPlaylistBeingRenamed] = useAtom(playlistBeingRenamedAtom);
    const isBeingRenamed = playlistBeingRenamed === playlist.id;
    const { videos, createdOn, modifiedOn } = useTranslations([
        'createdOn',
        'modifiedOn',
        'videos',
    ]);
    const onClick = () => {
        setPlaylistType(PlaylistType.CLIP);
        setSelectedMyPlaylistId(playlist.id);
        addItemsToPlaylist(playlist.playlistEntries.map((id) => ({
            id,
        })));
        setPlaylistBeingRenamed(null);
    };
    const playlistMetadata = [];
    if (playlist.createdAt)
        playlistMetadata.push(formatTemplatedMessage(createdOn, {
            date: formatDate(playlist.createdAt),
        }));
    if (playlist.lastUpdated)
        playlistMetadata.push(formatTemplatedMessage(modifiedOn, {
            date: formatDate(playlist.lastUpdated),
        }));
    const playlistMetadataString = playlistMetadata.join(' | ');
    return (jsx(MyPlaylistsItem, { children: jsxs(MyPlaylistsEntryContents, { children: [jsxs(MyPlaylistsEntryVideoNumberWrapper, { onClick: onClick, children: [jsx(Typography, { as: "div", variant: "headingLarge", children: ((_a = playlist.playlistEntries) === null || _a === void 0 ? void 0 : _a.length) || 0 }), jsx(Typography, { as: "div", variant: "bodyRegular", children: videos })] }), jsxs(MyPlaylistsEntryDetailWrapper, { onClick: !isBeingRenamed ? onClick : () => { }, children: [isBeingRenamed ? (jsx(RenamePlaylist, { id: playlist.id })) : (jsx(Typography, { as: "div", variant: "headingLarge", children: playlist.name })), jsx(Typography, { as: "div", variant: "bodyRegular", children: playlistMetadataString })] }), jsx(PlaylistActionsButtonContainer, { children: jsx(PlaylistActions, { playlistId: playlist.id }) })] }) }));
}

function MyPlaylists({ playlists, emptyStateContent }) {
    const defaultPlaylistTitle = useTranslation('defaultPlaylistTitle');
    const [message, setMessage] = useAtom(messageMyPlaylistsActionsAtom);
    useEffect(() => () => setMessage(null), []);
    return (jsxs(Fragment, { children: [jsxs(MyPlaylistsWrapper, { children: [jsx(MyPlaylistsHeader, { children: jsx(Typography, { variant: "headingLarge", as: "h1", children: defaultPlaylistTitle }) }), (playlists === null || playlists === void 0 ? void 0 : playlists.length) > 0 ? (jsx(MyPlaylistsOrderedList, { children: playlists.map((playlist) => (jsx(MyPlaylistsEntry, { playlist: playlist }, playlist.id))) })) : (jsx(MyPlaylistsEmptyState, { children: emptyStateContent }))] }), message && (jsx(ActionMessage, { message: message, onClose: () => setMessage(null) }))] }));
}

function MyPlaylistsContainer({ enableCreateCutup, emptyStateContent }) {
    const [elementRef, { width }] = useRect();
    const { data: playlists } = useMyPlaylists();
    const selectedMyPlaylist = useAtomValue(selectedMyPlaylistAtom);
    const setPlaylistIsEditMode = useSetAtom(playlistIsEditModeAtom);
    const setPlaylistEntries = useSetAtom(playlistEntriesAtom);
    const clearCheckedPlaylistEntries = useSetAtom(clearCheckedPlaylistEntriesAtom);
    const setSelectedMyPlaylistId = useSetAtom(selectedMyPlaylistIdAtom);
    const setPlaylistBeingRenamed = useSetAtom(playlistBeingRenamedAtom);
    const reset = () => {
        setSelectedMyPlaylistId(null);
        setPlaylistIsEditMode(false);
        clearCheckedPlaylistEntries();
        setPlaylistEntries([]);
        setPlaylistBeingRenamed(null);
    };
    useEffect(() => {
        reset();
        return () => reset();
    }, []);
    const displayVideo = width >= MY_PLAYLISTS_CONTAINER_BREAKPOINT || selectedMyPlaylist;
    return (jsx(PlayerAndMyPlaylistContainer, { ref: elementRef, children: jsxs(PlayerAndPlaylistWrapper, { children: [!selectedMyPlaylist ? (jsx(MyPlaylists, { playlists: playlists, emptyStateContent: emptyStateContent })) : (jsx(Playlist$1, { onBack: reset, enableEditMode: true, enableDelete: true, enableCreateCutup: enableCreateCutup, enableReordering: true })), displayVideo && (jsx(VideoCurrentlySelected$1, { autoPlay: true, controls: configureVideoControls({
                        controlsToIncludeInAdditionToDefault: [
                            Controls.SaveCurrentlyPlaying,
                        ],
                    }) }))] }) }));
}

/**
 * Convert the dependencies to a string to ensure the useEffect is not triggered if there are no changes to the
 * contents of an array or object.
 *
 * The main use case for this on VIL is to ensure the component does not continually spam the API if the user doesn't
 * change the contents of the events/plays array.
 *
 * Ex:
 * <VideoEvent events={[1, 2, 3, 4]} />
 *
 * @param {Function} callback
 * @param {DependencyList} dependencies
 * @void
 */
function useEffectWithDeepEqualityCheck(callback, dependencies) {
    const memorised = useMemo(() => JSON.stringify(dependencies), dependencies);
    return useEffect(callback, [memorised]);
}

function useAddToPlaylist(clientRequestedPlaylistItems, playlistType) {
    const addItemsToPlaylist = useSetAtom(playlistEntriesAtom);
    const setPlaylistType = useSetAtom(playlistTypeAtom);
    useEffectWithDeepEqualityCheck(() => {
        setPlaylistType(playlistType);
        addItemsToPlaylist(clientRequestedPlaylistItems);
    }, [clientRequestedPlaylistItems]);
}

const toPlaylistEntry$1 = (eventIdOrEvent) => {
    const isId = typeof eventIdOrEvent === 'string';
    return !isId
        ? eventIdOrEvent
        : {
            id: eventIdOrEvent,
        };
};
function VideoEvent({ event = '', events = [], ...videoPlayerProps }) {
    const eventsToAdd = (event ? [event, ...events] : events).map(toPlaylistEntry$1);
    useAddToPlaylist(eventsToAdd, PlaylistType.EVENT);
    // Allowing props spreading here as this is a wrapper for Video Player.
    // eslint-disable-next-line react/jsx-props-no-spreading
    return jsx(VideoCurrentlySelected$1, { ...videoPlayerProps });
}

/**
 * Generate an id for the item based on the video reference.
 *
 * @param {VideoReference} videoReference
 * @returns {PlaylistEntryId}
 */
const generateId = ({ sport, matchId, period, track = VideoTrack.Broadcast, version = 1, }) => `${sport}:${matchId}:${period}:${track}:${version}`;
function VideoFull({ ballInPlay, videoReference, ...videoPlayerProps }) {
    const id = generateId(videoReference);
    useAddToPlaylist([{ ballInPlay, id }], PlaylistType.FULL);
    // Allowing props spreading here as this is a wrapper for Video Player.
    // eslint-disable-next-line react/jsx-props-no-spreading
    return jsx(VideoCurrentlySelected$1, { ...videoPlayerProps });
}

/**
 * Convert the props passed into the video play component to a valid playlist entry.
 * This means we can share the data structure between the front and backend without needing to make a breaking change
 * for our consumers.
 *
 * @param {Play} playIdOrPlay
 * @returns {PlaylistEntryClient}
 */
const toPlaylistEntry = (playIdOrPlay) => {
    const isId = typeof playIdOrPlay === 'string';
    if (isId)
        return { id: playIdOrPlay };
    const { highlightPlayers, highlightPlayersDuration, playId, ...validPlaylistEntryProps } = playIdOrPlay;
    const playlistEntryClient = {
        ...validPlaylistEntryProps,
        id: playId,
    };
    if (highlightPlayers) {
        playlistEntryClient.videoClipReference = {};
        playlistEntryClient.videoClipReference.overlays = [
            {
                data: {
                    duration: highlightPlayersDuration,
                    id: playId,
                    players: highlightPlayers,
                    type: PlayerHighlightType.PLAY,
                },
                type: OverlayType.PLAYER_HIGHLIGHTS,
            },
        ];
    }
    return playlistEntryClient;
};
function VideoPlay({ play = '', plays = [], ...videoPlayerProps }) {
    const playsToAdd = (play ? [play, ...plays] : plays).map(toPlaylistEntry);
    useAddToPlaylist(playsToAdd, PlaylistType.PLAY);
    return (
    // Allowing props spreading here as this is a wrapper for Video Player.
    // eslint-disable-next-line react/jsx-props-no-spreading
    jsx(VideoCurrentlySelected$1, { ...videoPlayerProps }));
}

const StyledDiv = styled.div `
  &:focus {
    outline: none;
  }
`;

const KEYS = {
    Backspace: 'backspace',
    Comma: ',',
    CtrlArrowDown: 'ctrl+down',
    CtrlArrowUp: 'ctrl+up',
    Delete: 'delete',
    Digit0: '0',
    Digit1: '1',
    Digit2: '2',
    Digit3: '3',
    Digit4: '4',
    Escape: 'escape',
    KeyB: 'b',
    KeyC: 'c',
    KeyD: 'd',
    KeyE: 'e',
    KeyF: 'f',
    KeyI: 'i',
    KeyL: 'l',
    KeyM: 'm',
    KeyN: 'n',
    KeyP: 'p',
    KeyR: 'r',
    KeyU: 'u',
    KeyV: 'v',
    KeyX: 'x',
    KeyZ: 'z',
    Minus: '-',
    Period: '.',
    Plus: 'plus',
    Return: 'return',
    ShiftComma: 'shift+,',
    ShiftCtrlComma: 'ctrl+shift+,',
    ShiftCtrlPeriod: 'ctrl+shift+.',
    ShiftPeriod: 'shift+.',
    Space: 'space',
    SquareBracket: '[',
    SquareBracketClose: ']',
};
const VIDEO_SHORTCUTS = {
    FullscreenToggle: KEYS.KeyF,
    Next: KEYS.KeyN,
    PlaybackSpeedDecrease: KEYS.SquareBracket,
    PlaybackSpeedIncrease: KEYS.SquareBracketClose,
    PlayPause: KEYS.Space,
    Previous: KEYS.KeyP,
    ScrubBackwards: KEYS.ShiftComma,
    ScrubBackwardsFine: KEYS.Comma,
    ScrubBackwardsSingleStep: KEYS.ShiftCtrlComma,
    ScrubForwards: KEYS.ShiftPeriod,
    ScrubForwardsFine: KEYS.Period,
    ScrubForwardsSingleStep: KEYS.ShiftCtrlPeriod,
    VolumeDecrease: KEYS.CtrlArrowDown,
    VolumeIncrease: KEYS.CtrlArrowUp,
    VolumeMute: KEYS.KeyM,
};

const KEYBINDING_VOLUME_STEP = VOLUME_STEP * 10;
/**
 * Stops the keyboard bindings from being applied through certain conditions.
 *
 * @param {Mousetrap.ExtendedKeyboardEvent} _
 * @param {Element} element
 */
const applyKeyboardBindingExclusions = (_, element) => {
    if (element.className.includes('mousetrap')) {
        return false;
    }
    return ['INPUT', 'SELECT', 'TEXTAREA'].includes(element.tagName);
};
function VideoKeyboardShortcutProvider({ children, shortcuts }) {
    const { keybindingActionTitlePlayPause, keybindingActionTitlePlaybackSpeedIncrease, keybindingActionTitlePlaybackSpeedDecrease, keybindingActionTitleFullscreenToggle, keybindingActionTitleNext, keybindingActionTitlePrevious, keybindingActionTitleVolumeIncrease, keybindingActionTitleVolumeDecrease, keybindingActionTitleVolumeMute, keybindingActionTitleScrubForwardsFine, keybindingActionTitleScrubBackwardsFine, keybindingActionTitleScrubForwards, keybindingActionTitleScrubBackwards, keybindingActionTitleScrubForwardsSingleStep, keybindingActionTitleScrubBackwardsSingleStep, } = useTranslations([
        'keybindingActionTitlePlayPause',
        'keybindingActionTitlePlaybackSpeedIncrease',
        'keybindingActionTitlePlaybackSpeedDecrease',
        'keybindingActionTitleFullscreenToggle',
        'keybindingActionTitleNext',
        'keybindingActionTitlePrevious',
        'keybindingActionTitleVolumeIncrease',
        'keybindingActionTitleVolumeDecrease',
        'keybindingActionTitleVolumeMute',
        'keybindingActionTitleScrubForwardsFine',
        'keybindingActionTitleScrubBackwardsFine',
        'keybindingActionTitleScrubForwards',
        'keybindingActionTitleScrubBackwards',
        'keybindingActionTitleScrubForwardsSingleStep',
        'keybindingActionTitleScrubBackwardsSingleStep',
    ]);
    const containerRef = useRef(null);
    // Callback safe atom values
    const playRequested = useCallbackSafeAtomValue(playRequestedAtom);
    const fullscreenRequested = useCallbackSafeAtomValue(fullscreenRequestedAtom);
    const videoClipEnded = useCallbackSafeAtomValue(videoClipEndedAtom);
    const currentPlaybackRate = useCallbackSafeAtomValue(playbackRateRequestedAtom);
    const canSkip = useCallbackSafeAtomValue(skipAtom);
    const currentPlaybackTime = useCallbackSafeAtomValue(currentPlaybackTimeAtom);
    const clipRecentlyStarted = useCallbackSafeAtomValue(getVideoClipRecentlyStartedAtom);
    const currentVolume = useCallbackSafeAtomValue(volumeRequestedAtom);
    const muteRequested = useCallbackSafeAtomValue(mutedRequestedAtom);
    const scrubDuration = useAtomValue(scrubDurationAtom);
    const scrubFineDuration = useAtomValue(scrubFineDurationAtom);
    const startTimestamp = useCallbackSafeAtomValue(getVideoClipStartTimestampAtom);
    const endTimestamp = useCallbackSafeAtomValue(getVideoClipEndTimestampAtom);
    const currentPlaybackItemDuration = useCallbackSafeAtomValue(currentPlaybackItemDurationAtom);
    // Setters
    const setKeyboardShortcutsAtom = useSetAtom(keyboardShortcutsAtom);
    const setPlayRequested = useSetAtom(playRequestedAtom);
    const setFullscreenRequested = useSetAtom(fullscreenRequestedAtom);
    const setResetRequested = useSetAtom(resetRequestedAtom);
    const setPlaybackRate = useSetAtom(playbackRateRequestedAtom);
    const skip = useSetAtom(skipAtom);
    const setVolumeRequested = useSetAtom(volumeRequestedAtom);
    const setMuteRequested = useSetAtom(mutedRequestedAtom);
    const setTimeRequested = useSetAtom(playbackTimeRequestedAtom);
    // Key binding handlers
    const changePlaybackSpeed = (rateChangeAmount) => {
        const currentPlaybackRateIndex = allowedPlaybackRates.indexOf(currentPlaybackRate());
        const newRate = allowedPlaybackRates[currentPlaybackRateIndex + rateChangeAmount];
        if (newRate) {
            setPlaybackRate(newRate);
        }
    };
    const increasePlaybackSpeed = () => changePlaybackSpeed(1);
    const decreasePlaybackSpeed = () => changePlaybackSpeed(-1);
    const togglePlayRequestedOrResetIfTheClipHasEnded = () => {
        if (videoClipEnded()) {
            setResetRequested(true);
        }
        else {
            setPlayRequested(!playRequested());
        }
    };
    const toggleFullscreenMode = () => setFullscreenRequested(!fullscreenRequested());
    const setVolume = (newVolume) => {
        if (newVolume > currentVolume()) {
            setMuteRequested(false);
        }
        if (newVolume >= MIN_VOLUME && newVolume <= MAX_VOLUME) {
            setVolumeRequested(newVolume);
        }
    };
    const increaseVolume = () => setVolume(currentVolume() + KEYBINDING_VOLUME_STEP);
    const decreaseVolume = () => setVolume(currentVolume() - KEYBINDING_VOLUME_STEP);
    const toggleMute = () => setMuteRequested(!muteRequested());
    const requestNewTime = (newTimeRequested) => {
        let newTime = newTimeRequested;
        if (startTimestamp() || endTimestamp()) {
            if (newTimeRequested > endTimestamp()) {
                newTime = endTimestamp();
            }
            if (newTimeRequested < startTimestamp()) {
                newTime = startTimestamp();
            }
        }
        setTimeRequested(newTime);
    };
    const onScrub = (duration) => () => requestNewTime(currentPlaybackTime() + duration);
    const stepScrub = (steps) => {
        requestNewTime(calculatePlaybackTimeOfDesiredStep(currentPlaybackTime(), steps));
        setPlayRequested(false);
    };
    const scrubToNextStep = () => stepScrub(1);
    const scrubToPreviousStep = () => stepScrub(-1);
    const onNext = () => {
        if (canSkip().forward) {
            skip(SkipDirection.FORWARD);
        }
        else {
            // Paused to prevent a race condition when handleRequestVideoFrameCallback runs after the clip ends
            setPlayRequested(false);
            requestNewTime(endTimestamp() || currentPlaybackItemDuration());
        }
    };
    const onBack = () => {
        if (canSkip().back && clipRecentlyStarted()) {
            skip(SkipDirection.BACK);
        }
        else {
            setResetRequested(true);
        }
    };
    // Click and key press handlers
    const onKeyPress = (callback, shouldPreventDefault = false) => (event) => {
        var _a;
        if (fullscreenRequested() ||
            ((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.contains(document.activeElement))) {
            callback(event);
            if (shouldPreventDefault) {
                event.preventDefault();
            }
        }
    };
    const shortcutKeys = { ...VIDEO_SHORTCUTS, ...shortcuts };
    const bindings = [
        [
            shortcutKeys.PlayPause,
            onKeyPress(togglePlayRequestedOrResetIfTheClipHasEnded, true),
            keybindingActionTitlePlayPause,
        ],
        [
            shortcutKeys.PlaybackSpeedIncrease,
            onKeyPress(increasePlaybackSpeed),
            keybindingActionTitlePlaybackSpeedIncrease,
        ],
        [
            shortcutKeys.PlaybackSpeedDecrease,
            onKeyPress(decreasePlaybackSpeed),
            keybindingActionTitlePlaybackSpeedDecrease,
        ],
        [
            shortcutKeys.FullscreenToggle,
            onKeyPress(toggleFullscreenMode),
            keybindingActionTitleFullscreenToggle,
        ],
        [shortcutKeys.Next, onKeyPress(onNext), keybindingActionTitleNext],
        [shortcutKeys.Previous, onKeyPress(onBack), keybindingActionTitlePrevious],
        [
            shortcutKeys.VolumeIncrease,
            onKeyPress(increaseVolume),
            keybindingActionTitleVolumeIncrease,
        ],
        [
            shortcutKeys.VolumeDecrease,
            onKeyPress(decreaseVolume),
            keybindingActionTitleVolumeDecrease,
        ],
        [
            shortcutKeys.VolumeMute,
            onKeyPress(toggleMute),
            keybindingActionTitleVolumeMute,
        ],
        [
            shortcutKeys.ScrubForwardsFine,
            onKeyPress(onScrub(scrubFineDuration)),
            keybindingActionTitleScrubForwardsFine,
        ],
        [
            shortcutKeys.ScrubBackwardsFine,
            onKeyPress(onScrub(-scrubFineDuration)),
            keybindingActionTitleScrubBackwardsFine,
        ],
        [
            shortcutKeys.ScrubForwards,
            onKeyPress(onScrub(scrubDuration)),
            keybindingActionTitleScrubForwards,
        ],
        [
            shortcutKeys.ScrubBackwards,
            onKeyPress(onScrub(-scrubDuration)),
            keybindingActionTitleScrubBackwards,
        ],
        [
            shortcutKeys.ScrubForwardsSingleStep,
            onKeyPress(scrubToNextStep),
            keybindingActionTitleScrubForwardsSingleStep,
        ],
        [
            shortcutKeys.ScrubBackwardsSingleStep,
            onKeyPress(scrubToPreviousStep),
            keybindingActionTitleScrubBackwardsSingleStep,
        ],
    ];
    useEffect(() => {
        if (containerRef.current) {
            bindings.forEach(([trigger, callback]) => {
                Mousetrap.bind(trigger, callback);
            });
            Mousetrap.prototype.stopCallback = applyKeyboardBindingExclusions;
            setKeyboardShortcutsAtom(bindings.map(([trigger, , actionTitle]) => [trigger, actionTitle]));
        }
        return () => bindings.forEach(([trigger]) => Mousetrap.unbind(trigger));
    }, []);
    return (jsx(StyledDiv, { ref: containerRef, role: "presentation", tabIndex: -1, children: children }));
}

function HydrateAtoms({ children, initialValues }) {
    useHydrateAtoms(initialValues);
    return children;
}

function VideoProvider({ baseUrlVideoService, children, streamsOnly, theoPlayerLibraryLocation, theoPlayerLicense, translations, }) {
    const initialValues = [];
    if (baseUrlVideoService) {
        initialValues.push([baseUrlVideoServiceAtom, baseUrlVideoService]);
    }
    if (streamsOnly) {
        initialValues.push([streamsOnlyAtom, streamsOnly]);
    }
    if (theoPlayerLicense) {
        initialValues.push([theoPlayerLicenseAtom, theoPlayerLicense]);
    }
    if (theoPlayerLibraryLocation) {
        initialValues.push([
            theoPlayerLibraryLocationAtom,
            theoPlayerLibraryLocation,
        ]);
    }
    if (translations) {
        initialValues.push([translationsAtom, mergeTranslations(translations)]);
    }
    return (jsx(Provider, { children: jsx(HydrateAtoms, { initialValues: initialValues, children: children }) }));
}

/**
 * Retrieves an array of translation keys from the translationsAtom.
 * @returns An array of translation keys.
 */
function getTranslationKeys() {
    const translations = useAtomValue(translationsAtom);
    return Object.keys(translations);
}

const SECOND = 1000;
const RETRY_DELAY = 10 * SECOND;
const BASE_ENDPOINT = 'streams/exists';
const DEFAULT_IN_VIEW_OPTIONS = {
    threshold: 0.1,
    triggerOnce: true,
};
const videoReferenceToKey = ({ matchId, period }) => `${matchId}:${period}`;
const getEndpoint = (videoReferences) => {
    // Multiple videos must all be of the same sport
    const [{ sport }] = videoReferences || [{}];
    if (!sport || !Array.isArray(videoReferences) || videoReferences.length === 0)
        return undefined;
    const keys = videoReferences.map(videoReferenceToKey).join(',');
    return `${BASE_ENDPOINT}/${sport}?keys=${keys}`;
};
const handleRetries = (error, _, __, revalidate, revalidateOptions) => {
    // If the video is not found then it's not available
    // If a 403 is returned then the video is not licensed
    // In both these scenarios there's no need to repeatedly spam the server.
    if ([404, 403].includes(error.status))
        return;
    if (error.status === 500)
        // Poll the server again after it's been given sometime to recover
        setTimeout(() => revalidate(revalidateOptions), RETRY_DELAY);
};
function useVideoAvailability(videoReferences, inViewOptions = DEFAULT_IN_VIEW_OPTIONS) {
    var _a;
    const endpoint = getEndpoint(videoReferences);
    const [ref, { data, error, isLoading }] = useSWRWithAuthWhenInView(endpoint, {
        onErrorRetry: handleRetries,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
    }, inViewOptions);
    return [
        ref,
        {
            availability: (_a = videoReferences === null || videoReferences === void 0 ? void 0 : videoReferences.map((videoReference) => {
                var _a, _b;
                return ({
                    ...videoReference,
                    isAvailable: !!((_b = (_a = data === null || data === void 0 ? void 0 : data.availability) === null || _a === void 0 ? void 0 : _a[videoReferenceToKey(videoReference)]) === null || _b === void 0 ? void 0 : _b.available),
                });
            })) !== null && _a !== void 0 ? _a : [],
            error,
            isLoading,
        },
    ];
}

function PlayButton({ disabled = false, disabledReason, icon = 'Play', matchId, onClick = () => { }, period, size = 'small', sport, title, variant = 'ghost', }) {
    var _a;
    const videoNotAvailable = useTranslation('videoNotAvailableError');
    const [ref, { availability, isLoading, error }] = useVideoAvailability(disabled ? undefined : [{ matchId, period, sport }]);
    const { isAvailable = false } = (_a = availability[0]) !== null && _a !== void 0 ? _a : {};
    const disabledVisually = disabled || !!error;
    const button = (jsx(ButtonIcon, { ref: ref, disabled: disabled || !isAvailable, icon: isLoading ? 'Loading' : icon, off: disabledVisually, onClick: onClick, size: size, title: title, variant: variant }));
    const tooltipContent = (disabled && disabledReason) || (error && videoNotAvailable);
    return tooltipContent ? (jsx(Tooltip, { content: tooltipContent, children: button })) : (button);
}

/**
 * Provide access to the video controls.
 */
function useVideoControls() {
    const setPlayRequest = useSetAtom(playRequestedAtom);
    const setPlaybackTimeRequested = useSetAtom(playbackTimeRequestedAtom);
    const pause = () => setPlayRequest(false);
    const play = () => setPlayRequest(true);
    const setCurrentTime = (desiredTime) => setPlaybackTimeRequested(desiredTime);
    return { pause, play, setCurrentTime };
}
function useVideoPlaybackRate() {
    return useAtom(playbackRateRequestedAtom);
}

function usePlaylistCurrentlySelected() {
    return useAtom(selectedPlaylistEntryAtom);
}
function usePlaylistCurrentlySelectedInCallback() {
    return useCallbackSafeAtomValue(selectedPlaylistEntryAtom);
}
function usePlaylist() {
    return useAtomValue(playlistEntriesAtom);
}
function usePlaylistInCallback() {
    return useCallbackSafeAtomValue(playlistEntriesAtom);
}

export { Controls, MyCutupsContainer as MyCutups, MyPlaylistsContainer as MyPlaylists, PlayButton, PlayerAndPlaylistWrapper, Playlist$1 as Playlist, VideoEvent, VideoFull, VideoKeyboardShortcutProvider as VideoKeyBoardShortcutProvider, VideoPlay, VideoProvider, VideoSport, VideoTrack, VideoPlayer as _VideoPlayer, configureVideoControls, getTranslationKeys, usePlaylist, usePlaylistCurrentlySelected, usePlaylistCurrentlySelectedInCallback, usePlaylistInCallback, useVideoAvailability, useVideoCanPlay, useVideoCanPlayInCallback, useVideoControls, useVideoCurrentTime, useVideoCurrentTimeInCallback, useVideoDuration, useVideoDurationInCallback, useVideoIsPlaying, useVideoIsPlayingInCallback, useVideoPlaybackRate, useVideoResolution, useVideoResolutionInCallback };
//# sourceMappingURL=index.js.map
